import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, FormControl, AbstractControl, ValidatorFn } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import {
  ShipmentRequestService,
  Waybill,
  Parcel,
  Dealer,
  Brand,
  Client,
  ClientsBrands,
  ClientFieldSettings,
  Shipment,
  ParcelsSummary,
  WaybillStatus,
  ParcelStatus,
  WaybillDeliveryMethod,
  WaybillDeliveryType,
  DealerStatus,
  BrandStatus,
  InsertDataByFileResponse
} from '../../services/shipment-request.service';
import { Warehouse } from '../../interfaces/Warehouses.interfaces';
import { RoutingService } from '../../services/routing.service';
import { LabelGenerationService } from '../../services/label-generation.service';
import { WaybillDocumentService } from '../../services/waybill-document.service';
import { WaybillService } from '../../services/waybill.service';
import { TourGuideService } from '../../services/tour-guide.service';
import { TourGuideComponent } from '../../components/tour-guide/tour-guide.component';
import { SHIPMENT_REQUEST_TOUR_CONFIG } from '../../configs/shipment-request-tour.config';
import { catchError, finalize, switchMap, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { interval, of, Subject } from 'rxjs';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FunctionService } from '../../services/function.service';
import { Router } from '@angular/router';
import { BaseComponent } from '../../base/base.component';
import { NotificationService } from '../../services/notification.service';
import { ErrorHelperService } from '../../services/error-helper.service';
import { BorderoGroup } from '../../interfaces/LoadingList.interfaces';
import { DealerManagementPopupComponent, DealerPopupMode } from './dealer-management-popup/dealer-management-popup.component';

/**
 * File Excel caricato nella sezione di conversione e già convertito in CSV.
 * Il workbook resta in memoria per poter cambiare foglio senza rileggere il file.
 */
interface ConvertedExcelFile {
  id: number;
  name: string;
  size: number;
  sheetNames: string[];
  selectedSheet: string;
  workbook: XLSX.WorkBook;
  /** CSV del foglio ridotto alle sole righe collo (5C / 5B nella seconda colonna) */
  csv: string;
  /** Riga del foglio di origine per ogni riga di `csv`, stesso indice */
  csvSourceRows: number[];
  /** Righe del foglio scartate perché senza 5C / 5B nella seconda colonna */
  skippedRows: number;
}

/** Provenienza di una riga del CSV accorpato */
interface CsvRowSource {
  fileId: number;
  fileName: string;
  /** Numero di riga nel foglio Excel di origine */
  sheetRow: number;
}

/** Riga del CSV con almeno un dato obbligatorio mancante */
interface CsvCheckIssue {
  /** Numero della riga nel CSV accorpato */
  line: number;
  /** File di provenienza della riga */
  fileId: number;
  fileName: string;
  /** Numero di riga nel foglio Excel, per rintracciarla nel file originale */
  sheetRow: number;
  /** Descrizione dei dati mancanti su quella riga */
  messages: string[];
}

/** Esito del controllo statico del CSV prodotto dai file caricati */
interface CsvCheckResult {
  /** Righe lette dai file, comprese quelle non di tipo 5C / 5B */
  readRows: number;
  /** Righe scartate in conversione perché senza 5C / 5B nella seconda colonna */
  skippedRows: number;
  /** Numero di colli, cioè di righe 5C prelevate */
  parcelCount: number;
  totalWeight: number;
  totalVolume: number;
  issues: CsvCheckIssue[];
}

@Component({
  selector: 'app-shipment-request',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, NgxPaginationModule, TourGuideComponent, DealerManagementPopupComponent],
  templateUrl: './shipment-request.component.html',
  styleUrl: './shipment-request.component.css'
})
export class ShipmentRequestComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  private _requestedDateInput?: ElementRef;

  /**
   * L'input "Data Richiesta" vive dentro l'*ngIf `showInsertFormSections`:
   * ogni volta che la sezione viene distrutta e ricreata (es. scambio
   * flusso <-> form manuale) l'elemento DOM è nuovo e flatpickr va
   * riagganciato, altrimenti il click sull'input (readonly) non apre più
   * il calendario.
   */
  @ViewChild('requestedDateInput', { static: false })
  set requestedDateInput(ref: ElementRef | undefined) {
    const changed = ref?.nativeElement !== this._requestedDateInput?.nativeElement;
    this._requestedDateInput = ref;
    if (!changed) return;
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
    if (ref) {
      // Deferito per evitare ExpressionChangedAfterItHasBeenChecked:
      // initializeFlatpickr tocca proprietà legate al template (isTimeRestricted, ...).
      setTimeout(() => this.initializeFlatpickr());
    }
  }
  get requestedDateInput(): ElementRef {
    return this._requestedDateInput!;
  }
  @ViewChild('excelFileInput', { static: false }) excelFileInput!: ElementRef<HTMLInputElement>;
  shipmentForm: FormGroup;
  isFormCollapsed = false; // Stato del form (aperto/chiuso)
  isCompactTable = false; // Vista compatta della tabella risultati (strumentario)

  // ===== Conversione Excel -> CSV (sezione drag & drop, multi-file) =====
  /** Estensioni accettate: SheetJS legge tutte le versioni di Excel, più ODS e CSV */
  readonly excelAcceptedExtensions = ['.xlsx', '.xlsm', '.xlsb', '.xls', '.xltx', '.xltm', '.ods', '.csv'];
  readonly excelMaxFileSizeMb = 15;
  /**
   * Lunghezza massima della stringa CSV accettata da /insert-data-by-file.
   * Deve restare allineata a INSERT_DATA_BY_FILE_MAX_LENGTH lato backend.
   */
  readonly excelCsvMaxLength = 5_000_000;
  isExcelDragOver = false;
  isConvertingExcel = false;
  /** File convertiti, nell'ordine di inserimento: i CSV vengono accorpati in questo ordine */
  excelFiles: ConvertedExcelFile[] = [];
  /** Stringa CSV unica risultante dall'unione di tutti i file */
  excelCsvOutput = '';
  /**
   * Provenienza di ogni riga di `excelCsvOutput`, stesso indice (0 = prima riga).
   * Le righe marcatore non hanno origine e valgono null.
   */
  csvRowSources: (CsvRowSource | null)[] = [];
  /** Se true, prima di ogni blocco viene inserita una riga marcatore con il nome del file */
  excelInsertFileMarkers = true;
  excelErrorMessage = '';
  /** Invio a /insert-data-by-file in corso */
  isSendingCsv = false;
  /** Esito dell'ultimo invio, mostrato sotto la stringa */
  excelSendResult: InsertDataByFileResponse | null = null;
  private excelFileSequence = 0;

  // Parcels collapse state management
  parcelsCollapsed: boolean[] = []; // Array per gestire lo stato di collasso di ogni collo

  // Make Math available in template
  Math = Math;

  // Borderò data grouped by warehouseTo
  borderoGrouped: BorderoGroup[] = [];
  isBorderoLoading = false;
  stampingBordero = new Set<string>();
  generatingBordero = new Set<string>();

  // Main data arrays
  waybills: Waybill[] = [];
  parcels: Parcel[] = [];
  // Riepilogo giornaliero calcolato dal backend (endpoint /summary)
  parcelsSummary: ParcelsSummary = {
    distinctWaybills: 0,
    totalParcels: 0,
    totalWeight: 0,
    totalVolume: 0,
    totalInsuredValue: 0
  };
  dealers: Dealer[] = [];
  brands: Brand[] = [];
  clients: Client[] = [];
  clientsBrands: ClientsBrands[] = [];
  warehouses: Warehouse[] = [];
  filteredClients: Client[] = [];
  filteredBrands: Brand[] = [];
  filteredWarehouses: Warehouse[] = [];

  /**
   * Codici magazzino assegnati all'utente nel proprio profilo
   * (`userFunctions.warehouses` da localStorage). Se non vuoto, viene usato
   * per preselezionare un magazzino quando il cliente scelto ne ha
   * disponibili più di uno.
   */
  private userWarehouseCodes: string[] = [];

  // Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 25;

  // View control properties
  viewMode: 'waybill' | 'parcel' = 'waybill';
  displayMode: 'table' | 'cards' = 'table';
  searchTerm: string = '';

  // Sorting state
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';  // Loading and error states
  isLoading = false;
  isSubmitting = false;
  isExportingExcel = false;
  // Editing state
  editingWaybillId: number | null = null;
  editingParcelId: number | null = null;

  // Autocomplete suggestions for different fields
  dealerCodeSuggestions: Dealer[] = [];
  dealerNameSuggestions: Dealer[] = [];
  dealerVatSuggestions: Dealer[] = [];
  // Autocomplete visibility flags
  showDealerCodeSuggestions = false;
  showDealerNameSuggestions = false;
  showDealerVatSuggestions = false;  // Dealer selection state
  isDealerSelected = false;
  selectedDealer: Dealer | null = null;
  
  // Client and Brand selection check
  isClientBrandSelected = false;
  availableDealersCount = 0;
  
  // Easter egg properties
  showConfetti = false;
  confettiTimeout: any;
  mathUtil = Math; // Expose Math to template
  
  // Dealer popup properties
  showDealerInsertPopup = false;
  dealerForm: FormGroup | null = null;

  // ===== Dealer Management Popup (create / edit) =====
  showDealerManagementPopup = false;
  dealerManagementMode: DealerPopupMode = 'create';
  dealerManagementInitial: Dealer | null = null;
  
  // Distributive insertion popup properties
  showDistributiveInsertPopup = false;
  distributiveForm: FormGroup | null = null;

  // ============================================================
  // Settaggio campi (fornito dal backend tramite Client.clientFieldSettings)
  // ============================================================
  /**
   * Settaggio dei campi per il client attualmente selezionato.
   * Se null o se la chiave non è presente nei `fields`, il campo è considerato
   * abilitato per default. Vedi `isFieldEnabled` / `getFieldPlaceholder`.
   */
  currentFieldSettings: ClientFieldSettings | null = null;

  /** Funzione che governa la sezione Borderò */
  private readonly SHIPMENT_REQUEST_LOADING_LIST = 'shipment-request-loading-list';

  get showBorderoSection(): boolean {
    const userRole = localStorage.getItem('userRole');
    if (userRole?.toUpperCase() === 'ADMIN') return true;
    const stored = localStorage.getItem('userFunctions');
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      const fn = (parsed.functions as any[])?.find(f => f.functionName === this.SHIPMENT_REQUEST_LOADING_LIST);
      return fn?.read === true;
    } catch {
      return false;
    }
  }

  /**
   * Suddivisione del borderò per zone del cliente: opzione riservata a chi ha
   * il permesso `special` sulla funzione della sezione Borderò.
   * Per l'ADMIN FunctionService risponde sempre true (allowAll).
   */
  get showBorderoZoneSplitOption(): boolean {
    return this.functionService.canSpecial(this.SHIPMENT_REQUEST_LOADING_LIST);
  }

  /** Chiave di persistenza del toggle di suddivisione borderò */
  private readonly SPLIT_BORDERO_STORAGE_KEY = 'shipmentRequestSplitBorderoByClientZones';

  /**
   * Stato del toggle "suddividi per Zone del Cliente": attivo di default e
   * ripristinato da localStorage a ogni accesso alla pagina.
   * Al momento è solo un interruttore: non filtra i dati e non viene inviato
   * al backend.
   */
  splitBorderoByClientZones = true;

  /**
   * Valore effettivamente inviato al backend: senza il permesso `special` il
   * toggle non è visibile e la suddivisione non va mai richiesta, anche se in
   * localStorage è rimasto un `true` da quando il permesso c'era.
   */
  private get effectiveSplitBorderoByClientZones(): boolean {
    return this.showBorderoZoneSplitOption && this.splitBorderoByClientZones;
  }

  /**
   * Colonne della tabella borderò: la Zona Cliente compare solo col permesso
   * `special`, e le righe di caricamento/vuoto devono adeguare il colspan.
   */
  get borderoColumnCount(): number {
    return this.showBorderoZoneSplitOption ? 8 : 7;
  }

  /** Ripristina la scelta dell'utente; senza valore salvato resta attivo. */
  private loadSplitBorderoPreference(): void {
    const saved = localStorage.getItem(this.SPLIT_BORDERO_STORAGE_KEY);
    this.splitBorderoByClientZones = saved === null ? true : saved === 'true';
  }

  toggleSplitBorderoByClientZones(): void {
    this.splitBorderoByClientZones = !this.splitBorderoByClientZones;
    localStorage.setItem(this.SPLIT_BORDERO_STORAGE_KEY, String(this.splitBorderoByClientZones));
    // Il raggruppamento lo decide il backend: serve una nuova chiamata
    this.loadBordero();
  }

  get isAdmin(): boolean {
    return localStorage.getItem('userRole')?.toUpperCase() === 'ADMIN';
  }

  /** Funzione che abilita l'inserimento della richiesta tramite file */
  private readonly SHIPMENT_REQUEST_BY_FILE = 'shipment-request-by-file';

  /**
   * True se all'utente è assegnata la funzione di inserimento da file.
   * Per l'ADMIN FunctionService risponde sempre true (allowAll).
   */
  get hasShipmentRequestByFile(): boolean {
    return this.functionService.hasFunction(this.SHIPMENT_REQUEST_BY_FILE);
  }

  /**
   * Sezione "Inserimento Dati via File Excel": visibile all'ADMIN e agli utenti
   * con la funzione dedicata; nascosta a tutti gli altri.
   */
  get showExcelImportSection(): boolean {
    return this.isAdmin || this.hasShipmentRequestByFile;
  }

  /**
   * Sezioni del form manuale (Dati della Bolla, Dati dei Colli, Dati Destinazione):
   * l'utente abilitato all'inserimento da file lavora solo con quello, quindi le
   * sezioni gli vengono sostituite dalla sezione file. L'ADMIN continua a vedere tutto.
   */
  get showManualFormSections(): boolean {
    return this.isAdmin || !this.hasShipmentRequestByFile;
  }

  // ============================================================
  // Scambio tra i due canali di inserimento (da flusso / da form)
  // ============================================================
  /** Chiave di persistenza del canale di inserimento scelto dall'utente */
  private readonly DATA_ENTRY_VIEW_STORAGE_KEY = 'shipmentRequestDataEntryView';

  /**
   * Canale di inserimento attualmente mostrato:
   * - 'file': sezione "Inserimento Dati via File Excel" (da flusso)
   * - 'form': form manuale di inserimento
   * Rilevante solo per chi dispone dell'inserimento da file: senza quella
   * funzione resta valido il comportamento di sempre (solo form).
   */
  dataEntryView: 'file' | 'form' = 'file';

  /**
   * True se all'utente è disponibile l'inserimento da file: solo in questo caso
   * i due canali coesistono e ha senso il pulsante di scambio.
   */
  get canSwitchDataEntryView(): boolean {
    return this.showExcelImportSection;
  }

  /**
   * Sezione file: mostrata a chi ne ha diritto, e solo mentre è selezionato il
   * canale "da flusso".
   */
  get showExcelImportPanel(): boolean {
    return this.showExcelImportSection && this.dataEntryView === 'file';
  }

  /**
   * Form manuale: con lo scambio disponibile compare solo scegliendo il canale
   * "form", altrimenti valgono le regole di visibilità di sempre.
   */
  get showInsertFormSections(): boolean {
    return this.canSwitchDataEntryView
      ? this.dataEntryView === 'form'
      : this.showManualFormSections;
  }

  /** Ripristina il canale scelto dall'utente; senza valore salvato vale il flusso. */
  private loadDataEntryViewPreference(): void {
    const saved = localStorage.getItem(this.DATA_ENTRY_VIEW_STORAGE_KEY);
    this.dataEntryView = saved === 'form' ? 'form' : 'file';
  }

  /** Scambia flusso <-> form manuale, memorizzando la scelta. */
  toggleDataEntryView(): void {
    if (!this.canSwitchDataEntryView) return;
    this.dataEntryView = this.dataEntryView === 'file' ? 'form' : 'file';
    localStorage.setItem(this.DATA_ENTRY_VIEW_STORAGE_KEY, this.dataEntryView);

    // Il form va presentato aperto, anche se era stato collassato in precedenza
    if (this.dataEntryView === 'form') {
      this.isFormCollapsed = false;
    }
  }

  // ============================================================
  // Controllo statico del CSV caricato
  // ============================================================
  /*
   * Layout del file: le colonne sono numerate da 0 e la colonna 0 è di norma
   * vuota. La seconda colonna (indice 1) porta il tipo di riga (5C / 5B).
   */
  /** Colonna che identifica il tipo di riga: la seconda del file */
  private readonly CSV_TYPE_COLUMN = 1;
  /** Valori che identificano le righe collo da prelevare */
  private readonly CSV_TYPE_VALUES: string[] = ['5C', '5B'];
  /** Colonna del peso e colonna del volume */
  private readonly CSV_WEIGHT_COLUMN = 11;
  private readonly CSV_VOLUME_COLUMN = 12;
  /** Colonne obbligatorie sulle righe prelevate, con la relativa etichetta */
  private readonly CSV_REQUIRED_COLUMNS: { column: number; label: string }[] = [
    { column: 2, label: 'Codice Collo' },
    { column: 3, label: 'Codice Spedizione' },
    { column: 4, label: 'Codice Dealer' },
    { column: 11, label: 'Peso' },
    { column: 12, label: 'Volume' }
  ];

  /** Esito del controllo sul CSV corrente, mostrato nella sezione file */
  csvCheck: CsvCheckResult | null = null;

  /** Righe con dati mancanti per id file, per segnalare il problema sulla card */
  csvIssuesByFile: Record<number, number> = {};

  /** Numero di righe con dati mancanti prodotte da un singolo file */
  getFileIssueCount(file: ConvertedExcelFile): number {
    return this.csvIssuesByFile[file.id] ?? 0;
  }

  /**
   * True se la riga è una riga collo, cioè se la seconda colonna contiene
   * "5C" o "5B". È il criterio con cui il parser decide se prendere la riga.
   */
  private isParcelRow(cells: string[]): boolean {
    const type = (cells[this.CSV_TYPE_COLUMN] ?? '').trim().toUpperCase();
    return this.CSV_TYPE_VALUES.some(value => type.includes(value));
  }

  /**
   * Controllo statico del CSV prodotto dai file caricati. Le righe non collo
   * sono già state scartate in conversione da `sheetToParcelCsv`, quindi qui:
   * - verifica che le colonne 2, 3, 4, 11 e 12 siano valorizzate
   *   (per peso e volume lo zero non è un valore valido);
   * - conta i colli e somma peso (colonna 11) e volume (colonna 12);
   * - elenca le righe in cui manca almeno un dato obbligatorio.
   */
  private buildCsvCheck(): void {
    if (!this.excelCsvOutput) {
      this.csvCheck = null;
      this.csvIssuesByFile = {};
      return;
    }

    const parcelRows = this.parseCsvRows(this.excelCsvOutput);

    let totalWeight = 0;
    let totalVolume = 0;
    const issues: CsvCheckIssue[] = [];

    parcelRows.forEach(row => {
      totalWeight += this.parseCsvNumber(row.cells[this.CSV_WEIGHT_COLUMN]);
      totalVolume += this.parseCsvNumber(row.cells[this.CSV_VOLUME_COLUMN]);

      const messages: string[] = [];

      this.CSV_REQUIRED_COLUMNS.forEach(({ column, label }) => {
        const value = (row.cells[column] ?? '').trim();
        const isNumericColumn = column === this.CSV_WEIGHT_COLUMN || column === this.CSV_VOLUME_COLUMN;

        if (!value) {
          messages.push(`${label} mancante (colonna ${column})`);
        } else if (isNumericColumn && this.parseCsvNumber(value) === 0) {
          messages.push(`${label} a zero (colonna ${column})`);
        }
      });

      if (messages.length) {
        const source = this.csvRowSources[row.line - 1];
        issues.push({
          line: row.line,
          fileId: source?.fileId ?? 0,
          fileName: source?.fileName ?? '',
          sheetRow: source?.sheetRow ?? row.line,
          messages
        });
      }
    });

    // Conteggio per file, così la card del singolo file può segnalare il problema
    this.csvIssuesByFile = {};
    issues.forEach(issue => {
      this.csvIssuesByFile[issue.fileId] = (this.csvIssuesByFile[issue.fileId] ?? 0) + 1;
    });

    const skippedRows = this.excelFiles.reduce((total, file) => total + file.skippedRows, 0);

    this.csvCheck = {
      readRows: parcelRows.length + skippedRows,
      skippedRows,
      parcelCount: parcelRows.length,
      totalWeight,
      totalVolume,
      issues
    };
  }

  /**
   * Spezza il CSV in righe e colonne, saltando le righe vuote e i marcatori di
   * inizio file inseriti da `buildExcelFileMarker`. Il numero di riga è quello
   * del CSV complessivo, così la segnalazione è rintracciabile nel file.
   */
  private parseCsvRows(csv: string): { line: number; cells: string[] }[] {
    return csv
      .split(/\r?\n/)
      .map((line, index) => ({ line: index + 1, cells: line.split(';').map(cell => cell.trim()) }))
      .filter(row => row.cells.some(cell => cell.length > 0) && !row.cells[0].startsWith('####'));
  }

  /** Converte un valore del CSV in numero, accettando la virgola decimale. */
  private parseCsvNumber(value: string | undefined): number {
    const parsed = Number((value ?? '').trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Settaggio campi già disponibile in locale per un client (payload di login),
   * senza passare dal backend.
   */
  private findLocalFieldSettings(clientCode: string): ClientFieldSettings | null {
    const cb = this.clientsBrands.find(c => c.clientCode === clientCode);
    const fromClients = this.clients.find(c => c.clientCode === clientCode);
    return cb?.clientFieldSettings ?? fromClients?.clientFieldSettings ?? null;
  }

  isExportingCeva = false;

  exportDataToCeva(): void {
    this.isExportingCeva = true;
    this.waybillService.exportDataToCeva().subscribe({
      next: () => {
        this.isExportingCeva = false;
        this.notificationService.success('Export CEVA completato con successo');
      },
      error: (error) => {
        this.isExportingCeva = false;
        console.error('Errore export CEVA:', error);
        this.notificationService.error('Errore durante l\'export CEVA');
      }
    });
  }

  // Confirmation dialog properties
  showConfirmDialog = false;
  confirmationTitle = '';
  confirmationMessage = '';
  confirmationAction: (() => void) | null = null;

  // Dropdown management properties for action buttons
  openDropdowns = new Set<string>();

  // Popup informativo per bolle in stato PENDING (in attesa completamento dealer)
  pendingInfoWaybillId: number | null = null;

  // Flatpickr instance
  private flatpickrInstance: any;

  // Time restriction properties
  isTimeRestricted = false;
  timeRestrictionMessage = '';
  isDateFieldRestricted = false; // Controls field-specific warnings
  isWeekendRestricted = false; // Controls weekend-specific warnings
  weekendRestrictionMessage = '';

  /**
   * Orario oltre il quale non è più possibile inserire una richiesta
   * per la giornata odierna. Letto dal parametro `max_date_insert` del
   * `clientFieldSettings` del client selezionato. Se non configurato per
   * il client, nessuna restrizione viene applicata (come per gli altri
   * parametri di settaggio campi).
   */
  private maxInsertHour: number | null = null;
  private maxInsertMinute = 0;

  /**
   * Data (dd/MM/yyyy) a cui il campo "Data Richiesta" è stato spostato
   * automaticamente dalla restrizione oraria. Serve a distinguere una data
   * avanzata dal sistema da una scelta dall'utente: solo la prima viene
   * riportata a oggi quando la restrizione decade (es. quando arrivano le
   * settings del client con un `max_date_insert` più permissivo).
   */
  private autoAdvancedDateStr: string | null = null;

  // Volume validation cache for performance
  private volumeValidationCache = new Map<number, any>();

  // FormControl per ricerca con debounce
  searchControl = new FormControl('');
  private destroy$ = new Subject<void>();

  // Cache properties per ottimizzazione performance
  private _filteredWaybills: Waybill[] = [];
  private _filteredParcels: any[] = [];
  private _parcelCountMap = new Map<string, number>();
  private _volumeValidationCache = new Map<string, any>();
  private _lastCalculationState = {
    searchTerm: '',
    sortColumn: '',
    sortDirection: '',
    waybillsLength: 0,
    parcelsLength: 0,
    dealersLength: 0
  };

  // Enums per i dropdown
  waybillStatuses = Object.values(WaybillStatus);
  parcelStatuses = Object.values(ParcelStatus);
  deliveryMethods = Object.values(WaybillDeliveryMethod);
  deliveryTypes = Object.values(WaybillDeliveryType);
  dealerStatuses = Object.values(DealerStatus);

  // Priority options (simple string values)
  priorities = ['NORMAL', 'URGENT'];
  
  // Getter per totalPages
  get totalPages(): number {
    const total = this.viewMode === 'waybill' ? this.filteredWaybills.length : this.filteredParcels.length;
    return Math.ceil(total / this.itemsPerPage);
  }

  /** Numero di elementi filtrati nella vista attiva (bolle o colli) */
  get totalItems(): number {
    return this.viewMode === 'waybill' ? this.filteredWaybills.length : this.filteredParcels.length;
  }

  /** Primo elemento mostrato nella pagina corrente (0 se la lista è vuota) */
  get rangeStart(): number {
    return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  /** Ultimo elemento mostrato nella pagina corrente */
  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  // Utility function to format date to yyyy-mm-dd format for frontend input
  private formatDateForInput(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '';
    
    let date: Date;
    if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format to yyyy-mm-dd
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  // Check if current time is after the configured closing time and update
  // time restrictions accordingly.
  private checkTimeRestrictions(): void {
    const now = new Date();

    // Carica (o ricarica) il limite orario configurato per il client.
    this.loadMaxInsertTimeFromUserFunctions();

    // Se il client non ha configurato `max_date_insert`, nessuna
    // restrizione viene applicata (coerente con gli altri parametri di
    // settaggio campi: assenti = nessun vincolo).
    if (this.maxInsertHour === null) {
      this.isTimeRestricted = false;
      this.timeRestrictionMessage = '';
      this.checkDateFieldRestriction();
      return;
    }

    const limitMinutes = this.maxInsertHour * 60 + this.maxInsertMinute;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    this.isTimeRestricted = currentMinutes >= limitMinutes;


    if (this.isTimeRestricted) {
      const limitLabel = this.formatHourMinute(this.maxInsertHour, this.maxInsertMinute);
      // Use subscription to ensure translation is loaded with fallback
      this.translate.get('SHIPMENT_REQUEST.TIME_RESTRICTION_MESSAGE', { time: limitLabel }).subscribe(
        (translation: string) => {
          this.timeRestrictionMessage = translation;
          // Fallback if translation key is returned instead of translated text
          if (translation === 'SHIPMENT_REQUEST.TIME_RESTRICTION_MESSAGE') {
            this.timeRestrictionMessage = `Dopo le ${limitLabel} non è possibile inserire richieste di spedizione per la giornata odierna. È possibile selezionare solo date future.`;
          }
        }
      );

      // Se il form ha ancora "oggi" come data, avanza al prossimo giorno valido (saltando i weekend) per allinearsi al minDate del flatpickr.
      const dateCtrl = this.shipmentForm.get('waybill.waybillDate');
      const todayStr = this.formatDateForDisplay(now);
      if (dateCtrl && (!dateCtrl.value || dateCtrl.value === todayStr)) {
        const nextValid = this.getNextValidDate(now);
        const nextValidStr = this.formatDateForDisplay(nextValid);
        dateCtrl.setValue(nextValidStr, { emitEvent: false });
        this.autoAdvancedDateStr = nextValidStr;
        if (this.flatpickrInstance) {
          this.flatpickrInstance.set('minDate', nextValid);
          this.flatpickrInstance.setDate(nextValid, false);
          if (this.requestedDateInput?.nativeElement) {
            this.requestedDateInput.nativeElement.value = nextValidStr;
          }
        }
      }
    } else {
      this.timeRestrictionMessage = '';
      // Se la restrizione NON è attiva, e la data è su domani (o dopo), riportala a oggi se oggi è selezionabile
      const dateCtrl = this.shipmentForm.get('waybill.waybillDate');
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayStr = this.formatDateForDisplay(today);
      if (dateCtrl) {
        // Se la data è dopo oggi, riportala a oggi
        let selectedDate: Date | null = null;
        if (dateCtrl.value) {
          // Prova a parsare la data selezionata
          if (typeof dateCtrl.value === 'string') {
            // accetta sia dd/MM/yyyy che yyyy-MM-dd
            const parts = dateCtrl.value.split('/');
            if (parts.length === 3) {
              // dd/MM/yyyy
              selectedDate = new Date(+parts[2], +parts[1] - 1, +parts[0]);
            } else {
              // yyyy-MM-dd
              selectedDate = new Date(dateCtrl.value);
            }
          } else if (dateCtrl.value instanceof Date) {
            selectedDate = dateCtrl.value;
          }
        }
        // Una data spostata dal sistema (restrizione che nel frattempo è
        // decaduta, es. arrivato il max_date_insert del client) va riportata
        // a oggi; una data scelta dall'utente resta invariata.
        const wasAutoAdvanced = !!dateCtrl.value && dateCtrl.value === this.autoAdvancedDateStr;
        if (!selectedDate || wasAutoAdvanced) {
          this.autoAdvancedDateStr = null;
          dateCtrl.setValue(todayStr, { emitEvent: false });
          if (this.flatpickrInstance) {
            this.flatpickrInstance.set('minDate', today);
            this.flatpickrInstance.setDate(today, false);
            if (this.requestedDateInput?.nativeElement) {
              this.requestedDateInput.nativeElement.value = todayStr;
            }
          }
        } else if (this.flatpickrInstance) {
          // Aggiorna comunque la minDate
          this.flatpickrInstance.set('minDate', today);
        }
      }
    }

    // Initially set field restriction same as time restriction
    this.checkDateFieldRestriction();
  }

  /**
   * Restituisce il prossimo giorno valido (non weekend) successivo a `from`.
   * Sabato → Lunedì, Domenica → Lunedì, altrimenti il giorno seguente
   * (eventualmente avanzando se cade nel weekend).
   */
  private getNextValidDate(from: Date): Date {
    const next = new Date(from);
    next.setDate(next.getDate() + 1);
    while (next.getDay() === 0 || next.getDay() === 6) {
      next.setDate(next.getDate() + 1);
    }
    return next;
  }

  /**
   * Carica il limite orario di inserimento dal parametro `max_date_insert`
   * presente nel `clientFieldSettings` del client attualmente selezionato
   * (`currentFieldSettings`). Accetta i formati: numero (ora intera, es.
   * 23), stringa "HH" (es. "23"), "HH:mm" (es. "23:30") o "HH:mm:ss".
   *
   * Se il parametro non è presente o non valido, `maxInsertHour` resta
   * `null` e nessuna restrizione viene applicata (coerente con gli altri
   * parametri di settaggio campi).
   */
  private loadMaxInsertTimeFromUserFunctions(): void {
    // Nessun default hardcoded: senza `max_date_insert` configurato per il
    // client, nessuna restrizione oraria viene applicata.
    this.maxInsertHour = null;
    this.maxInsertMinute = 0;

    const raw = this.currentFieldSettings?.max_date_insert;
    if (raw === null || raw === undefined || raw === '') return;

    let h: number | null = null;
    let m: number = 0;

    if (typeof raw === 'number' && Number.isFinite(raw)) {
      h = Math.trunc(raw);
    } else if (typeof raw === 'string') {
      const match = raw.trim().match(/^(\d{1,2})(?::(\d{1,2}))?(?::\d{1,2})?$/);
      if (match) {
        h = parseInt(match[1], 10);
        m = match[2] ? parseInt(match[2], 10) : 0;
      }
    }

    if (h === null || isNaN(h) || h < 0 || h > 23 || m < 0 || m > 59) {
      // console.warn('[ShipmentRequest] max_date_insert non valido, nessuna restrizione applicata:', raw);
      return;
    }
    this.maxInsertHour = h;
    this.maxInsertMinute = m;
  }

  /** Formatta un orario HH:MM con zero-padding. */
  private formatHourMinute(hour: number, minute: number): string {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  /** True se il client selezionato ha un `max_date_insert` configurato. */
  get hasMaxInsertTime(): boolean {
    return this.maxInsertHour !== null;
  }

  /**
   * Etichetta HH:MM dell'orario limite corrente (parametro
   * `max_date_insert` del client). Usata dalle traduzioni per mostrare
   * l'ora effettiva nel warning; vuota se nessun limite è configurato.
   */
  get maxInsertTimeLabel(): string {
    if (this.maxInsertHour === null) return '';
    return this.formatHourMinute(this.maxInsertHour, this.maxInsertMinute);
  }

  // Check if the selected date requires field-level restrictions
  private checkDateFieldRestriction(): void {
    // Quando siamo dopo l'orario di chiusura il warning sul campo
    // "Data Richiesta" deve restare visibile, indipendentemente dalla data
    // selezionata (oggi/domani), per ricordare all'utente la regola.
    this.isDateFieldRestricted = this.isTimeRestricted;

    // Always check for weekend restriction
    this.checkWeekendRestriction();
  }

  // Check if the selected date is a weekend
  private checkWeekendRestriction(): void {
    const selectedDateValue = this.shipmentForm.get('waybill.waybillDate')?.value;
    
    if (!selectedDateValue) {
      this.isWeekendRestricted = false;
      this.weekendRestrictionMessage = '';
      return;
    }

    this.isWeekendRestricted = this.isDateWeekend(selectedDateValue);
    
    if (this.isWeekendRestricted) {
      // Use subscription to ensure translation is loaded with fallback
      this.translate.get('SHIPMENT_REQUEST.WEEKEND_RESTRICTION_MESSAGE').subscribe(
        (translation: string) => {
          this.weekendRestrictionMessage = translation;
          // Fallback if translation key is returned instead of translated text
          if (translation === 'SHIPMENT_REQUEST.WEEKEND_RESTRICTION_MESSAGE') {
            this.weekendRestrictionMessage = 'La data selezionata corrisponde a sabato o domenica. Le spedizioni non vengono elaborate nei giorni non lavorativi.';
          }
        }
      );
    } else {
      this.weekendRestrictionMessage = '';
    }
  }

  // Utility function to format date to dd/MM/yyyy format for display
  private formatDateForDisplay(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '';
    
    let date: Date;
    if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format to dd/MM/yyyy
    return String(date.getDate()).padStart(2, '0') + '/' +
      String(date.getMonth() + 1).padStart(2, '0') + '/' +
      date.getFullYear();
  }
  // Utility function to convert dd/MM/yyyy or YYYY-MM-DD back to ISO format for server
  private convertDateToISO(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return new Date().toISOString();
    
    let date: Date;
    
    if (typeof dateValue === 'string') {
      // Check if it's in dd/MM/yyyy format
      if (dateValue.includes('/')) {
        const [day, month, year] = dateValue.split('/');
        // Create date with current time to preserve the hour
        const now = new Date();
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 
                       now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      } 
      // Check if it's in YYYY-MM-DD format (from flatpickr)
      else if (dateValue.includes('-')) {
        date = new Date(dateValue);
      } 
      // Otherwise try to parse as normal date string
      else {
        date = new Date(dateValue);
      }
    } else {
      date = dateValue;
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return new Date().toISOString();
    }
    
    return date.toISOString();
  }

  // Custom validator to check that the date is not Saturday or Sunday
  private noWeekendsValidator(control: any) {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    let date: Date;
    if (typeof control.value === 'string') {
      // Handle dd/MM/yyyy format
      if (control.value.includes('/')) {
        const [day, month, year] = control.value.split('/');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } 
      // Handle YYYY-MM-DD format
      else if (control.value.includes('-')) {
        date = new Date(control.value);
      } 
      else {
        date = new Date(control.value);
      }
    } else {
      date = control.value;
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null; // Let other validators handle invalid dates
    }

    // Check if it's Saturday (6) or Sunday (0)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { weekend: true };
    }

    return null;
  }

  // Helper method to check if a date is weekend
  private isDateWeekend(dateValue: string): boolean {
    if (!dateValue) return false;
    
    let date: Date;
    if (dateValue.includes('/')) {
      // Handle dd/MM/yyyy format
      const [day, month, year] = dateValue.split('/');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return false;

    // Check if it's Saturday (6) or Sunday (0)
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  // Helper method to check if a waybill can be edited based on date and time restrictions
  canEditWaybill(waybillDate: string): boolean {
    if (!waybillDate) return true; // If no date, allow editing
    
    // Parse the waybill date
    let waybillDateObj: Date;
    if (waybillDate.includes('/')) {
      // Handle dd/MM/yyyy format
      const [day, month, year] = waybillDate.split('/');
      waybillDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      waybillDateObj = new Date(waybillDate);
    }

    // Check if date is valid
    if (isNaN(waybillDateObj.getTime())) return true; // If invalid date, allow editing

    // Get today's date
    const today = new Date();
    const todayStr = this.formatDateForDisplay(today);
    const waybillDateStr = this.formatDateForDisplay(waybillDateObj);

    // If waybill date is not today, allow editing
    if (waybillDateStr !== todayStr) return true;

    // If waybill date is today, check time restriction (oltre l'orario configurato).
    // Se il client non ha configurato `max_date_insert`, nessuna restrizione.
    this.loadMaxInsertTimeFromUserFunctions();
    if (this.maxInsertHour === null) return true;
    const limitMinutes = this.maxInsertHour * 60 + this.maxInsertMinute;
    const currentMinutes = today.getHours() * 60 + today.getMinutes();
    return currentMinutes < limitMinutes;
  }

  // Custom validator for decimal numbers with max 3 decimal places and max value 99999.999
  private decimalValidator(control: any) {
    if (!control.value && control.value !== 0) {
      return null; // Let required validator handle empty values
    }

    const value = parseFloat(control.value);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return { invalidNumber: true };
    }

    // Check maximum value
    if (value > 99999.999) {
      return { maxValue: true };
    }

    // Check minimum value (must be positive)
    if (value < 0) {
      return { minValue: true };
    }

    // Check decimal places (max 3)
    const stringValue = control.value.toString();
    if (stringValue.includes('.')) {
      const decimalPart = stringValue.split('.')[1];
      if (decimalPart && decimalPart.length > 3) {
        return { maxDecimals: true };
      }
    }

    return null;
  }

  // Custom validator for weight and volume with minimum 0.001
  private weightVolumeValidator(control: any) {
    if (!control.value && control.value !== 0) {
      return null; // Let required validator handle empty values
    }

    const value = parseFloat(control.value);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return { invalidNumber: true };
    }

    // Check maximum value
    if (value > 99999.999) {
      return { maxValue: true };
    }

    // Check minimum value (must be at least 0.001)
    if (value < 0.001) {
      return { minWeightVolume: true };
    }

    // Check decimal places (max 3)
    const stringValue = control.value.toString();
    if (stringValue.includes('.')) {
      const decimalPart = stringValue.split('.')[1];
      if (decimalPart && decimalPart.length > 3) {
        return { maxDecimals: true };
      }
    }

    return null;
  }

  // Custom validator specifically for volume with 4 decimal places
  private volumeValidator(control: any) {
    if (!control.value && control.value !== 0) {
      return null; // Let required validator handle empty values
    }

    const value = parseFloat(control.value);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return { invalidNumber: true };
    }

    // Check maximum value for volume (99999.9999)
    if (value > 99999.9999) {
      return { maxValue: true };
    }

    // Check minimum value (must be at least 0.0001)
    if (value < 0.0001) {
      return { minWeightVolume: true };
    }

    // Check decimal places (max 4 for volume)
    const stringValue = control.value.toString();
    if (stringValue.includes('.')) {
      const decimalPart = stringValue.split('.')[1];
      if (decimalPart && decimalPart.length > 4) {
        return { maxDecimalsVolume: true };
      }
    }

    return null;
  }

  // Custom validator for insured value with max 2 decimal places
  private insuredValueValidator(control: any) {
    if (!control.value && control.value !== 0) {
      return null; // Let required validator handle empty values
    }

    const value = parseFloat(control.value);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return { invalidNumber: true };
    }

    // Check maximum value
    if (value > 99999.99) {
      return { maxValueInsured: true };
    }

    // Check minimum value (must be positive or zero)
    if (value < 0) {
      return { minValue: true };
    }

    // Check decimal places (max 2 for insured value)
    const stringValue = control.value.toString();
    if (stringValue.includes('.')) {
      const decimalPart = stringValue.split('.')[1];
      if (decimalPart && decimalPart.length > 2) {
        return { maxDecimalsInsured: true };
      }
    }

    return null;
  }

  // Custom validator for dimensions (length, width, height) with max 2 decimal places and range 0.00-99999.99
  private dimensionValidator(control: any) {
    if (!control.value && control.value !== 0) {
      return null; // Allow empty values since dimensions are optional
    }

    const value = parseFloat(control.value);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return { invalidNumber: true };
    }

    // Check maximum value
    if (value > 99999.99) {
      return { maxValueDimension: true };
    }

    // Check minimum value (must be positive or zero)
    if (value < 0) {
      return { minValue: true };
    }

    // Check decimal places (max 1 for dimensions)
    const stringValue = control.value.toString();
    if (stringValue.includes('.')) {
      const decimalPart = stringValue.split('.')[1];
      if (decimalPart && decimalPart.length > 1) {
        return { maxDecimalsDimension: true };
      }
    }

    return null;
  }

  /**
   * Validates all decimal fields in parcels array
   * Returns array of error messages
   */
  private validateParcelsDecimals(parcels: any[]): string[] {
    const errors: string[] = [];

    parcels.forEach((parcel, index) => {
      const parcelNum = index + 1;

      // Validate weight (min 0.001, max 3 decimals)
      // if (parcel.parcelWeight !== null && parcel.parcelWeight !== undefined && parcel.parcelWeight !== '') {
      //   const weightError = this.weightVolumeValidator({ value: parcel.parcelWeight });
      //   if (weightError) {
      //     if (weightError.minWeightVolume) {
      //       errors.push(`Collo ${parcelNum}: Peso deve essere almeno 0.001 kg`);
      //     }
      //     if (weightError.maxValue) {
      //       errors.push(`Collo ${parcelNum}: Peso non può superare 99999.999 kg`);
      //     }
      //     if (weightError.maxDecimals) {
      //       errors.push(`Collo ${parcelNum}: Peso può avere al massimo 3 decimali`);
      //     }
      //     if (weightError.invalidNumber) {
      //       errors.push(`Collo ${parcelNum}: Peso non valido`);
      //     }
      //   }
      // }

      // // Validate volume (min 0.0001, max 4 decimals)
      // if (parcel.parcelVolume !== null && parcel.parcelVolume !== undefined && parcel.parcelVolume !== '') {
      //   const volumeError = this.volumeValidator({ value: parcel.parcelVolume });
      //   if (volumeError) {
      //     if (volumeError.minWeightVolume) {
      //       errors.push(`Collo ${parcelNum}: Volume deve essere almeno 0.0001 m³`);
      //     }
      //     if (volumeError.maxValue) {
      //       errors.push(`Collo ${parcelNum}: Volume non può superare 99999.9999 m³`);
      //     }
      //     if (volumeError.maxDecimalsVolume) {
      //       errors.push(`Collo ${parcelNum}: Volume può avere al massimo 4 decimali`);
      //     }
      //     if (volumeError.invalidNumber) {
      //       errors.push(`Collo ${parcelNum}: Volume non valido`);
      //     }
      //   }
      // }

      // Validate insured value (max 2 decimals)
      if (parcel.parcelInsuredValue !== null && parcel.parcelInsuredValue !== undefined && parcel.parcelInsuredValue !== '') {
        const insuredError = this.insuredValueValidator({ value: parcel.parcelInsuredValue });
        if (insuredError) {
          if (insuredError.maxValueInsured) {
            errors.push(`Collo ${parcelNum}: Valore assicurato non può superare 99999.99 €`);
          }
          if (insuredError.maxDecimalsInsured) {
            errors.push(`Collo ${parcelNum}: Valore assicurato può avere al massimo 2 decimali`);
          }
          if (insuredError.minValue) {
            errors.push(`Collo ${parcelNum}: Valore assicurato non può essere negativo`);
          }
          if (insuredError.invalidNumber) {
            errors.push(`Collo ${parcelNum}: Valore assicurato non valido`);
          }
        }
      }

      // Validate dimensions (max 1 decimal each)
      const dimensions = [
        { field: 'parcelLength', name: 'Lunghezza' },
        { field: 'parcelWidth', name: 'Larghezza' },
        { field: 'parcelHeight', name: 'Altezza' }
      ];

      dimensions.forEach(dim => {
        const value = parcel[dim.field];
        if (value !== null && value !== undefined && value !== '') {
          const dimError = this.dimensionValidator({ value });
          if (dimError) {
            if (dimError.maxValueDimension) {
              errors.push(`Collo ${parcelNum}: ${dim.name} non può superare 99999.99 cm`);
            }
            if (dimError.maxDecimalsDimension) {
              errors.push(`Collo ${parcelNum}: ${dim.name} può avere al massimo 1 decimale`);
            }
            if (dimError.minValue) {
              errors.push(`Collo ${parcelNum}: ${dim.name} non può essere negativa`);
            }
            if (dimError.invalidNumber) {
              errors.push(`Collo ${parcelNum}: ${dim.name} non valida`);
            }
          }
        }
      });
    });

    return errors;
  }

  constructor(
    public http: HttpClient,
    public fb: FormBuilder,
    public translate: TranslateService,
    public shipmentRequestService: ShipmentRequestService,
    public labelGenerationService: LabelGenerationService,
    public waybillDocumentService: WaybillDocumentService,
    public waybillService: WaybillService,
    private tourGuideService: TourGuideService,
    functionService: FunctionService,
    router: Router,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService
  ) {
    super(functionService, router);
    
    this.shipmentForm = this.createForm();
    
    // Inizializza l'array di collasso per il primo collo (espanso)
    this.parcelsCollapsed = [false];
    
    // Rendi il componente disponibile globalmente per la guida
    (window as any).shipmentComponent = this;
  }
  // constructor(
  //     public http: HttpClient,
  //     functionService: FunctionService,
  //     router: Router
  //   ) {
  //     super(functionService, router); // Passa i servizi al costruttore padre
  //   }
  ngOnInit(): void {
    // Initialize pagination from localStorage
    const savedPage = localStorage.getItem('shipmentRequestCurrentPage');
    this.currentPage = savedPage ? +savedPage : 1;

    const savedItemsPerPage = localStorage.getItem('shipmentRequestItemsPerPage');
    this.itemsPerPage = savedItemsPerPage ? +savedItemsPerPage : 25;

    this.loadSplitBorderoPreference();
    this.loadDataEntryViewPreference();

    // Set default sorting to show most recent entries first
    this.sortColumn = 'waybillCreatedAt';
    this.sortDirection = 'desc';

    // Check time restrictions
    this.checkTimeRestrictions();

    // Ricontrolla ogni minuto: così il superamento dell'orario limite
    // (max_date_insert) a pagina aperta sposta la data al giorno successivo
    // senza bisogno di ricaricare o cambiare client.
    interval(60_000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkTimeRestrictions());

    this.initializeTranslations();

    // Setup debounced search
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged(), // Only emit when value actually changes
        takeUntil(this.destroy$)
      )
      .subscribe(searchValue => {
        this.searchTerm = searchValue || '';
        this.currentPage = 1; // Reset to first page on search
      });

    // Carica configurazioni e dipendenze quando cambia il client selezionato
    // (sia da UI che da setValue programmatico).
    this.shipmentForm.get('waybill.waybillClientCode')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(clientCode => {
        this.onClientCodeChange(clientCode || '');
      });

    const stored = localStorage.getItem('userFunctions');
    // console.log("stored : ", stored);
    if(stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients;
      this.userWarehouseCodes = Array.isArray(parsed.warehouses) ? parsed.warehouses : [];
      // console.log('[ShipmentRequest] User warehouses from userFunctions:', this.userWarehouseCodes);
      this.filteredClients = this.clientsBrands.map(cb => ({
        clientCode: cb.clientCode,
        clientName: cb.clientName
      })).sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;       
        if (!a.clientName) return 1;                        
        if (!b.clientName) return -1;                       
        return a.clientName.localeCompare(b.clientName);   
      });

      if (this.filteredClients.length === 1) {
        const firstClientCode = this.filteredClients[0].clientCode;
        this.shipmentForm.get('waybill.waybillClientCode')?.setValue(firstClientCode);

        const firstClientBrands = this.clientsBrands.find(cb => cb.clientCode === firstClientCode);
        if (firstClientBrands) {
          this.filteredBrands = firstClientBrands.brands.map(b => ({
            brandCode: b.brandCode,
            brandName: b.brandName,
          })).sort((a, b) => {
            if (!a.brandName && !b.brandName) return 0;       
            if (!a.brandName) return 1;                        
            if (!b.brandName) return -1;                       
            return a.brandName.localeCompare(b.brandName);   
          });

          if (this.filteredBrands.length === 1) {
            this.shipmentForm.get('waybill.waybillBrandCode')?.setValue(this.filteredBrands[0].brandCode);
          } else {
            this.shipmentForm.get('waybill.waybillBrandCode')?.setValue('');
          }
        }

        // Gestione magazzini per il primo cliente
        // Nota: loadWarehouses() viene chiamato dopo, quindi gestiamo i magazzini dopo il caricamento
      } else {
        this.shipmentForm.get('waybill.waybillClientCode')?.setValue('');
        this.shipmentForm.get('waybill.waybillBrandCode')?.setValue('');

        const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
          cb.brands.map(b => ({
            brandCode: b.brandCode,
            brandName: b.brandName,
          }))
        );

        this.filteredBrands = Array.from(
          new Map(allBrands.map(b => [b.brandCode, b])).values()
        );
      }
      // this.filteredClients = this.clientsBrands.map(cb => ({
      //   clientCode: cb.clientCode,
      //   clientName: cb.clientName
      // }));      // Imposta il primo client come selezionato di default
      // if (this.filteredClients.length > 0) {
      //   const firstClientCode = this.filteredClients[0].clientCode;
      //   // Imposta il client nel form
      //   this.shipmentForm.get('waybill.waybillClientCode')?.setValue(firstClientCode);
        
      //   // Filtra i brand per il primo client
      //   const firstClientBrands = this.clientsBrands.find(cb => cb.clientCode === firstClientCode);
      //   if (firstClientBrands) {
      //     this.filteredBrands = this.clientsBrands.flatMap(c => c.brands.map(b => ({
      //       brandCode: b.brandCode,
      //       brandName: b.brandName,
      //     })));
          
      //     // Opzionalmente, imposta anche il primo brand come selezionato
      //     if (this.filteredBrands.length > 0) {
      //       this.shipmentForm.get('waybill.waybillBrandCode')?.setValue(this.filteredBrands[0].brandCode);
      //     }
      //   }
      // } else {
      //   // Se non ci sono client, mostra tutti i brand
      //   const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
      //     cb.brands.map(b => ({
      //       brandCode: b.brandCode,
      //       brandName: b.brandName,
      //     }))
      //   );

      //   this.filteredBrands = Array.from(
      //     new Map(allBrands.map(b => [b.brandCode, b])).values()
      //   );
      // }
      this.loadWaybills();
      this.loadParcels();
      this.loadDealers();
      this.loadWarehouses();
      this.loadBordero();
    }
    
    // Update client and brand selection state after initialization
    this.updateClientBrandSelectionState();
  }  private initializeTranslations(): void {
    // Force reload translations for this component
    const currentLang = this.translate.currentLang || 'it';
    this.translate.reloadLang(currentLang).subscribe(() => {
      // console.log('Translations reloaded for shipment-request component');
    });
  }

  ngAfterViewInit(): void {
    // Flatpickr viene inizializzato dal setter di `requestedDateInput`, che
    // scatta anche quando la sezione form entra/esce dall'*ngIf.
  }
  ngOnDestroy(): void {
    // Cleanup destroy subject
    this.destroy$.next();
    this.destroy$.complete();
    
    // Cleanup flatpickr instance
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
    
    // Cleanup confetti timeout
    if (this.confettiTimeout) {
      clearTimeout(this.confettiTimeout);
    }
  }
  private initializeFlatpickr(): void {
    if (this._requestedDateInput && this._requestedDateInput.nativeElement) {
      // Se un'istanza esiste già (re-init), va distrutta prima di ricrearla.
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
        this.flatpickrInstance = null;
      }

      // Check time restrictions
      this.checkTimeRestrictions();
      
      const today = new Date();
      const tomorrow = this.getNextValidDate(today);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 3, 0); // Last day of current month
      
      // Determine minimum date based on time restrictions
      const minDate = this.isTimeRestricted ? tomorrow : today;
      
      // Get the initial value from the form control. Se siamo dopo le 17:00 e
      // il valore corrente è "oggi", forza tomorrow (checkTimeRestrictions lo
      // dovrebbe aver già fatto, ma è una safety net).
      const todayStr = this.formatDateForDisplay(today);
      const currentFormValue = this.shipmentForm.get('waybill.waybillDate')?.value;
      const initialDate = (this.isTimeRestricted && (!currentFormValue || currentFormValue === todayStr))
        ? tomorrow
        : (currentFormValue || tomorrow);

      this.flatpickrInstance = flatpickr(this.requestedDateInput.nativeElement, {
        locale: Italian,
        dateFormat: 'Y-m-d',
        allowInput: false, // Disable manual input to prevent clearing
        clickOpens: true,
        disableMobile: true,
        defaultDate: initialDate, // Set default date
        minDate: minDate, // From today or tomorrow based on time restriction
        maxDate: endOfMonth, // Until end of current month
        disable: [
          // Disable weekends (Saturday = 6, Sunday = 0)
          function (date) {
            return (date.getDay() === 0 || date.getDay() === 6);
          }
        ],        
        onChange: (selectedDates, dateStr) => {
          // Convert YYYY-MM-DD format from flatpickr to dd/MM/yyyy for display
          if (dateStr && selectedDates.length > 0) {
            const displayDate = this.formatDateForDisplay(selectedDates[0]);
            // Scelta manuale dell'utente: la data non è più "auto-avanzata".
            this.autoAdvancedDateStr = null;
            this.shipmentForm.get('waybill.waybillDate')?.setValue(displayDate);
            this.requestedDateInput.nativeElement.value = displayDate;
            // Check if the selected date requires field-level restrictions
            this.checkDateFieldRestriction();
          }
        },
        onClose: (selectedDates, dateStr) => {
          // Ensure the input value is maintained when calendar closes
          if (selectedDates.length > 0) {
            const displayDate = this.formatDateForDisplay(selectedDates[0]);
            this.requestedDateInput.nativeElement.value = displayDate;
          } else {            // If no date is selected, keep the current form value
            const currentValue = this.shipmentForm.get('waybill.waybillDate')?.value;
            if (currentValue) {
              this.flatpickrInstance.setDate(currentValue, false);
              this.requestedDateInput.nativeElement.value = currentValue;
            }
          }
          // Check restrictions after calendar closes
          this.checkDateFieldRestriction();
        },        onReady: () => {
          // Set initial value in the input field
          const currentValue = this.shipmentForm.get('waybill.waybillDate')?.value;
          if (currentValue) {
            this.requestedDateInput.nativeElement.value = currentValue;
          }
        }
      });
      this.setupWaybillDateSync();
    }
  }

  /**
   * Sincronizza il form control `waybill.waybillDate` con flatpickr.
   * Registrata una sola volta: `initializeFlatpickr` può girare più volte
   * (l'input sta dentro un *ngIf) e la subscription non va duplicata.
   */
  private waybillDateSyncInitialized = false;
  private setupWaybillDateSync(): void {
    if (this.waybillDateSyncInitialized) return;
    this.waybillDateSyncInitialized = true;
    this.shipmentForm.get('waybill.waybillDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        // console.trace('[waybillDate] valore cambiato a:', value);
        if (value && this.flatpickrInstance && this._requestedDateInput?.nativeElement) {
          // Convert dd/MM/yyyy format to Date object for flatpickr
          let dateToSet: Date;
          if (typeof value === 'string' && value.includes('/')) {
            const [day, month, year] = value.split('/');
            dateToSet = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } else {
            dateToSet = new Date(value);
          }

          this.flatpickrInstance.setDate(dateToSet, false);
          this._requestedDateInput.nativeElement.value = value;
        }
      });
  }

  private createForm(): FormGroup {
    // Get current date in dd/MM/yyyy format
    const today = new Date();
    const currentDate = this.formatDateForDisplay(today);

    return this.fb.group({
      // Dati della bolla (basati sulla nuova interfaccia Waybill) - TUTTI I CAMPI
      waybill: this.fb.group({
        // Primary identifiers
        waybillCode: [{ value: '', disabled: true }], // Disabled and non-mandatory
        waybillPalletCode: [''],
        waybillShipmentCode: [{ value: '', disabled: true }], // Disabled and non-mandatory
        waybillTrackingNumber: [''],
        waybillOriginLocation: [{ value: '', disabled: true }, [Validators.required]], // Disabled finché non viene selezionato un cliente
        
        // Dates
        waybillDate: [currentDate, [Validators.required, this.noWeekendsValidator.bind(this)]],
        waybillEstimatedDeliveryDate: [''],
        waybillActualDeliveryDate: [''],
        waybillOriginalDeliveryDate: [''],
        waybillCreatedAt: [new Date().toISOString()],
        waybillUpdatedAt: [new Date().toISOString()],
        
        // Delivery information
        waybillDeliveryMethod: [''],
        waybillDeliveryType: [''],
        waybillTransitTimeHours: [null],
        
        // Location information
        waybillDestinationLocation: [''],
        waybillLastKnownLocation: [''],
        waybillLatitude: [null],
        waybillLongitude: [null],
        waybillOriginalLatitude: [null],
        waybillOriginalLongitude: [null],
        
        // Cargo information
        waybillTotalWeight: [null],
        waybillTotalVolume: [null],
        waybillFragile: ['N'],
        waybillHazardousMaterial: ['N'],
        waybillCustomsClearanceRequired: ['N'],
          // Business information
        waybillClientCode: ['', [Validators.required]], // Aggiunto campo client mancante
        waybillBrandCode: ['', [Validators.required]],  // Aggiunta validazione required
        waybillDealerCode: [''],
        waybillCarrierCode: [''],
          // Status and metadata
        waybillStatus: [WaybillStatus.ACTIVE],
        waybillUpdatedBy: [''],
        waybillNote: ['', [Validators.maxLength(200)]],
        waybillFile: [''],
        waybillVersion: [1]
      }),      // Dati anagrafici destinazione (basati sull'interfaccia Dealer)
      destination: this.fb.group({
        dealerCode: ['', [Validators.required]],
        dealerName: ['', [Validators.required]], // Ora rappresenta la Ragione Sociale
        dealerVatNumber: [''], // Non più obbligatorio
        dealerInternalBillingId: [''],
        dealerInternalId: [''],
        dealerDeliveryZoneCode: [''],
        dealerBillingZoneCode: [''],
        dealerIndustry: [''],
        dealerStatus: [DealerStatus.ACTIVE],
        // Contact information
        dealerOperatingPhone: [''],
        dealerOperatingEmail: ['', [Validators.email]],

        // Legal address
        dealerLegalAddressStreet: [''],
        dealerLegalAddressNumber: [''],
        dealerLegalAddressCity: [''],
        dealerLegalAddressZip: [''],
        dealerLegalAddressProvince: [''],
        dealerLegalAddressCountry: [''],

        // Operating address
        dealerOperatingAddressStreet: [''],
        dealerOperatingAddressNumber: [''],
        dealerOperatingAddressCity: [''],
        dealerOperatingAddressZip: [''],
        dealerOperatingAddressProvince: [''],
        dealerOperatingAddressCountry: ['']
      }),

      // Colli (array di colli, inizialmente vuoto)
      parcels: this.fb.array([this.createParcelGroup()])
    });
  }  private createParcelGroup(): FormGroup {
    const group = this.fb.group({
      // Primary identifiers
      parcelCode: [{ value: '', disabled: true }], // Disabled
      parcelWaybillCode: [''],
      parcelPalletCode: [''],      parcelShipmentCode: [''],
      parcelBrandCode: ['DEFAULT'],
      parcelClientCode: [''], // Campo client code aggiunto
      parcelDealerCode: [''],
      parcelBarcode: [''],
      parcelTrackingNumber: [''],
      
      // Dates
      parcelDate: [new Date().toISOString()],
      parcelDeliveryDate: [''],
      parcelEstimatedDeliveryDate: [''],
      parcelOriginalDeliveryDate: [''],
      parcelLastEventDate: [''],
      parcelTakeInChargeDate: [''],
      parcelLastUpdateAt: [''],
      parcelUpdatedAt: [new Date().toISOString()],
      parcelCreatedAt: [new Date().toISOString()],
      
      // Delivery information
      parcelDeliveryStatus: [''],
      parcelDeliveryMethod: [''],
      parcelDeliveryType: [''],
      parcelDeliveryMaterial: [''],
      parcelDeliveryReason: [''],
      parcelTakeInChargeReason: [''],
      
      // Physical properties
      parcelDescription: ['', [Validators.maxLength(40)]],
      parcelWeight: [null, [this.weightVolumeValidator.bind(this)]],
      parcelVolume: [null, [this.volumeValidator.bind(this)]],
      parcelLength: [null, [this.dimensionValidator.bind(this)]],
      parcelWidth: [null, [this.dimensionValidator.bind(this)]],
      parcelHeight: [null, [this.dimensionValidator.bind(this)]],
      parcelType: [''],
      
      // Location information
      parcelLocation: [''],
      parcelLatitude: [null],
      parcelLongitude: [null],
      parcelOriginalLatitude: [null],
      parcelOriginalLongitude: [null],
      
      // Special handling flags (Y/N strings from database)
      parcelFragile: ['N'],
      parcelHazardousMaterial: ['N'],
      parcelTemperatureSensitive: ['N'],
      parcelHighValueItem: ['N'],
      parcelRestrictedItem: ['N'],
      parcelCustomsClearanceRequired: ['N'],
      parcelDeliveryDateIsForced: ['N'],
      parcelDeliveryDateIsUpdated: ['N'],
      parcelDeliveryCoordinatesIsForced: ['N'],
      parcelDeliveryCoordinatesIsUpdated: ['N'],
      
      // Financial information
      parcelInsuredValue: [null, [this.insuredValueValidator.bind(this)]],
      
      // Status and metadata
      parcelStatus: [ParcelStatus.ACTIVE],
      parcelDetailStatus: [ParcelStatus.PREPARED],
      parcelVersion: [1],
      parcelLastUpdateBy: [''],
      parcelUpdatedBy: ['']
    });

    // Applica eventuale settaggio campi (clientFieldSettings) al collo
    // appena creato. Per default tutti i campi sono abilitati.
    this.applyFieldSettingsToFormGroup(group);
    return group;
  }  private createDealerForm(): FormGroup {
    return this.fb.group({      // Main dealer data
      dealerCode: [{ value: '', disabled: true }], // Disabled and not required
      dealerClientCode: [{ value: '', disabled: true }], // Disabled and not required
      dealerBrandCode: [{ value: '', disabled: true }], // Disabled and not required
      code: ['', [Validators.required]],
      name: ['', [Validators.required]],
      vatNumber: [''],
      internalBillingId: [''],
      dealerInternalId: [''],
      sdiCode: [''],
      industry: [''],
      status: [DealerStatus.ACTIVE],
      notes: [''],
        // Legal address as nested FormGroup
      legalAddress: this.fb.group({
        street: ['', [Validators.required]],
        number: [''], // Numero non obbligatorio
        zip: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        country: ['IT', [Validators.required]] // Default to Italy and required
      }),
      
      // Operating address as nested FormGroup
      operatingAddress: this.fb.group({
        street: ['', [Validators.required]],
        number: [''], // Numero non obbligatorio
        zip: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        country: ['IT', [Validators.required]] // Default to Italy and required
      }),
      
      // Coordinates
      latitude: [null],
      longitude: [null]
    });
  }

  get parcelsArray(): FormArray {
    return this.shipmentForm.get('parcels') as FormArray;
  }

  // Getter per waybills filtrate (sostituisce getFilteredWaybills)
  get filteredWaybills(): Waybill[] {
    if (this.needsRecalculation()) {
      this._filteredWaybills = this.calculateFilteredWaybills();
      this.updateCalculationState();
    }
    return this._filteredWaybills;
  }

  // Getter per parcels filtrati (sostituisce getFilteredParcels)
  get filteredParcels(): any[] {
    if (this.needsRecalculation()) {
      this._filteredParcels = this.calculateFilteredParcels();
      this.updateCalculationState();
    }
    return this._filteredParcels;
  }

  // Getter per waybills paginati
  get paginatedWaybills(): Waybill[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredWaybills.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // Getter per parcels paginati
  get paginatedParcels(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredParcels.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // Metodi utility per performance
  clearSearch(): void {
    this.searchControl.setValue('');
  }

  addParcel(): void {
    // Check if maximum limit of 99 parcels is reached
    if (this.parcelsArray.length >= 99) {
      this.showErrorMessage(this.translate.instant('SHIPMENT_REQUEST.MAX_PARCELS_REACHED'));
      return;
    }
    
    const newParcelGroup = this.createParcelGroup();
    
    // Automatically set the parcelBrandCode to the waybillBrandCode from "Dati della Bolla" section
    const waybillBrandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
    if (waybillBrandCode) {
      newParcelGroup.get('parcelBrandCode')?.setValue(waybillBrandCode);
    }
    
    // Automatically set the parcelClientCode to the waybillClientCode from "Dati della Bolla" section
    const waybillClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
    if (waybillClientCode) {
      newParcelGroup.get('parcelClientCode')?.setValue(waybillClientCode);
    }
    
    this.parcelsArray.push(newParcelGroup);
    
    // Aggiungi lo stato di collasso per il nuovo collo (espanso inizialmente)
    this.parcelsCollapsed.push(false);
    
    // Chiudi tutti i colli tranne l'ultimo aggiunto
    const newParcelIndex = this.parcelsArray.length - 1;
    this.collapseAllParcelsExcept(newParcelIndex);
  }

  /**
   * Check if maximum number of parcels (99) is reached
   */
  isMaxParcelsReached(): boolean {
    return this.parcelsArray.length >= 99;
  }

  /**
   * Check if approaching maximum number of parcels (95 or more)
   */
  isApproachingMaxParcels(): boolean {
    return this.parcelsArray.length >= 95 && this.parcelsArray.length < 99;
  }

  /**
   * Open distributive insert popup
   */
  openDistributiveInsertPopup(): void {
    this.distributiveForm = this.createDistributiveForm();
    this.showDistributiveInsertPopup = true;
  }

  /**
   * Close distributive insert popup
   */
  closeDistributiveInsertPopup(): void {
    this.showDistributiveInsertPopup = false;
    this.distributiveForm = null;
  }

  /**
   * Create form for distributive parcel insertion.
   * Per default tutti i campi sono ABILITATI: il settaggio
   * `currentFieldSettings` può disattivarne alcuni (es. peso/volume totale
   * calcolati dal sistema). Vedi {@link applyFieldSettingsToFormGroup}.
   */
  private createDistributiveForm(): FormGroup {
    // Di default totalWeight e totalVolume sono DISABILITATI, salvo override da config
    const form = this.fb.group({
      parcelCount: [1, [Validators.required, Validators.min(1), Validators.max(99)]],
      totalWeight: [{ value: 0, disabled: true }],
      totalVolume: [{ value: 0, disabled: true }]
    });
    this.applyFieldSettingsToFormGroup(form);
    return form;
  }

  // ============================================================
  // Helper per il settaggio campi (clientFieldSettings)
  // ============================================================

  /**
   * Carica il settaggio campi dal client selezionato e lo applica a form
   * dei colli e (se aperta) della popup distributiva. Tutti i campi sono
   * abilitati per default; il settaggio serve solo a disabilitare quelli
   * non attivi.
   */
  private loadFieldSettingsFromClient(clientCode: string | null | undefined): void {
    // console.log('[ShipmentRequest] loadFieldSettingsFromClient called', { clientCode });

    if (!clientCode) {
      this.currentFieldSettings = null;
      // console.log('[ShipmentRequest] No client selected, resetting field settings to default (all enabled)');
      this.applyAllFieldSettings();
      this.checkTimeRestrictions();
      return;
    }

    // Fallback immediato locale (userFunctions/login) per non lasciare il form
    // in attesa della risposta backend.
    this.currentFieldSettings = this.findLocalFieldSettings(clientCode);
    // console.log('[ShipmentRequest] Local field settings fallback applied', {
    //   hasClientsBrandsSettings: !!cb?.clientFieldSettings,
    //   hasClientsSettings: !!fromClients?.clientFieldSettings,
    //   hasResolvedSettings: !!this.currentFieldSettings,
    //   fieldsCount: Object.keys(this.currentFieldSettings?.fields || {}).length
    // });

    this.applyAllFieldSettings();
    this.checkTimeRestrictions();

    // Chiamata separata backend per configurazioni client.
    this.shipmentRequestService
      .getClientFieldSettings(clientCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((remoteSettings) => {
        // Evita race condition: applica solo se il client attuale è ancora lo stesso.
        const selectedClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
        if (selectedClientCode !== clientCode) {
          // console.log('[ShipmentRequest] Ignoring stale remote settings response', {
          //   requestedClientCode: clientCode,
          //   selectedClientCode
          // });
          return;
        }

        this.currentFieldSettings = remoteSettings;
        // console.log('[ShipmentRequest] Remote field settings applied', {
        //   clientCode,
        //   hasRemoteSettings: !!remoteSettings,
        //   fieldsCount: Object.keys(remoteSettings?.fields || {}).length
        // });
        const dateBeforeRemote = this.shipmentForm.get('waybill.waybillDate')?.value;
        // Se la data era stata avanzata automaticamente dalla restrizione
        // oraria, non va ripristinata: checkTimeRestrictions (con il
        // max_date_insert remoto) potrebbe averla appena corretta a oggi.
        const dateWasAutoAdvanced = !!dateBeforeRemote && dateBeforeRemote === this.autoAdvancedDateStr;
        this.applyAllFieldSettings();
        this.checkTimeRestrictions();
        // Specularmente: se checkTimeRestrictions ha APPENA avanzato la data
        // (orario limite del client già superato), il ripristino non deve
        // riportarla indietro a oggi.
        const dateNow = this.shipmentForm.get('waybill.waybillDate')?.value;
        const dateJustAutoAdvanced = !!dateNow && dateNow === this.autoAdvancedDateStr;
        if (dateBeforeRemote && !dateWasAutoAdvanced && !dateJustAutoAdvanced) {
          this.shipmentForm.get('waybill.waybillDate')?.setValue(dateBeforeRemote, { emitEvent: false });
          if (this.flatpickrInstance) {
            this.flatpickrInstance.setDate(dateBeforeRemote, false);
            if (this.requestedDateInput?.nativeElement) {
              this.requestedDateInput.nativeElement.value = dateBeforeRemote;
            }
          }
        }
      });
  }

  /**
   * Ritorna true se il campo è abilitato. Se non c'è settaggio per quel
   * campo, è considerato abilitato (default).
   */
  isFieldEnabled(fieldName: string): boolean {
    const cfg = this.currentFieldSettings?.fields?.[fieldName];
    if (!cfg) return true;
    return cfg.enabled !== false;
  }

  /**
   * Ritorna il placeholder configurato per il campo (se presente nel
   * settaggio), indipendentemente dallo stato `enabled`. Se non c'è
   * placeholder configurato, ritorna il fallback originale del campo.
   */
  getFieldPlaceholder(fieldName: string, fallback: string): string {
    const cfg = this.currentFieldSettings?.fields?.[fieldName];
    if (cfg?.placeholder) {
      return cfg.placeholder;
    }
    return fallback;
  }

  /**
   * Ritorna true se il campo è marcato come obbligatorio nel settaggio
   * corrente. Usato dal template per mostrare l'asterisco dinamico
   * accanto alle label dei campi parametrizzati.
   */
  isFieldRequired(fieldName: string): boolean {
    const cfg = this.currentFieldSettings?.fields?.[fieldName];
    return cfg?.required === true;
  }

  // ------------------------------------------------------------
  // Applicazione generica del settaggio ai FormControl
  // ------------------------------------------------------------

  /**
   * Mappa alias: una chiave del settaggio (`parcelDimensions`) governa più
   * FormControl (`parcelLength` / `parcelWidth` / `parcelHeight`).
   * Aggiungere qui altri alias se servono in futuro.
   */
  private static readonly FIELD_KEY_ALIASES: Record<string, string[]> = {
    parcelDimensions: ['parcelLength', 'parcelWidth', 'parcelHeight']
  };

  /**
   * Tiene traccia delle chiavi gestite nell'ultima applicazione del
   * settaggio. Serve per ripristinare correttamente i controlli che erano
   * stati toccati da una config precedente ma non sono più presenti
   * nella config corrente.
   */
  private lastManagedFieldKeys = new Set<string>();

  /**
   * Snapshot dello stato `disabled` originale di ciascun FormControl al
   * momento della prima applicazione del settaggio. Usato per ripristinare
   * lo stato di partenza (così come definito in `createForm()`) quando si
   * cambia client o si rimuove la chiave dalla config.
   */
  private originalDisabledState = new WeakMap<AbstractControl, boolean>();

  /**
   * Snapshot dei validator originali (quelli definiti in `createForm()`/
   * `createParcelGroup()`) per ciascun FormControl gestito dal settaggio.
   * Serve a poter aggiungere/togliere `Validators.required` in base al
   * `required` del settaggio senza perdere gli altri validatori esistenti.
   */
  private originalValidators = new WeakMap<AbstractControl, ValidatorFn | null>();

  /**
   * Restituisce la chiave del settaggio che governa un dato `controlName`,
   * cercando prima nella config corrente, poi nelle chiavi gestite in
   * precedenza, e infine attraverso la mappa alias.
   */
  private resolveSettingKeyForControl(controlName: string): string | null {
    const currentKeys = this.currentFieldSettings?.fields || {};

    // 1) Match diretto sul nome del controllo
    if (controlName in currentKeys) return controlName;
    if (this.lastManagedFieldKeys.has(controlName)) return controlName;

    // 2) Match via alias (es. parcelLength -> parcelDimensions)
    for (const [aliasKey, members] of Object.entries(ShipmentRequestComponent.FIELD_KEY_ALIASES)) {
      if (members.includes(controlName)) {
        if (aliasKey in currentKeys || this.lastManagedFieldKeys.has(aliasKey)) {
          return aliasKey;
        }
      }
    }

    return null;
  }

  /**
   * Abilita/disabilita un singolo FormControl in base al settaggio.
   * - Se la chiave è presente nella config corrente, applica `enabled`.
   * - Se la chiave non è più nella config ma lo era in precedenza,
   *   ripristina lo stato originale (quello definito in `createForm()`).
   * - Se non è mai stato gestito, lo lascia invariato.
   */
  private applyFieldSettingToControl(ctrl: AbstractControl, controlName: string): void {
    // Il campo `dealerCode` della sezione "Dati Dealer" del form bolle NON
    // deve essere governato dalla config client: il parametro `dealerCode`
    // agisce solo nella popup di inserimento/modifica distributivo
    // (gestita da DealerManagementPopupComponent tramite @Input fieldSettings).
    if (controlName === 'dealerCode') return;

    const key = this.resolveSettingKeyForControl(controlName);
    if (!key) return;

    // Memorizza lo stato originale alla prima applicazione (lazy snapshot).
    if (!this.originalDisabledState.has(ctrl)) {
      this.originalDisabledState.set(ctrl, ctrl.disabled);
    }
    if (!this.originalValidators.has(ctrl)) {
      this.originalValidators.set(ctrl, ctrl.validator ?? null);
    }

    const cfg = this.currentFieldSettings?.fields?.[key];
    const originalValidator = this.originalValidators.get(ctrl) ?? null;

    if (cfg) {
      // Chiave presente nella config corrente: rispetta `enabled`.
      if (cfg.enabled !== false) {
        if (ctrl.disabled) ctrl.enable({ emitEvent: false });
      } else {
        if (controlName !== 'waybillDate') {
          ctrl.setValue(null, { emitEvent: false });
        }
        if (ctrl.enabled) ctrl.disable({ emitEvent: false });
      }

      // Applica `required` dinamico sopra i validator originali.
      if (cfg.required === true) {
        const validators: ValidatorFn[] = originalValidator
          ? [originalValidator, Validators.required]
          : [Validators.required];
        ctrl.setValidators(validators);
      } else {
        ctrl.setValidators(originalValidator);
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    } else {
      // Chiave non più presente: ripristina lo stato originale del form.
      const originallyDisabled = this.originalDisabledState.get(ctrl) === true;
      if (originallyDisabled) {
        if (ctrl.enabled) ctrl.disable({ emitEvent: false });
      } else {
        if (ctrl.disabled) ctrl.enable({ emitEvent: false });
      }
      ctrl.setValidators(originalValidator);
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Walker generico: attraversa ricorsivamente un FormGroup/FormArray e
   * applica il settaggio a ogni leaf FormControl che corrisponde a una
   * chiave del settaggio (direttamente o tramite alias).
   */
  private applyFieldSettingsToFormGroup(group: AbstractControl): void {
    if (group instanceof FormGroup) {
      Object.keys(group.controls).forEach(name => {
        const child = group.get(name);
        if (!child) return;
        if (child instanceof FormGroup || child instanceof FormArray) {
          this.applyFieldSettingsToFormGroup(child);
        } else {
          this.applyFieldSettingToControl(child, name);
        }
      });
    } else if (group instanceof FormArray) {
      group.controls.forEach(child => {
        if (child instanceof FormGroup || child instanceof FormArray) {
          this.applyFieldSettingsToFormGroup(child);
        }
      });
    }
  }

  /**
   * Applica il settaggio corrente a tutti i form gestiti dal componente:
   * shipmentForm (waybill + destination + parcels) e popup distributiva.
   * Aggiorna anche `lastManagedFieldKeys` per la prossima invocazione.
   */
  private applyAllFieldSettings(): void {
    if (this.shipmentForm) {
      this.applyFieldSettingsToFormGroup(this.shipmentForm);
    }
    if (this.distributiveForm) {
      this.applyFieldSettingsToFormGroup(this.distributiveForm);
    }
    this.lastManagedFieldKeys = new Set(Object.keys(this.currentFieldSettings?.fields || {}));
  }

  /**
   * Process distributive parcel insertion
   */
  processDistributiveInsertion(): void {
    if (!this.distributiveForm || this.distributiveForm.get('parcelCount')?.invalid) {
      this.markFormGroupTouched(this.distributiveForm || this.fb.group({}));
      return;
    }

    // Usa getRawValue per includere anche i campi disabilitati dal settaggio.
    const formValue = this.distributiveForm.getRawValue();
    const parcelCount = formValue.parcelCount;

    // Check if adding these parcels would exceed the maximum limit
    if (this.parcelsArray.length + parcelCount > 99) {
      this.showErrorMessage(`Non è possibile aggiungere ${parcelCount} colli. Limite massimo: 99 colli totali.`);
      return;
    }

    // Clear existing parcels (keep only the first one if it exists, or create a new one)
    while (this.parcelsArray.length > 0) {
      this.parcelsArray.removeAt(0);
    }
    this.parcelsCollapsed = [];

    // Distribuzione di peso e volume totali sui singoli colli.
    // - Se `totalWeight` / `totalVolume` non sono attivi nel settaggio, si
    //   considera 0 e i singoli pesi/volumi rimangono null (verranno
    //   calcolati lato sistema).
    // - Se i singoli `parcelWeight` / `parcelVolume` sono disattivati dal
    //   settaggio, non vengono valorizzati (il FormControl resta disabled).
    const totalWeight = this.isFieldEnabled('totalWeight') ? Number(formValue.totalWeight) || 0 : 0;
    const totalVolume = this.isFieldEnabled('totalVolume') ? Number(formValue.totalVolume) || 0 : 0;
    const weightDistribution = totalWeight > 0
      ? this.distributeValue(totalWeight, parcelCount, 3)
      : new Array(parcelCount).fill(null);
    const volumeDistribution = totalVolume > 0
      ? this.distributeValue(totalVolume, parcelCount, 4)
      : new Array(parcelCount).fill(null);

    for (let i = 0; i < parcelCount; i++) {
      const newParcelGroup = this.createParcelGroup();

      // Imposta peso/volume solo se il singolo campo è abilitato dal settaggio.
      if (this.isFieldEnabled('parcelWeight')) {
        newParcelGroup.get('parcelWeight')?.setValue(weightDistribution[i]);
      }
      if (this.isFieldEnabled('parcelVolume')) {
        newParcelGroup.get('parcelVolume')?.setValue(volumeDistribution[i]);
      }
      
      // Automatically set the parcelBrandCode and parcelClientCode from waybill
      const waybillBrandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
      const waybillClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
      
      if (waybillBrandCode) {
        newParcelGroup.get('parcelBrandCode')?.setValue(waybillBrandCode);
      }
      if (waybillClientCode) {
        newParcelGroup.get('parcelClientCode')?.setValue(waybillClientCode);
      }
      
      this.parcelsArray.push(newParcelGroup);
      
      // Add collapsed state for each parcel (all collapsed initially)
      this.parcelsCollapsed.push(true);
    }

    // Close the popup
    this.closeDistributiveInsertPopup();
    
    // Show success message
    this.showSuccessMessage(`Creati ${parcelCount} colli.`);
  }

  /**
   * Distribute a total value among a number of parcels as evenly as possible
   * while respecting decimal place limits
   */
  private distributeValue(totalValue: number, parcelCount: number, maxDecimals: number): number[] {
    const values: number[] = [];
    const baseValue = totalValue / parcelCount;
    
    // Calculate base value for each parcel with proper rounding
    const multiplier = Math.pow(10, maxDecimals);
    const roundedBaseValue = Math.floor(baseValue * multiplier) / multiplier;
    
    // Fill array with base values
    for (let i = 0; i < parcelCount; i++) {
      values.push(roundedBaseValue);
    }
    
    // Calculate remaining value to distribute
    const distributedTotal = roundedBaseValue * parcelCount;
    const remainder = totalValue - distributedTotal;
    
    if (remainder > 0) {
      // Distribute remainder as evenly as possible
      const remainderPerParcel = remainder / parcelCount;
      const smallestUnit = 1 / multiplier; // e.g., 0.001 for 3 decimals, 0.0001 for 4 decimals
      
      for (let i = 0; i < parcelCount; i++) {
        const additionalValue = Math.round(remainderPerParcel * multiplier) / multiplier;
        if (additionalValue >= smallestUnit) {
          values[i] = Math.round((values[i] + additionalValue) * multiplier) / multiplier;
        }
      }
      
      // Final adjustment: if there's still a small remainder, add it to the first parcels
      const finalTotal = values.reduce((sum, val) => sum + val, 0);
      const finalRemainder = totalValue - finalTotal;
      
      if (Math.abs(finalRemainder) >= smallestUnit) {
        const adjustmentPerParcel = Math.round((finalRemainder / parcelCount) * multiplier) / multiplier;
        for (let i = 0; i < parcelCount && Math.abs(finalRemainder) >= smallestUnit; i++) {
          values[i] = Math.round((values[i] + adjustmentPerParcel) * multiplier) / multiplier;
        }
      }
    }
    
    return values;
  }

  /**
   * Easter egg: Check for magic words in third parcel description
   */
  onParcelDescriptionChange(event: any, parcelIndex: number): void {
    // Only check for the third parcel (index 2)
    if (parcelIndex === 2) {
      const description = event.target.value;
      if (description === 'abracadabra') {
        this.triggerConfettiEasterEgg();
      }
    }
  }

  /**
   * Trigger confetti animation easter egg
   */
  private triggerConfettiEasterEgg(): void {
    // console.log('✨ Easter egg activated! Sparkling stars magic! 🌟');
    
    this.showConfetti = true;
    
    // Clear any existing timeout
    if (this.confettiTimeout) {
      clearTimeout(this.confettiTimeout);
    }
    
    // Stop sparkling stars after 10 seconds
    this.confettiTimeout = setTimeout(() => {
      this.showConfetti = false;
    }, 10000);
  }

  removeParcel(index: number): void {
    if (this.parcelsArray.length > 1) {
      this.parcelsArray.removeAt(index);
      // Rimuovi anche lo stato di collasso corrispondente
      this.parcelsCollapsed.splice(index, 1);
    }
  }

  /**
   * Reset all parcels to a single empty parcel
   */
  resetParcels(): void {
    const title = this.translate.instant('SHIPMENT_REQUEST.RESET_PARCELS_CONFIRM_TITLE');
    const message = this.translate.instant('SHIPMENT_REQUEST.RESET_PARCELS_CONFIRM_MESSAGE');
    
    this.showConfirmation(title, message, () => {
      // Clear all parcels
      while (this.parcelsArray.length > 0) {
        this.parcelsArray.removeAt(0);
      }
      this.parcelsCollapsed = [];
      
      // Add a single empty parcel
      const newParcelGroup = this.createParcelGroup();
      
      // Automatically set the parcelBrandCode and parcelClientCode from waybill
      const waybillBrandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
      const waybillClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
      
      if (waybillBrandCode) {
        newParcelGroup.get('parcelBrandCode')?.setValue(waybillBrandCode);
      }
      if (waybillClientCode) {
        newParcelGroup.get('parcelClientCode')?.setValue(waybillClientCode);
      }
      
      this.parcelsArray.push(newParcelGroup);
      
      // Add collapsed state for the new parcel (expanded initially)
      this.parcelsCollapsed.push(false);
      
      // Show success message
      this.showSuccessMessage(this.translate.instant('SHIPMENT_REQUEST.RESET_PARCELS_SUCCESS'));
    });
  }

  /**
   * Toggle collapse state for a specific parcel
   */
  toggleParcelCollapse(index: number): void {
    this.parcelsCollapsed[index] = !this.parcelsCollapsed[index];
  }

  /**
   * Check if a parcel is collapsed
   */
  isParcelCollapsed(index: number): boolean {
    return this.parcelsCollapsed[index] || false;
  }

  /**
   * Collapse all parcels except the specified one
   */
  collapseAllParcelsExcept(exceptIndex: number): void {
    for (let i = 0; i < this.parcelsCollapsed.length; i++) {
      this.parcelsCollapsed[i] = i !== exceptIndex;
    }
  }

  /**
   * Open a specific parcel and close all others
   */
  openParcelCloseOthers(index: number): void {
    // Apri il collo specificato e chiudi tutti gli altri
    for (let i = 0; i < this.parcelsCollapsed.length; i++) {
      this.parcelsCollapsed[i] = i !== index;
    }
  }    
  
  /**
   * Esegue il trim (rimozione spazi iniziali/finali) di tutti i valori stringa
   * contenuti nel form, in modo ricorsivo su FormGroup e FormArray annidati.
   * I valori non stringa (numeri, null, ecc.) vengono lasciati invariati.
   * Aggiorna direttamente i FormControl così che sia i valori visualizzati che
   * il payload inviato al backend risultino "trimmati".
   */
  private trimFormControls(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach(child => this.trimFormControls(child));
    } else if (control instanceof FormControl) {
      const value = control.value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== value) {
          control.setValue(trimmed, { emitEvent: false });
        }
      }
    }
  }

  onSubmit(): void {
    // Esegui il trim di tutti i campi stringa del form prima di validare/inviare,
    // sia in inserimento che in modifica.
    this.trimFormControls(this.shipmentForm);

    if (this.shipmentForm.valid) {
      // Usa getRawValue() per includere anche i campi disabilitati come waybillCode e parcelCode
      const formValue = this.shipmentForm.getRawValue();

      // Controlli di validazione applicati a TUTTE le operazioni
      
      // 1. Controllo dealer code obbligatorio
      const dealerCode = formValue.destination?.dealerCode;
      if (!dealerCode) {
        this.showErrorMessage('Codice dealer obbligatorio');
        return;
      }

      // 2. Controllo dei valori assicurati prima dell'invio per TUTTE le operazioni
      if (this.hasHighInsuredValueParcels(formValue.parcels)) {
        this.showHighInsuredValueConfirmation(formValue);
        return;
      }

      // 3. Controllo date weekend se applicabile
      if (this.isWeekendRestricted && formValue.waybill?.waybillDate) {
        if (this.isDateWeekend(formValue.waybill.waybillDate)) {
          this.showWarningMessage('Attenzione: La data selezionata è un weekend');
        }
      }

      // 4. Controllo peso e volume minimi e validazione decimali
      if (formValue.parcels && formValue.parcels.length > 0) {
        const parcelValidationErrors = this.validateParcelsDecimals(formValue.parcels);
        if (parcelValidationErrors.length > 0) {
          this.showErrorMessage(`Errori di validazione: ${parcelValidationErrors.join(', ')}`);
          return;
        }
      }

      this.processSubmit(formValue);
    } else {
      this.markFormGroupTouched(this.shipmentForm);
      this.showErrorMessage('Compila tutti i campi obbligatori');
    }
  }

  private processSubmit(formValue: any): void {
    this.isSubmitting = true;

    if (this.editingParcelId) {
      // Modalità aggiornamento singolo collo
      this.updateParcel(formValue);
    } else if (this.editingWaybillId) {
      // Modalità aggiornamento bolla completa
      this.updateShipment(formValue);
    } else {
      // Modalità creazione
      this.createShipment(formValue);
    }
  }

  private hasHighInsuredValueParcels(parcels: any[]): boolean {
    return parcels.some(parcel => 
      parcel.parcelInsuredValue && 
      parseFloat(parcel.parcelInsuredValue) > 1000
    );
  }

  private getHighInsuredValueParcelsCount(parcels: any[]): number {
    return parcels.filter(parcel => 
      parcel.parcelInsuredValue && 
      parseFloat(parcel.parcelInsuredValue) > 1000
    ).length;
  }

  private showHighInsuredValueConfirmation(formValue: any): void {
    const highValueParcelsCount = this.getHighInsuredValueParcelsCount(formValue.parcels);
    
    // Determina il tipo di operazione per personalizzare il messaggio
    let operationType = 'creazione spedizione';
    if (this.editingParcelId) {
      operationType = 'aggiornamento collo';
    } else if (this.editingWaybillId) {
      operationType = 'aggiornamento spedizione';
    }
    
    const title = this.translate.instant('SHIPMENT_REQUEST.HIGH_INSURED_VALUE_TITLE');
    const message = this.translate.instant('SHIPMENT_REQUEST.HIGH_INSURED_VALUE_MESSAGE');
    const operationText = `Stai procedendo con ${operationType} che include colli con valore assicurato elevato.`;
    const details = this.translate.instant('SHIPMENT_REQUEST.HIGH_INSURED_VALUE_DETAILS', { count: highValueParcelsCount });
    
    const fullMessage = `${operationText}<br><br>${message}<br><br><strong>${details}</strong>`;
    
    this.showConfirmation(title, fullMessage, () => this.processSubmit(formValue));
  }  
  
  private createShipment(formValue: any): void {
    // Prendi il codice dealer dal form
    const dealerCode = formValue.destination.dealerCode;
    
    // Crea la waybill completa con tutti i campi dell'interfaccia
    const waybillData: Waybill = {
      // Spread dei dati dal form
      ...formValue.waybill,
      
      // Convert date to ISO format for server
      waybillDate: this.convertDateToISO(formValue.waybill.waybillDate),
      
      // Campi obbligatori e quelli calcolati
      waybillDealerCode: dealerCode,
      
      // Campi con valori di default se non presenti
      waybillId: undefined, // Sarà generato dal server
      waybillPalletCode: formValue.waybill.waybillPalletCode || null,
      waybillTrackingNumber: formValue.waybill.waybillTrackingNumber || null,
      waybillActualDeliveryDate: formValue.waybill.waybillActualDeliveryDate || null,
      waybillCreatedAt: formValue.waybill.waybillCreatedAt || new Date().toISOString(),
      waybillUpdatedAt: formValue.waybill.waybillUpdatedAt || new Date().toISOString(),
      waybillDeliveryMethod: formValue.waybill.waybillDeliveryMethod || null,
      waybillDeliveryType: formValue.waybill.waybillDeliveryType || null,
      waybillTransitTimeHours: formValue.waybill.waybillTransitTimeHours || null,
      waybillOriginLocation: formValue.waybill.waybillOriginLocation || null, // Warehouse code (origin location)
      waybillDestinationLocation: formValue.waybill.waybillDestinationLocation || null,
      waybillLastKnownLocation: formValue.waybill.waybillLastKnownLocation || null,
      waybillLatitude: formValue.waybill.waybillLatitude || null,
      waybillLongitude: formValue.waybill.waybillLongitude || null,
      waybillOriginalLatitude: formValue.waybill.waybillOriginalLatitude || null,
      waybillOriginalLongitude: formValue.waybill.waybillOriginalLongitude || null,
      waybillTotalWeight: formValue.waybill.waybillTotalWeight || null,
      waybillTotalVolume: formValue.waybill.waybillTotalVolume || null,
      waybillFragile: formValue.waybill.waybillFragile || 'N',
      waybillHazardousMaterial: formValue.waybill.waybillHazardousMaterial || 'N',
      waybillCustomsClearanceRequired: formValue.waybill.waybillCustomsClearanceRequired || 'N',
      waybillBrandCode: formValue.waybill.waybillBrandCode || null,
      waybillCarrierCode: formValue.waybill.waybillCarrierCode || null,
      waybillStatus: formValue.waybill.waybillStatus || WaybillStatus.ACTIVE,
      waybillUpdatedBy: formValue.waybill.waybillUpdatedBy || null,
      waybillNote: formValue.waybill.waybillNote || null,
      waybillFile: formValue.waybill.waybillFile || null,
      waybillVersion: formValue.waybill.waybillVersion || 1
    };

    // Crea i colli completi con tutti i campi dell'interfaccia
    const parcelsData: Parcel[] = formValue.parcels.map((parcel: any) => ({
      // Spread dei dati dal form
      ...parcel,
      
      // Campi obbligatori e quelli calcolati
      parcelDealerCode: dealerCode,
      
      // Campi con valori di default se non presenti
      parcelId: undefined, // Sarà generato dal server
      parcelWaybillCode: parcel.parcelWaybillCode || null,
      parcelPalletCode: parcel.parcelPalletCode || null,      
      parcelShipmentCode: parcel.parcelShipmentCode || null,
      parcelBrandCode: waybillData.waybillBrandCode || 'DEFAULT',
      parcelClientCode: waybillData.waybillClientCode || null,
      parcelBarcode: parcel.parcelCode || parcel.parcelBarcode || null,
      parcelTrackingNumber: parcel.parcelTrackingNumber || null,
      parcelDate: parcel.parcelDate || new Date().toISOString(),
      parcelDeliveryDate: parcel.parcelDeliveryDate || null,
      parcelEstimatedDeliveryDate: parcel.parcelEstimatedDeliveryDate || null,
      parcelOriginalDeliveryDate: parcel.parcelOriginalDeliveryDate || null,
      parcelLastEventDate: parcel.parcelLastEventDate || null,
      parcelTakeInChargeDate: parcel.parcelTakeInChargeDate || null,
      parcelLastUpdateAt: parcel.parcelLastUpdateAt || null,
      parcelUpdatedAt: parcel.parcelUpdatedAt || new Date().toISOString(),
      parcelCreatedAt: parcel.parcelCreatedAt || new Date().toISOString(),
      parcelDeliveryStatus: parcel.parcelDeliveryStatus || null,
      parcelDeliveryMethod: parcel.parcelDeliveryMethod || null,
      parcelDeliveryType: parcel.parcelDeliveryType || null,
      parcelDeliveryMaterial: parcel.parcelDeliveryMaterial || null,
      parcelDeliveryReason: parcel.parcelDeliveryReason || null,
      parcelTakeInChargeReason: parcel.parcelTakeInChargeReason || null,
      parcelDescription: parcel.parcelDescription || null,
      parcelWeight: parcel.parcelWeight || null,
      parcelVolume: parcel.parcelVolume || null,
      parcelLength: parcel.parcelLength || null,
      parcelWidth: parcel.parcelWidth || null,
      parcelHeight: parcel.parcelHeight || null,
      parcelType: parcel.parcelType || null,
      parcelLocation: parcel.parcelLocation || null,
      parcelLatitude: parcel.parcelLatitude || null,
      parcelLongitude: parcel.parcelLongitude || null,
      parcelOriginalLatitude: parcel.parcelOriginalLatitude || null,
      parcelOriginalLongitude: parcel.parcelOriginalLongitude || null,
      parcelFragile: parcel.parcelFragile || 'N',
      parcelHazardousMaterial: parcel.parcelHazardousMaterial || 'N',
      parcelTemperatureSensitive: parcel.parcelTemperatureSensitive || 'N',
      parcelHighValueItem: parcel.parcelHighValueItem || 'N',
      parcelRestrictedItem: parcel.parcelRestrictedItem || 'N',
      parcelCustomsClearanceRequired: parcel.parcelCustomsClearanceRequired || 'N',
      parcelDeliveryDateIsForced: parcel.parcelDeliveryDateIsForced || 'N',
      parcelDeliveryDateIsUpdated: parcel.parcelDeliveryDateIsUpdated || 'N',
      parcelDeliveryCoordinatesIsForced: parcel.parcelDeliveryCoordinatesIsForced || 'N',
      parcelDeliveryCoordinatesIsUpdated: parcel.parcelDeliveryCoordinatesIsUpdated || 'N',
      parcelInsuredValue: parcel.parcelInsuredValue || null,
      parcelStatus: parcel.parcelStatus || ParcelStatus.ACTIVE,
      parcelVersion: parcel.parcelVersion || 1,
      parcelLastUpdateBy: parcel.parcelLastUpdateBy || null,
      parcelUpdatedBy: parcel.parcelUpdatedBy || null,
      parcelDetailStatus: parcel.parcelDetailStatus || ParcelStatus.PREPARED,
    }));



    // Crea l'oggetto Shipment composto
    const shipment: Shipment = {
      waybill: waybillData,
      parcels: parcelsData
    };

    // console.log('Shipment data:', shipment);

    this.shipmentRequestService.createShipment(shipment)
      .pipe(
        catchError(error => {
          const msg = error?.message || 'Errore nell\'invio dei dati al server';
          this.showErrorMessage(msg);
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe((response: Shipment | null) => {
        if (response) {
          this.resetForm();
          this.showSuccessMessage('Richiesta di spedizione creata con successo');
          this.refreshTables();
        }
      });
  }    
    private updateShipment(formValue: any): void {
  const waybillId = this.editingWaybillId!;
  const waybill = this.waybills.find(w => w.waybillId === waybillId);
  if (!waybill) return;

  // Prendi il codice dealer dal form o dalla waybill esistente
  const dealerCode = formValue.destination?.dealerCode || waybill.waybillDealerCode;
  
  // Crea la waybill aggiornata completa con tutti i campi dell'interfaccia
  const waybillData: Waybill = {
    // Spread dei dati dal form
    ...formValue.waybill,
    
    // Convert date to ISO format for server
    waybillDate: this.convertDateToISO(formValue.waybill.waybillDate),
    
    // Mantieni i dati esistenti necessari
    waybillId: waybillId,
    waybillDealerCode: dealerCode,
    waybillCode: waybill.waybillCode || '',
    waybillShipmentCode: waybill.waybillShipmentCode || '',
    
    // Campi con valori di default se non presenti
    waybillPalletCode: formValue.waybill.waybillPalletCode || waybill.waybillPalletCode || null,
    waybillTrackingNumber: formValue.waybill.waybillTrackingNumber || waybill.waybillTrackingNumber || null,
    waybillActualDeliveryDate: formValue.waybill.waybillActualDeliveryDate || waybill.waybillActualDeliveryDate || null,
    waybillCreatedAt: waybill.waybillCreatedAt || new Date().toISOString(),
    waybillUpdatedAt: new Date().toISOString(),
    waybillDeliveryMethod: formValue.waybill.waybillDeliveryMethod || waybill.waybillDeliveryMethod || null,
    waybillDeliveryType: formValue.waybill.waybillDeliveryType || waybill.waybillDeliveryType || null,
    waybillTransitTimeHours: formValue.waybill.waybillTransitTimeHours || waybill.waybillTransitTimeHours || null,
    waybillOriginLocation: formValue.waybill.waybillOriginLocation || waybill.waybillOriginLocation || null, // Warehouse code (origin location)
    waybillDestinationLocation: formValue.waybill.waybillDestinationLocation || waybill.waybillDestinationLocation || null,
    waybillLastKnownLocation: formValue.waybill.waybillLastKnownLocation || waybill.waybillLastKnownLocation || null,
    waybillLatitude: formValue.waybill.waybillLatitude || waybill.waybillLatitude || null,
    waybillLongitude: formValue.waybill.waybillLongitude || waybill.waybillLongitude || null,
    waybillOriginalLatitude: formValue.waybill.waybillOriginalLatitude || waybill.waybillOriginalLatitude || null,
    waybillOriginalLongitude: formValue.waybill.waybillOriginalLongitude || waybill.waybillOriginalLongitude || null,
    waybillTotalWeight: formValue.waybill.waybillTotalWeight || waybill.waybillTotalWeight || null,
    waybillTotalVolume: formValue.waybill.waybillTotalVolume || waybill.waybillTotalVolume || null,
    waybillFragile: formValue.waybill.waybillFragile !== undefined ? formValue.waybill.waybillFragile : (waybill.waybillFragile || 'N'),
    waybillHazardousMaterial: formValue.waybill.waybillHazardousMaterial !== undefined ? formValue.waybill.waybillHazardousMaterial : (waybill.waybillHazardousMaterial || 'N'),
    waybillCustomsClearanceRequired: formValue.waybill.waybillCustomsClearanceRequired !== undefined ? formValue.waybill.waybillCustomsClearanceRequired : (waybill.waybillCustomsClearanceRequired || 'N'),
    waybillBrandCode: formValue.waybill.waybillBrandCode || waybill.waybillBrandCode || null,
    waybillCarrierCode: formValue.waybill.waybillCarrierCode || waybill.waybillCarrierCode || null,
    waybillStatus: formValue.waybill.waybillStatus || waybill.waybillStatus || WaybillStatus.ACTIVE,
    waybillUpdatedBy: formValue.waybill.waybillUpdatedBy || waybill.waybillUpdatedBy || null,
    waybillNote: formValue.waybill.waybillNote || waybill.waybillNote || null,
    waybillFile: formValue.waybill.waybillFile || waybill.waybillFile || null,
    waybillVersion: (waybill.waybillVersion || 1) + 1
  };

  // Crea i colli aggiornati completi con tutti i campi dell'interfaccia
  const parcelsData: Parcel[] = formValue.parcels.map((parcel: any) => {
    // Trova il collo esistente se disponibile
    const existingParcel = this.parcels.find(p => p.parcelCode === parcel.parcelCode && p.parcelWaybillCode === waybill.waybillCode);
    
    return {
      // Spread dei dati dal form
      ...parcel,
      
      // Mantieni i dati esistenti necessari
      parcelId: existingParcel?.parcelId || undefined,
      parcelWaybillCode: waybill.waybillCode,
      parcelDealerCode: dealerCode,
      parcelCode: parcel.parcelCode || existingParcel?.parcelCode,
      
      // Campi con valori di default se non presenti
      parcelPalletCode: parcel.parcelPalletCode || existingParcel?.parcelPalletCode || null,      parcelShipmentCode: parcel.parcelShipmentCode || existingParcel?.parcelShipmentCode || null,
      parcelBrandCode: parcel.parcelBrandCode || existingParcel?.parcelBrandCode || 'DEFAULT',
      parcelClientCode: parcel.parcelClientCode || existingParcel?.parcelClientCode || waybillData.waybillClientCode || null,
      parcelBarcode: parcel.parcelCode || existingParcel?.parcelCode || parcel.parcelBarcode || existingParcel?.parcelBarcode || null,
      parcelTrackingNumber: parcel.parcelTrackingNumber || existingParcel?.parcelTrackingNumber || null,
      parcelDate: parcel.parcelDate || existingParcel?.parcelDate || new Date().toISOString(),
      parcelDeliveryDate: parcel.parcelDeliveryDate || existingParcel?.parcelDeliveryDate || null,
      parcelEstimatedDeliveryDate: parcel.parcelEstimatedDeliveryDate || existingParcel?.parcelEstimatedDeliveryDate || null,
      parcelOriginalDeliveryDate: parcel.parcelOriginalDeliveryDate || existingParcel?.parcelOriginalDeliveryDate || null,
      parcelLastEventDate: parcel.parcelLastEventDate || existingParcel?.parcelLastEventDate || null,
      parcelTakeInChargeDate: parcel.parcelTakeInChargeDate || existingParcel?.parcelTakeInChargeDate || null,
      parcelLastUpdateAt: parcel.parcelLastUpdateAt || existingParcel?.parcelLastUpdateAt || null,
      parcelUpdatedAt: new Date().toISOString(),
      parcelCreatedAt: existingParcel?.parcelCreatedAt || new Date().toISOString(),
      parcelDeliveryStatus: parcel.parcelDeliveryStatus || existingParcel?.parcelDeliveryStatus || null,
      parcelDeliveryMethod: parcel.parcelDeliveryMethod || existingParcel?.parcelDeliveryMethod || null,
      parcelDeliveryType: parcel.parcelDeliveryType || existingParcel?.parcelDeliveryType || null,
      parcelDeliveryMaterial: parcel.parcelDeliveryMaterial || existingParcel?.parcelDeliveryMaterial || null,
      parcelDeliveryReason: parcel.parcelDeliveryReason || existingParcel?.parcelDeliveryReason || null,
      parcelTakeInChargeReason: parcel.parcelTakeInChargeReason || existingParcel?.parcelTakeInChargeReason || null,
      parcelDescription: parcel.parcelDescription || existingParcel?.parcelDescription || null,
      parcelWeight: parcel.parcelWeight || existingParcel?.parcelWeight || null,
      parcelVolume: parcel.parcelVolume || existingParcel?.parcelVolume || null,
      parcelLength: parcel.parcelLength || existingParcel?.parcelLength || null,
      parcelWidth: parcel.parcelWidth || existingParcel?.parcelWidth || null,
      parcelHeight: parcel.parcelHeight || existingParcel?.parcelHeight || null,
      parcelType: parcel.parcelType || existingParcel?.parcelType || null,
      parcelLocation: parcel.parcelLocation || existingParcel?.parcelLocation || null,
      parcelLatitude: parcel.parcelLatitude || existingParcel?.parcelLatitude || null,
      parcelLongitude: parcel.parcelLongitude || existingParcel?.parcelLongitude || null,
      parcelOriginalLatitude: parcel.parcelOriginalLatitude || existingParcel?.parcelOriginalLatitude || null,
      parcelOriginalLongitude: parcel.parcelOriginalLongitude || existingParcel?.parcelOriginalLongitude || null,
      parcelFragile: parcel.parcelFragile || existingParcel?.parcelFragile || 'N',
      parcelHazardousMaterial: parcel.parcelHazardousMaterial || existingParcel?.parcelHazardousMaterial || 'N',
      parcelTemperatureSensitive: parcel.parcelTemperatureSensitive || existingParcel?.parcelTemperatureSensitive || 'N',
      parcelHighValueItem: parcel.parcelHighValueItem || existingParcel?.parcelHighValueItem || 'N',
      parcelRestrictedItem: parcel.parcelRestrictedItem || existingParcel?.parcelRestrictedItem || 'N',
      parcelCustomsClearanceRequired: parcel.parcelCustomsClearanceRequired || existingParcel?.parcelCustomsClearanceRequired || 'N',
      parcelDeliveryDateIsForced: parcel.parcelDeliveryDateIsForced || existingParcel?.parcelDeliveryDateIsForced || 'N',
      parcelDeliveryDateIsUpdated: parcel.parcelDeliveryDateIsUpdated || existingParcel?.parcelDeliveryDateIsUpdated || 'N',
      parcelDeliveryCoordinatesIsForced: parcel.parcelDeliveryCoordinatesIsForced || existingParcel?.parcelDeliveryCoordinatesIsForced || 'N',
      parcelDeliveryCoordinatesIsUpdated: parcel.parcelDeliveryCoordinatesIsUpdated || 'N',
      parcelInsuredValue: parcel.parcelInsuredValue || existingParcel?.parcelInsuredValue || null,
      parcelStatus: parcel.parcelStatus || existingParcel?.parcelStatus || ParcelStatus.ACTIVE,
      parcelVersion: ((existingParcel?.parcelVersion || 1) + 1),
      parcelLastUpdateBy: parcel.parcelLastUpdateBy || existingParcel?.parcelLastUpdateBy || null,
      parcelUpdatedBy: parcel.parcelUpdatedBy || existingParcel?.parcelUpdatedBy || null,
      parcelDetailStatus: parcel.parcelDetailStatus || ParcelStatus.PREPARED,
    };
  });

  // Crea l'oggetto Shipment composto per l'update
  const shipment: Shipment = {
    waybill: waybillData,
    parcels: parcelsData
  };

  // console.log('Updating shipment data:', shipment);

  // Usa un metodo di update del servizio che gestisce sia waybill che parcels
  this.shipmentRequestService.updateShipment(waybill.waybillCode || '',waybill.waybillShipmentCode , shipment)
    .pipe(
      catchError(error => {
        // console.error('Error updating shipment:', error);
        const message = this.errorHelperService.extractBackendMessage(error);
        this.showErrorMessage(`Errore nell'aggiornamento dei dati al server: ${message}`);
        return of(null);
      }),
      finalize(() => {
        this.isSubmitting = false;
      })
    )
    .subscribe((response: Shipment | null) => {
      if (response) {
        this.resetForm();
        this.showSuccessMessage('Spedizione aggiornata con successo');
        this.refreshTables();
      }
    });
}  private updateParcel(formValue: any): void {
    const parcelId = this.editingParcelId!;
    const waybillId = this.editingWaybillId!;
    
    const parcel = this.parcels.find(p => p.parcelId === parcelId);
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
      if (!parcel || !waybill) {
      this.showErrorMessage('Collo o bolla non trovata');
      this.isSubmitting = false;
      return;
    }

    // Prendi il codice dealer dal form o dalla waybill esistente
    const dealerCode = formValue.destination?.dealerCode || waybill.waybillDealerCode;
    
    // Crea la waybill aggiornata completa con tutti i campi dell'interfaccia
    const waybillData: Waybill = {
      // Spread dei dati dal form
      ...formValue.waybill,
      
      // Convert date to ISO format for server
      waybillDate: this.convertDateToISO(formValue.waybill.waybillDate),
      
      // Mantieni i dati esistenti necessari
      waybillId: waybillId,
      waybillDealerCode: dealerCode,
      waybillCode: waybill.waybillCode || '',
      waybillShipmentCode: waybill.waybillShipmentCode || '',
      
      // Campi con valori di default se non presenti
      waybillPalletCode: formValue.waybill.waybillPalletCode || waybill.waybillPalletCode || null,
      waybillTrackingNumber: formValue.waybill.waybillTrackingNumber || waybill.waybillTrackingNumber || null,
      waybillActualDeliveryDate: formValue.waybill.waybillActualDeliveryDate || waybill.waybillActualDeliveryDate || null,
      waybillCreatedAt: waybill.waybillCreatedAt || new Date().toISOString(),
      waybillUpdatedAt: new Date().toISOString(),
      waybillDeliveryMethod: formValue.waybill.waybillDeliveryMethod || waybill.waybillDeliveryMethod || null,
      waybillDeliveryType: formValue.waybill.waybillDeliveryType || waybill.waybillDeliveryType || null,
      waybillTransitTimeHours: formValue.waybill.waybillTransitTimeHours || waybill.waybillTransitTimeHours || null,
      waybillOriginLocation: formValue.waybill.waybillOriginLocation || waybill.waybillOriginLocation || null,
      waybillDestinationLocation: formValue.waybill.waybillDestinationLocation || waybill.waybillDestinationLocation || null,
      waybillLastKnownLocation: formValue.waybill.waybillLastKnownLocation || waybill.waybillLastKnownLocation || null,
      waybillLatitude: formValue.waybill.waybillLatitude || waybill.waybillLatitude || null,
      waybillLongitude: formValue.waybill.waybillLongitude || waybill.waybillLongitude || null,
      waybillOriginalLatitude: formValue.waybill.waybillOriginalLatitude || waybill.waybillOriginalLatitude || null,
      waybillOriginalLongitude: formValue.waybill.waybillOriginalLongitude || waybill.waybillOriginalLongitude || null,
      waybillTotalWeight: formValue.waybill.waybillTotalWeight || waybill.waybillTotalWeight || null,
      waybillTotalVolume: formValue.waybill.waybillTotalVolume || waybill.waybillTotalVolume || null,
      waybillFragile: formValue.waybill.waybillFragile !== undefined ? formValue.waybill.waybillFragile : (waybill.waybillFragile || 'N'),
      waybillHazardousMaterial: formValue.waybill.waybillHazardousMaterial !== undefined ? formValue.waybill.waybillHazardousMaterial : (waybill.waybillHazardousMaterial || 'N'),
      waybillCustomsClearanceRequired: formValue.waybill.waybillCustomsClearanceRequired !== undefined ? formValue.waybill.waybillCustomsClearanceRequired : (waybill.waybillCustomsClearanceRequired || 'N'),
      waybillBrandCode: formValue.waybill.waybillBrandCode || waybill.waybillBrandCode || null,
      waybillCarrierCode: formValue.waybill.waybillCarrierCode || waybill.waybillCarrierCode || null,
      waybillStatus: formValue.waybill.waybillStatus || waybill.waybillStatus || WaybillStatus.ACTIVE,
      waybillUpdatedBy: formValue.waybill.waybillUpdatedBy || waybill.waybillUpdatedBy || null,
      waybillNote: formValue.waybill.waybillNote || waybill.waybillNote || null,
      waybillFile: formValue.waybill.waybillFile || waybill.waybillFile || null,
      waybillVersion: (waybill.waybillVersion || 1) + 1
    };

    // Crea i colli aggiornati completi con tutti i campi dell'interfaccia
    const parcelsData: Parcel[] = formValue.parcels.map((parcelForm: any) => ({
      // Spread dei dati dal form
      ...parcelForm,
      
      // Mantieni i dati esistenti necessari
      parcelId: parcel.parcelId,
      parcelWaybillCode: waybill.waybillCode,
      parcelDealerCode: dealerCode,
      parcelCode: parcelForm.parcelCode || parcel.parcelCode || `PRC-${Date.now()}`,
      
      // Campi con valori di default se non presenti
      parcelPalletCode: parcelForm.parcelPalletCode || parcel.parcelPalletCode || null,      parcelShipmentCode: parcelForm.parcelShipmentCode || parcel.parcelShipmentCode || null,
      parcelBrandCode: parcelForm.parcelBrandCode || parcel.parcelBrandCode || 'DEFAULT',
      parcelClientCode: parcelForm.parcelClientCode || parcel.parcelClientCode || waybillData.waybillClientCode || null,
      parcelBarcode: parcelForm.parcelCode || parcel.parcelCode || parcelForm.parcelBarcode || parcel.parcelBarcode || null,
      parcelTrackingNumber: parcelForm.parcelTrackingNumber || parcel.parcelTrackingNumber || null,
      parcelDate: parcelForm.parcelDate || parcel.parcelDate || new Date().toISOString(),
      parcelDeliveryDate: parcelForm.parcelDeliveryDate || parcel.parcelDeliveryDate || null,
      parcelEstimatedDeliveryDate: parcelForm.parcelEstimatedDeliveryDate || parcel.parcelEstimatedDeliveryDate || null,
      parcelOriginalDeliveryDate: parcelForm.parcelOriginalDeliveryDate || parcel.parcelOriginalDeliveryDate || null,
      parcelLastEventDate: parcelForm.parcelLastEventDate || parcel.parcelLastEventDate || null,
      parcelTakeInChargeDate: parcelForm.parcelTakeInChargeDate || parcel.parcelTakeInChargeDate || null,
      parcelLastUpdateAt: parcelForm.parcelLastUpdateAt || parcel.parcelLastUpdateAt || null,
      parcelUpdatedAt: new Date().toISOString(),
      parcelCreatedAt: parcel.parcelCreatedAt || new Date().toISOString(),
      parcelDeliveryStatus: parcelForm.parcelDeliveryStatus || parcel.parcelDeliveryStatus || null,
      parcelDeliveryMethod: parcelForm.parcelDeliveryMethod || parcel.parcelDeliveryMethod || null,
      parcelDeliveryType: parcelForm.parcelDeliveryType || parcel.parcelDeliveryType || null,
      parcelDeliveryMaterial: parcelForm.parcelDeliveryMaterial || parcel.parcelDeliveryMaterial || null,
      parcelDeliveryReason: parcelForm.parcelDeliveryReason || parcel.parcelDeliveryReason || null,
      parcelTakeInChargeReason: parcelForm.parcelTakeInChargeReason || parcel.parcelTakeInChargeReason || null,
      parcelDescription: parcelForm.parcelDescription || parcel.parcelDescription || null,
      parcelWeight: parcelForm.parcelWeight || parcel.parcelWeight || null,
      parcelVolume: parcelForm.parcelVolume || parcel.parcelVolume || null,
      parcelLength: parcelForm.parcelLength || parcel.parcelLength || null,
      parcelWidth: parcelForm.parcelWidth || parcel.parcelWidth || null,
      parcelHeight: parcelForm.parcelHeight || parcel.parcelHeight || null,
      parcelType: parcelForm.parcelType || parcel.parcelType || null,
      parcelLocation: parcelForm.parcelLocation || parcel.parcelLocation || null,
      parcelLatitude: parcelForm.parcelLatitude || parcel.parcelLatitude || null,
      parcelLongitude: parcelForm.parcelLongitude || parcel.parcelLongitude || null,
      parcelOriginalLatitude: parcelForm.parcelOriginalLatitude || parcel.parcelOriginalLatitude || null,
      parcelOriginalLongitude: parcelForm.parcelOriginalLongitude || parcel.parcelOriginalLongitude || null,
      parcelFragile: parcelForm.parcelFragile || parcel.parcelFragile || 'N',
      parcelHazardousMaterial: parcelForm.parcelHazardousMaterial || parcel.parcelHazardousMaterial || 'N',
      parcelTemperatureSensitive: parcelForm.parcelTemperatureSensitive || parcel.parcelTemperatureSensitive || 'N',
      parcelHighValueItem: parcelForm.parcelHighValueItem || parcel.parcelHighValueItem || 'N',
      parcelRestrictedItem: parcelForm.parcelRestrictedItem || parcel.parcelRestrictedItem || 'N',
      parcelCustomsClearanceRequired: parcelForm.parcelCustomsClearanceRequired || parcel.parcelCustomsClearanceRequired || 'N',
      parcelDeliveryDateIsForced: parcelForm.parcelDeliveryDateIsForced || parcel.parcelDeliveryDateIsForced || 'N',
      parcelDeliveryDateIsUpdated: parcelForm.parcelDeliveryDateIsUpdated || parcel.parcelDeliveryDateIsUpdated || 'N',
      parcelDeliveryCoordinatesIsForced: parcelForm.parcelDeliveryCoordinatesIsForced || parcel.parcelDeliveryCoordinatesIsForced || 'N',
      parcelDeliveryCoordinatesIsUpdated: parcelForm.parcelDeliveryCoordinatesIsUpdated || 'N',
      parcelInsuredValue: parcelForm.parcelInsuredValue || parcel.parcelInsuredValue || null,
      parcelStatus: parcelForm.parcelStatus || parcel.parcelStatus || ParcelStatus.ACTIVE,
      parcelVersion: ((parcel.parcelVersion || 1) + 1),
      parcelLastUpdateBy: parcelForm.parcelLastUpdateBy || parcel.parcelLastUpdateBy || null,
      parcelUpdatedBy: parcelForm.parcelUpdatedBy || parcel.parcelUpdatedBy || null,
      parcelDetailStatus: parcelForm.parcelDetailStatus || ParcelStatus.PREPARED,
    }));

    // Crea l'oggetto Shipment composto per l'update
    const shipment: Shipment = {
      waybill: waybillData,
      parcels: parcelsData
    };

    // console.log('Updating shipment from parcel edit:', shipment);

    // Usa il metodo updateShipment che gestisce sia waybill che parcels
    this.shipmentRequestService.updateShipment(waybill.waybillCode || '',waybill.waybillShipmentCode, shipment)
      .pipe(
        catchError(error => {
          // console.error('Error updating shipment from parcel:', error);
          const message = this.errorHelperService.extractBackendMessage(error);
          this.showErrorMessage(`Errore nell'aggiornamento della spedizione: ${message}`);
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe((response: Shipment | null) => {
        if (response) {
          this.resetForm();
          this.showSuccessMessage('Spedizione aggiornata con successo');
          this.refreshTables();
        }
      });
  }
  resetForm(): void {
    this.shipmentForm.reset();    // Reset editing state
    this.editingWaybillId = null;
    this.editingParcelId = null;

    // Reset dealer selection state
    this.isDealerSelected = false;
    this.selectedDealer = null;

    // Enable all dealer fields
    this.enableAllDealerFields();

    // Riabilita i campi Client e Brand dopo il reset
    this.shipmentForm.get('waybill.waybillClientCode')?.enable();
    this.shipmentForm.get('waybill.waybillBrandCode')?.enable();

    // Reset date to today in dd/MM/yyyy format
    const today = new Date();
    const currentDate = this.formatDateForDisplay(today);    // Set default client and filter corresponding brands
    let defaultClientCode = '';
    let defaultBrandCode = '';
    let defaultWarehouseCode = '';
    
    // Solo se c'è un solo client, lo impostiamo come default
    if (this.filteredClients.length === 1) {
      defaultClientCode = this.filteredClients[0].clientCode;
      
      // Find the first client in clientsBrands to get its associated brands
      const firstClientBrands = this.clientsBrands.find(cb => cb.clientCode === defaultClientCode);
      if (firstClientBrands && firstClientBrands.brands.length > 0) {
        // Set filtered brands for the UI
        this.filteredBrands = firstClientBrands.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }));
        
        // Solo se c'è un solo brand, lo impostiamo come default
        if (this.filteredBrands.length === 1) {
          defaultBrandCode = this.filteredBrands[0].brandCode;
        }
        // Se ci sono più brand, lasciamo vuoto per forzare la selezione
      }

      // Filtra i magazzini in base al client selezionato
      this.filteredWarehouses = this.filterWarehousesByUserConfig(
        this.getWarehousesForClient(defaultClientCode)
      );

      // Abilita il campo warehouse
      const warehouseControl = this.shipmentForm.get('waybill.waybillOriginLocation');
      warehouseControl?.enable();

      // Preselezione magazzino: singolo disponibile, oppure primo dei
      // magazzini configurati per l'utente che combaci con quelli filtrati.
      defaultWarehouseCode = this.pickDefaultWarehouseCode(this.filteredWarehouses);
    } else if (this.filteredClients.length > 1) {
      // Se ci sono più client, mostra tutti i brand disponibili ma non preselezionarne nessuno
      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      );

      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );
      // Non impostiamo defaultBrandCode, rimane vuoto per forzare la selezione

      // Disabilita il campo warehouse e resetta i magazzini filtrati
      this.filteredWarehouses = [];
      this.shipmentForm.get('waybill.waybillOriginLocation')?.disable();
    } else {
      // Nessun client disponibile
      this.filteredWarehouses = [];
      this.shipmentForm.get('waybill.waybillOriginLocation')?.disable();
    }

    this.shipmentForm.patchValue({
      waybill: {
        waybillDate: currentDate,
        waybillStatus: WaybillStatus.ACTIVE,
        waybillClientCode: defaultClientCode,
        waybillBrandCode: defaultBrandCode,
        waybillOriginLocation: defaultWarehouseCode
      },
      destination: {
        dealerStatus: DealerStatus.ACTIVE
      }
    });

    // Update flatpickr instance with the new date
    if (this.flatpickrInstance) {
      this.flatpickrInstance.setDate(currentDate, false);
      if (this.requestedDateInput?.nativeElement) {
        this.requestedDateInput.nativeElement.value = currentDate;
      }
    }

    // Remove extra parcels, keep only one
    while (this.parcelsArray.length > 1) {
      this.parcelsArray.removeAt(1);
    }
    
    // Reset parcels collapsed array to have only one expanded parcel
    this.parcelsCollapsed = [false];

    // Reset the single parcel
    this.parcelsArray.at(0).patchValue({
      parcelStatus: ParcelStatus.ACTIVE,
      parcelCreatedAt: new Date().toISOString(),
      parcelUpdatedAt: new Date().toISOString(),
      parcelDetailStatus: ParcelStatus.PREPARED
    });
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }
  
  deleteShipment(waybillId: number): void {
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
    if (!waybill) return;

    const associatedParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);
    const parcelCount = associatedParcels.length;    // Show confirmation dialog with parcel count
    this.showConfirmation(
      this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_WAYBILL_TITLE'),
      parcelCount === 1 
        ? this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_WAYBILL_SINGLE_MESSAGE')
        : this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_WAYBILL_MESSAGE', { count: parcelCount }),
      () => this.executeWaybillDeletion(waybillId)
    );
  }

  private executeWaybillDeletion(waybillId: number): void {
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
    if (!waybill) return;

    // Prima elimina tutti i colli associati a questa waybill
    const associatedParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);

    // Elimina i colli dal database
    associatedParcels.forEach(parcel => {
      if (parcel.parcelId) {
        // this.shipmentRequestService.deleteParcel(parcel.parcelId.toString()).subscribe({
        //   error: (error) => console.error('Error deleting parcel:', error)
        // });
      }
    });

    var clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();

    // Elimina la waybill dal database
    this.shipmentRequestService.deleteWaybill(waybill.waybillCode,waybill.waybillShipmentCode, clientsBrandsSelected).subscribe({
      next: () => {
        // Rimuovi dalle liste locali
        this.waybills = this.waybills.filter(w => w.waybillCode !== waybill.waybillCode);
        this.parcels = this.parcels.filter(p => p.parcelWaybillCode !== waybill.waybillCode);
        this.showSuccessMessage('Spedizione eliminata con successo');
        // Il filtro locale aggiorna subito la tabella dati, ma borderò e
        // riepilogo restano allineati solo ricaricando dal backend
        this.refreshTables();
      },
      error: (_error) => {
        // console.error('Error deleting waybill:', _error);
        this.showErrorMessage('Errore nell\'eliminazione della spedizione');
      }    });
  }  // Confirmation dialog methods
  private showConfirmation(title: string, message: string, action: () => void): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.confirmationAction = action;
    this.showConfirmDialog = true;
  }

  confirmAction(): void {
    if (this.confirmationAction) {
      this.confirmationAction();
    }
    this.closeConfirmationDialog();
  }
  closeConfirmationDialog(): void {
    this.showConfirmDialog = false;
    this.confirmationTitle = '';
    this.confirmationMessage = '';
    this.confirmationAction = null;
  }  // New method for deleting individual parcels
 
  // deleteParcel(parcelId: number): void {
    
  //   const parcel = this.parcels.find(p => p.parcelId === parcelId);
  //   console.log('Deleting parcel:', parcel);
  //   if (!parcel) {
  //     return;
  //   }

    
  //   // Check if this is the last parcel for the waybill
  //   const waybillParcels = this.parcels.filter(p => p.parcelWaybillCode === parcel.parcelWaybillCode);
  //   const isLastParcel = waybillParcels.length === 1;if (isLastParcel) {
  //     // This is the last parcel - warn about waybill deletion
  //     this.showConfirmation(
  //       this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_LAST_PARCEL_TITLE'),
  //       this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_LAST_PARCEL_MESSAGE'),
  //       () => this.executeParcelDeletion(parcelId, true)
  //     );
  //   } else {
  //     // Normal parcel deletion
  //     this.showConfirmation(
  //       this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_PARCEL_TITLE'),
  //       this.translate.instant('SHIPMENT_REQUEST.CONFIRM_DELETE_PARCEL_MESSAGE'),
  //       () => this.executeParcelDeletion(parcelId, false)
  //     );
  //   }
  // }

  // private executeParcelDeletion(parcelId: number, isLastParcel: boolean): void {
  //   const parcel = this.parcels.find(p => p.parcelId === parcelId);
  //   if (!parcel) return;

  //   this.shipmentRequestService.deleteParcel(parcelId.toString()).subscribe({
  //     next: () => {
  //       // Remove parcel from local list
  //       this.parcels = this.parcels.filter(p => p.parcelId !== parcelId);

  //       if (isLastParcel) {
  //         // Also remove the waybill since this was the last parcel
  //         const waybill = this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode);
  //         if (waybill && waybill.waybillId) {
  //           this.shipmentRequestService.deleteWaybill(waybill.waybillId.toString()).subscribe({
  //             next: () => {
  //               this.waybills = this.waybills.filter(w => w.waybillId !== waybill.waybillId);
  //               this.showSuccessMessage('Collo e bolla eliminati con successo');
  //             },
  //             error: (error) => {
  //               console.error('Error deleting waybill:', error);
  //               this.showErrorMessage('Errore nell\'eliminazione della bolla');
  //             }
  //           });
  //         }
  //       } else {
  //         this.showSuccessMessage('Collo eliminato con successo');
  //       }
  //     },
  //     error: (error) => {
  //       console.error('Error deleting parcel:', error);
  //       this.showErrorMessage('Errore nell\'eliminazione del collo');
  //     }
  //   });
  // }
  
  editShipment(waybillId: number): void {
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
    if (!waybill) return;

    const waybillParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);
    const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);

    // Prima di popolare il form, imposta i valori di Client e Brand e disabilitali
    const waybillClientCode = waybill.waybillClientCode || '';
    const waybillBrandCode = waybill.waybillBrandCode || '';

    // Filtra i brand per il client della waybill
    if (waybillClientCode) {
      const selectedClientBrands = this.clientsBrands.find(cb => cb.clientCode === waybillClientCode);
      if (selectedClientBrands) {
        this.filteredBrands = selectedClientBrands.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }));
      }

      // Filtra i magazzini per il client della waybill
      this.filteredWarehouses = this.filterWarehousesByUserConfig(
        this.getWarehousesForClient(waybillClientCode)
      );
    }

    // Popola il form con i dati esistenti - TUTTI I CAMPI DELLA WAYBILL
    this.shipmentForm.patchValue({
      waybill: {
        // Primary identifiers
        waybillCode: waybill.waybillCode,
        waybillPalletCode: waybill.waybillPalletCode,
        waybillShipmentCode: waybill.waybillShipmentCode,
        waybillTrackingNumber: waybill.waybillTrackingNumber,
        waybillOriginLocation: waybill.waybillOriginLocation || '', // Warehouse code (origin location)
        
        // Dates
        waybillDate: this.formatDateForDisplay(waybill.waybillDate),
        waybillEstimatedDeliveryDate: waybill.waybillEstimatedDeliveryDate,
        waybillActualDeliveryDate: waybill.waybillActualDeliveryDate,
        waybillOriginalDeliveryDate: waybill.waybillOriginalDeliveryDate,
        waybillCreatedAt: waybill.waybillCreatedAt,
        waybillUpdatedAt: waybill.waybillUpdatedAt,
        
        // Delivery information
        waybillDeliveryMethod: waybill.waybillDeliveryMethod,
        waybillDeliveryType: waybill.waybillDeliveryType,
        waybillTransitTimeHours: waybill.waybillTransitTimeHours,
        
        // Location information
        waybillDestinationLocation: waybill.waybillDestinationLocation,
        waybillLastKnownLocation: waybill.waybillLastKnownLocation,
        waybillLatitude: waybill.waybillLatitude,
        waybillLongitude: waybill.waybillLongitude,
        waybillOriginalLatitude: waybill.waybillOriginalLatitude,
        waybillOriginalLongitude: waybill.waybillOriginalLongitude,
        
        // Cargo information
        waybillTotalWeight: waybill.waybillTotalWeight,
        waybillTotalVolume: waybill.waybillTotalVolume,
        waybillFragile: waybill.waybillFragile,
        waybillHazardousMaterial: waybill.waybillHazardousMaterial,
        waybillCustomsClearanceRequired: waybill.waybillCustomsClearanceRequired,
        
        // Business information
        waybillClientCode: waybillClientCode,
        waybillBrandCode: waybillBrandCode,
        waybillDealerCode: waybill.waybillDealerCode,
        waybillCarrierCode: waybill.waybillCarrierCode,
        
        // Status and metadata
        waybillStatus: waybill.waybillStatus,
        waybillUpdatedBy: waybill.waybillUpdatedBy,
        waybillNote: waybill.waybillNote,
        waybillFile: waybill.waybillFile,
        waybillVersion: waybill.waybillVersion
      },destination: dealer ? {
        dealerCode: dealer.dealerCode,
        dealerName: dealer.dealerName,
        dealerVatNumber: dealer.dealerVatNumber,
        dealerInternalBillingId: dealer.dealerInternalBillingId,
        dealerInternalId: dealer.dealerInternalId,
        dealerDeliveryZoneCode: dealer.dealerDeliveryZoneCode,
        dealerBillingZoneCode: dealer.dealerBillingZoneCode,
        dealerIndustry: dealer.dealerIndustry,
        dealerStatus: dealer.dealerStatus,
        dealerOperatingPhone: '-', // Default value as these aren't in Dealer interface
        dealerOperatingEmail: '-', // Default value as these aren't in Dealer interface
        dealerLegalAddressStreet: dealer.dealerLegalAddressStreet,
        dealerLegalAddressNumber: dealer.dealerLegalAddressNumber,
        dealerLegalAddressCity: dealer.dealerLegalAddressCity,
        dealerLegalAddressZip: dealer.dealerLegalAddressZip,
        dealerLegalAddressProvince: dealer.dealerLegalAddressProvince,
        dealerLegalAddressCountry: dealer.dealerLegalAddressCountry,
        dealerOperatingAddressStreet: dealer.dealerOperatingAddressStreet,
        dealerOperatingAddressNumber: dealer.dealerOperatingAddressNumber,
        dealerOperatingAddressCity: dealer.dealerOperatingAddressCity,
        dealerOperatingAddressZip: dealer.dealerOperatingAddressZip,
        dealerOperatingAddressProvince: dealer.dealerOperatingAddressProvince,
        dealerOperatingAddressCountry: dealer.dealerOperatingAddressCountry
      } : {}
    });

    // Disabilita i campi Client e Brand in modalità modifica
    this.shipmentForm.get('waybill.waybillClientCode')?.disable();
    this.shipmentForm.get('waybill.waybillBrandCode')?.disable();
    
    // Abilita il campo warehouse se ci sono magazzini filtrati
    if (this.filteredWarehouses.length > 0) {
      this.shipmentForm.get('waybill.waybillOriginLocation')?.enable();
    }

    // Clear existing parcels
    while (this.parcelsArray.length > 0) {
      this.parcelsArray.removeAt(0);
    }

    // Reset parcels collapsed array
    this.parcelsCollapsed = [];

    // Add parcels from shipment
    if (waybillParcels.length === 0) {
      // Se non ci sono colli, aggiungi un collo vuoto
      this.parcelsArray.push(this.createParcelGroup());
      this.parcelsCollapsed.push(false); // Primo collo espanso
    } else {      waybillParcels.forEach((parcel, index) => {
        this.parcelsArray.push(this.fb.group({
          // Primary identifiers
          parcelCode: [{ value: parcel.parcelCode || '', disabled: true }], // Disabilitato come waybillCode
          parcelWaybillCode: [parcel.parcelWaybillCode || ''],
          parcelPalletCode: [parcel.parcelPalletCode || ''],
          parcelShipmentCode: [parcel.parcelShipmentCode || ''],
          parcelBrandCode: [parcel.parcelBrandCode || 'DEFAULT'],
          parcelDealerCode: [parcel.parcelDealerCode || ''],
          parcelBarcode: [parcel.parcelBarcode || ''],
          parcelTrackingNumber: [parcel.parcelTrackingNumber || ''],
          parcelClientCode: [parcel.parcelClientCode || ''],
          
          // Dates
          parcelDate: [parcel.parcelDate || new Date().toISOString()],
          parcelDeliveryDate: [parcel.parcelDeliveryDate || ''],
          parcelEstimatedDeliveryDate: [parcel.parcelEstimatedDeliveryDate || ''],
          parcelOriginalDeliveryDate: [parcel.parcelOriginalDeliveryDate || ''],
          parcelLastEventDate: [parcel.parcelLastEventDate || ''],
          parcelTakeInChargeDate: [parcel.parcelTakeInChargeDate || ''],
          parcelLastUpdateAt: [parcel.parcelLastUpdateAt || ''],
          parcelUpdatedAt: [parcel.parcelUpdatedAt || new Date().toISOString()],
          parcelCreatedAt: [parcel.parcelCreatedAt || new Date().toISOString()],
          
          // Delivery information
          parcelDeliveryStatus: [parcel.parcelDeliveryStatus || ''],
          parcelDeliveryMethod: [parcel.parcelDeliveryMethod || ''],
          parcelDeliveryType: [parcel.parcelDeliveryType || ''],
          parcelDeliveryMaterial: [parcel.parcelDeliveryMaterial || ''],
          parcelDeliveryReason: [parcel.parcelDeliveryReason || ''],
          parcelTakeInChargeReason: [parcel.parcelTakeInChargeReason || ''],
          
          // Physical properties
          parcelDescription: [parcel.parcelDescription || '', [Validators.maxLength(40)]],
          parcelWeight: [parcel.parcelWeight, [this.weightVolumeValidator.bind(this)]],
          parcelVolume: [parcel.parcelVolume, [this.volumeValidator.bind(this)]],
          parcelLength: [parcel.parcelLength, [this.dimensionValidator.bind(this)]],
          parcelWidth: [parcel.parcelWidth, [this.dimensionValidator.bind(this)]],
          parcelHeight: [parcel.parcelHeight, [this.dimensionValidator.bind(this)]],
          parcelType: [parcel.parcelType || ''],
          
          // Location information
          parcelLocation: [parcel.parcelLocation || ''],
          parcelLatitude: [parcel.parcelLatitude],
          parcelLongitude: [parcel.parcelLongitude],
          parcelOriginalLatitude: [parcel.parcelOriginalLatitude],
          parcelOriginalLongitude: [parcel.parcelOriginalLongitude],
          
          // Special handling flags (Y/N strings from database)
          parcelFragile: [parcel.parcelFragile || 'N'],
          parcelHazardousMaterial: [parcel.parcelHazardousMaterial || 'N'],
          parcelTemperatureSensitive: [parcel.parcelTemperatureSensitive || 'N'],
          parcelHighValueItem: [parcel.parcelHighValueItem || 'N'],
          parcelRestrictedItem: [parcel.parcelRestrictedItem || 'N'],
          parcelCustomsClearanceRequired: [parcel.parcelCustomsClearanceRequired || 'N'],
          parcelDeliveryDateIsForced: [parcel.parcelDeliveryDateIsForced || 'N'],
          parcelDeliveryDateIsUpdated: [parcel.parcelDeliveryDateIsUpdated || 'N'],
          parcelDeliveryCoordinatesIsForced: [parcel.parcelDeliveryCoordinatesIsForced || 'N'],
          parcelDeliveryCoordinatesIsUpdated: [parcel.parcelDeliveryCoordinatesIsUpdated || 'N'],
          
          // Financial information
          parcelInsuredValue: [parcel.parcelInsuredValue, [this.insuredValueValidator.bind(this)]],
          
          // Status and metadata
          parcelStatus: [parcel.parcelStatus || ParcelStatus.ACTIVE],
          parcelDetailStatus: [parcel.parcelDetailStatus || ParcelStatus.PREPARED],
          parcelVersion: [parcel.parcelVersion || 1],
          parcelLastUpdateBy: [parcel.parcelLastUpdateBy || ''],
          parcelUpdatedBy: [parcel.parcelUpdatedBy || '']
        }));
        
        // Aggiungi stato di collasso per ogni collo (solo il primo espanso)
        this.parcelsCollapsed.push(index === 0 ? false : true);
      });
    }

    // Set dealer as selected if found
    if (dealer) {
      this.setDealerAsSelected(dealer);
    }

    // Scroll to form and expand it
    this.isFormCollapsed = false;    
    
    // Store the waybill ID being edited for update operations
    this.editingWaybillId = waybillId;
    
    // Force validation of all parcel fields after loading existing data
    this.parcelsArray.controls.forEach(parcelControl => {
      parcelControl.markAllAsTouched();
      parcelControl.updateValueAndValidity();
    });
  }  editParcel(parcelId: number): void {
    // console.log('Editing parcel with ID:', parcelId);
    const parcel = this.parcels.find(p => p.parcelId === parcelId);
    if (!parcel) {
      return;
    }

    const waybill = this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode);
    if (!waybill) {
      return;
    }

    const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);
    
    // Reset form to empty state first
    this.resetForm();

    // Prima di popolare il form, imposta i valori di Client e Brand e disabilitali
    const waybillClientCode = waybill.waybillClientCode || '';
    const waybillBrandCode = waybill.waybillBrandCode || '';

    // Filtra i brand per il client della waybill
    if (waybillClientCode) {
      const selectedClientBrands = this.clientsBrands.find(cb => cb.clientCode === waybillClientCode);
      if (selectedClientBrands) {
        this.filteredBrands = selectedClientBrands.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }));
      }

      // Filtra i magazzini per il client della waybill
      this.filteredWarehouses = this.filterWarehousesByUserConfig(
        this.getWarehousesForClient(waybillClientCode)
      );
    }

    // Fill the form with waybill data - TUTTI I CAMPI DELLA WAYBILL
    this.shipmentForm.patchValue({
      waybill: {
        // Primary identifiers
        waybillCode: waybill.waybillCode,
        waybillPalletCode: waybill.waybillPalletCode,
        waybillShipmentCode: waybill.waybillShipmentCode,
        waybillTrackingNumber: waybill.waybillTrackingNumber,
        waybillOriginLocation: waybill.waybillOriginLocation || '', // Warehouse code (origin location)
        
        // Dates
        waybillDate: waybill.waybillDate, // this.formatDateForInput(waybill.waybillDate),
        waybillEstimatedDeliveryDate: waybill.waybillEstimatedDeliveryDate,
        waybillActualDeliveryDate: waybill.waybillActualDeliveryDate,
        waybillOriginalDeliveryDate: waybill.waybillOriginalDeliveryDate,
        waybillCreatedAt: waybill.waybillCreatedAt,
        waybillUpdatedAt: waybill.waybillUpdatedAt,
        
        // Delivery information
        waybillDeliveryMethod: waybill.waybillDeliveryMethod,
        waybillDeliveryType: waybill.waybillDeliveryType,
        waybillTransitTimeHours: waybill.waybillTransitTimeHours,
        
        // Location information
        waybillDestinationLocation: waybill.waybillDestinationLocation,
        waybillLastKnownLocation: waybill.waybillLastKnownLocation,
        waybillLatitude: waybill.waybillLatitude,
        waybillLongitude: waybill.waybillLongitude,
        waybillOriginalLatitude: waybill.waybillOriginalLatitude,
        waybillOriginalLongitude: waybill.waybillOriginalLongitude,
        
        // Cargo information
        waybillTotalWeight: waybill.waybillTotalWeight,
        waybillTotalVolume: waybill.waybillTotalVolume,
        waybillFragile: waybill.waybillFragile,
        waybillHazardousMaterial: waybill.waybillHazardousMaterial,
        waybillCustomsClearanceRequired: waybill.waybillCustomsClearanceRequired,
        
        // Business information
        waybillClientCode: waybillClientCode,
        waybillBrandCode: waybillBrandCode,
        waybillDealerCode: waybill.waybillDealerCode,
        waybillCarrierCode: waybill.waybillCarrierCode,
        
        // Status and metadata
        waybillStatus: waybill.waybillStatus,
        waybillUpdatedBy: waybill.waybillUpdatedBy,
        waybillNote: waybill.waybillNote,
        waybillFile: waybill.waybillFile,
        waybillVersion: waybill.waybillVersion
      },
      destination: dealer ? {
        dealerCode: dealer.dealerCode,
        dealerName: dealer.dealerName,
        dealerVatNumber: dealer.dealerVatNumber,
        dealerInternalBillingId: dealer.dealerInternalBillingId,
        dealerInternalId: dealer.dealerInternalId,
        dealerDeliveryZoneCode: dealer.dealerDeliveryZoneCode,
        dealerBillingZoneCode: dealer.dealerBillingZoneCode,
        dealerIndustry: dealer.dealerIndustry,
        dealerStatus: dealer.dealerStatus,
        dealerOperatingPhone: '-',
        dealerOperatingEmail: '-',
        dealerLegalAddressStreet: dealer.dealerLegalAddressStreet,
        dealerLegalAddressNumber: dealer.dealerLegalAddressNumber,
        dealerLegalAddressCity: dealer.dealerLegalAddressCity,
        dealerLegalAddressZip: dealer.dealerLegalAddressZip,
        dealerLegalAddressProvince: dealer.dealerLegalAddressProvince,
        dealerLegalAddressCountry: dealer.dealerLegalAddressCountry,
        dealerOperatingAddressStreet: dealer.dealerOperatingAddressStreet,
        dealerOperatingAddressNumber: dealer.dealerOperatingAddressNumber,
        dealerOperatingAddressCity: dealer.dealerOperatingAddressCity,
        dealerOperatingAddressZip: dealer.dealerOperatingAddressZip,
        dealerOperatingAddressProvince: dealer.dealerOperatingAddressProvince,
        dealerOperatingAddressCountry: dealer.dealerOperatingAddressCountry
      } : {}
    });

    // Disabilita i campi Client e Brand in modalità modifica
    this.shipmentForm.get('waybill.waybillClientCode')?.disable();
    this.shipmentForm.get('waybill.waybillBrandCode')?.disable();

    // Clear existing parcels and add only the specific parcel being edited
    while (this.parcelsArray.length > 0) {
      this.parcelsArray.removeAt(0);
    }
    
    // Reset parcels collapsed array for single parcel (expanded)
    this.parcelsCollapsed = [false];

    // Add the specific parcel to edit - TUTTI I CAMPI DEL PARCEL
    this.parcelsArray.push(this.fb.group({
      // Primary identifiers
      parcelCode: [{ value: parcel.parcelCode || '', disabled: true }], // Disabilitato come waybillCode
      parcelWaybillCode: [parcel.parcelWaybillCode || ''],
      parcelPalletCode: [parcel.parcelPalletCode || ''],
      parcelShipmentCode: [parcel.parcelShipmentCode || ''],
      parcelBrandCode: [parcel.parcelBrandCode || 'DEFAULT'],
      parcelDealerCode: [parcel.parcelDealerCode || ''],
      parcelBarcode: [parcel.parcelBarcode || ''],
      parcelTrackingNumber: [parcel.parcelTrackingNumber || ''],
      parcelClientCode: [parcel.parcelClientCode || ''],
      
      // Dates
      parcelDate: [parcel.parcelDate || new Date().toISOString()],
      parcelDeliveryDate: [parcel.parcelDeliveryDate || ''],
      parcelEstimatedDeliveryDate: [parcel.parcelEstimatedDeliveryDate || ''],
      parcelOriginalDeliveryDate: [parcel.parcelOriginalDeliveryDate || ''],
      parcelLastEventDate: [parcel.parcelLastEventDate || ''],
      parcelTakeInChargeDate: [parcel.parcelTakeInChargeDate || ''],
      parcelLastUpdateAt: [parcel.parcelLastUpdateAt || ''],
      parcelUpdatedAt: [parcel.parcelUpdatedAt || new Date().toISOString()],
      parcelCreatedAt: [parcel.parcelCreatedAt || new Date().toISOString()],
      
      // Delivery information
      parcelDeliveryStatus: [parcel.parcelDeliveryStatus || ''],
      parcelDeliveryMethod: [parcel.parcelDeliveryMethod || ''],
      parcelDeliveryType: [parcel.parcelDeliveryType || ''],
      parcelDeliveryMaterial: [parcel.parcelDeliveryMaterial || ''],
      parcelDeliveryReason: [parcel.parcelDeliveryReason || ''],
      parcelTakeInChargeReason: [parcel.parcelTakeInChargeReason || ''],
      
      // Physical properties
      parcelDescription: [parcel.parcelDescription || '', [Validators.maxLength(40)]],
      parcelWeight: [parcel.parcelWeight, [this.weightVolumeValidator.bind(this)]],
      parcelVolume: [parcel.parcelVolume, [this.volumeValidator.bind(this)]],
      parcelLength: [parcel.parcelLength, [this.dimensionValidator.bind(this)]],
      parcelWidth: [parcel.parcelWidth, [this.dimensionValidator.bind(this)]],
      parcelHeight: [parcel.parcelHeight, [this.dimensionValidator.bind(this)]],
      parcelType: [parcel.parcelType || ''],
      
      // Location information
      parcelLocation: [parcel.parcelLocation || ''],
      parcelLatitude: [parcel.parcelLatitude],
      parcelLongitude: [parcel.parcelLongitude],
      parcelOriginalLatitude: [parcel.parcelOriginalLatitude],
      parcelOriginalLongitude: [parcel.parcelOriginalLongitude],
      
      // Special handling flags (Y/N strings from database)
      parcelFragile: [parcel.parcelFragile || 'N'],
      parcelHazardousMaterial: [parcel.parcelHazardousMaterial || 'N'],
      parcelTemperatureSensitive: [parcel.parcelTemperatureSensitive || 'N'],
      parcelHighValueItem: [parcel.parcelHighValueItem || 'N'],
      parcelRestrictedItem: [parcel.parcelRestrictedItem || 'N'],
      parcelCustomsClearanceRequired: [parcel.parcelCustomsClearanceRequired || 'N'],
      parcelDeliveryDateIsForced: [parcel.parcelDeliveryDateIsForced || 'N'],
      parcelDeliveryDateIsUpdated: [parcel.parcelDeliveryDateIsUpdated || 'N'],
      parcelDeliveryCoordinatesIsForced: [parcel.parcelDeliveryCoordinatesIsForced || 'N'],
      parcelDeliveryCoordinatesIsUpdated: [parcel.parcelDeliveryCoordinatesIsUpdated || 'N'],
      
      // Financial information
      parcelInsuredValue: [parcel.parcelInsuredValue, [this.insuredValueValidator.bind(this)]],
      
      // Status and metadata
      parcelStatus: [parcel.parcelStatus || ParcelStatus.ACTIVE],
      parcelDetailStatus: [parcel.parcelDetailStatus || ParcelStatus.PREPARED],
      parcelVersion: [parcel.parcelVersion || 1],
      parcelLastUpdateBy: [parcel.parcelLastUpdateBy || ''],
      parcelUpdatedBy: [parcel.parcelUpdatedBy || '']
    }));

    // Set dealer as selected if found
    if (dealer) {
      this.setDealerAsSelected(dealer);
    }

    // Abilita il campo warehouse se ci sono magazzini filtrati
    if (this.filteredWarehouses.length > 0) {
      this.shipmentForm.get('waybill.waybillOriginLocation')?.enable();
    }

    // Scroll to form and expand it
    this.isFormCollapsed = false;    
    
    // Store the parcel ID being edited and its waybill ID for update operations
    this.editingParcelId = parcel.parcelId ?? null;
    this.editingWaybillId = waybill.waybillId ?? null;
    
    // Force validation of all parcel fields after loading existing data
    this.parcelsArray.controls.forEach(parcelControl => {
      parcelControl.markAllAsTouched();
      parcelControl.updateValueAndValidity();
    });
  }

  isFieldInvalid(fieldPath: string): boolean {
    const field = this.shipmentForm.get(fieldPath);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(fieldPath: string): string {
    const field = this.shipmentForm.get(fieldPath);
    if (field && field.errors) {
      if (field.errors['required']) return this.translate.instant('COMMON.VALIDATION.REQUIRED');
      if (field.errors['weekend']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.DATE_NO_WEEKENDS');
      if (field.errors['invalidNumber']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.INVALID_NUMBER');
      if (field.errors['maxValue']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_VALUE_EXCEEDED');
      if (field.errors['maxValueInsured']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_VALUE_INSURED_EXCEEDED');
      if (field.errors['maxValueDimension']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_VALUE_DIMENSION_EXCEEDED');
      if (field.errors['minValue']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MIN_VALUE_REQUIRED');
      if (field.errors['minWeightVolume']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MIN_WEIGHT_VOLUME');
      if (field.errors['maxDecimals']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_DECIMALS_EXCEEDED');
      if (field.errors['maxDecimalsVolume']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_DECIMALS_VOLUME_EXCEEDED');
      if (field.errors['maxDecimalsInsured']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_DECIMALS_INSURED_EXCEEDED');
      if (field.errors['maxDecimalsDimension']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.MAX_DECIMALS_DIMENSION_EXCEEDED');
      if (field.errors['maxlength']) return this.translate.instant('SHIPMENT_REQUEST.VALIDATION.DESCRIPTION_MAX_LENGTH');
      if (field.errors['email']) return this.translate.instant('COMMON.VALIDATION.EMAIL_INVALID');
      if (field.errors['min']) return this.translate.instant('COMMON.VALIDATION.MIN_VALUE');
      if (field.errors['max']) return this.translate.instant('COMMON.VALIDATION.MAX_VALUE');
    }
    return '';
  }

  toggleFormCollapse(): void {
    this.isFormCollapsed = !this.isFormCollapsed;
  }

  /** Strumentario: alterna la vista compatta della tabella delle bolle/colli inviati */
  toggleCompactTable(): void {
    this.isCompactTable = !this.isCompactTable;
  }

  // ============================================================
  // SEZIONE CONVERSIONE EXCEL -> CSV
  // ============================================================

  /** Stringa per l'attributo accept dell'input file */
  get excelAcceptAttribute(): string {
    return this.excelAcceptedExtensions.join(',');
  }

  /** Apre il selettore file del sistema operativo */
  openExcelFilePicker(): void {
    this.excelFileInput?.nativeElement.click();
  }

  onExcelDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isExcelDragOver = true;
  }

  onExcelDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isExcelDragOver = false;
  }

  onExcelDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isExcelDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.processExcelFiles(Array.from(files));
    }
  }

  /** Selezione tramite pulsante "Scegli file" (selezione multipla ammessa) */
  onExcelFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.processExcelFiles(Array.from(input.files));
    }
    // Azzera il valore così ri-selezionando lo stesso file l'evento scatta di nuovo
    input.value = '';
  }

  /**
   * Converte in CSV tutti i file passati e li accoda a quelli già presenti.
   * SheetJS riconosce da solo il formato (xlsx/xlsm/xlsb/xls legacy/ods/csv),
   * quindi non serve distinguere la versione di Excel.
   */
  private async processExcelFiles(files: File[]): Promise<void> {
    this.excelErrorMessage = '';
    // L'esito dell'invio precedente non riguarda i nuovi file
    this.excelSendResult = null;
    this.isConvertingExcel = true;

    const maxBytes = this.excelMaxFileSizeMb * 1024 * 1024;
    const errors: string[] = [];
    let converted = 0;

    for (const file of files) {
      const extension = ('.' + (file.name.split('.').pop() || '')).toLowerCase();

      if (!this.excelAcceptedExtensions.includes(extension)) {
        errors.push(`"${file.name}": formato ${extension || 'sconosciuto'} non supportato`);
        continue;
      }

      if (file.size > maxBytes) {
        errors.push(`"${file.name}": supera il limite di ${this.excelMaxFileSizeMb} MB`);
        continue;
      }

      try {
        const workbook = await this.readWorkbook(file);

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          errors.push(`"${file.name}": nessun foglio di lavoro`);
          continue;
        }

        const selectedSheet = workbook.SheetNames[0];
        const parsed = this.sheetToParcelCsv(workbook, selectedSheet);
        this.excelFiles.push({
          id: ++this.excelFileSequence,
          name: file.name,
          size: file.size,
          sheetNames: workbook.SheetNames,
          selectedSheet,
          workbook,
          csv: parsed.csv,
          csvSourceRows: parsed.csvSourceRows,
          skippedRows: parsed.skippedRows
        });

        // Il file entra comunque in elenco anche senza righe collo: con più fogli
        // si può ancora cambiare foglio. La segnalazione è affidata alla card
        // rossa e all'avviso della sezione, non a excelErrorMessage
        converted++;
      } catch (error) {
        console.error(`Errore nella conversione di ${file.name}:`, error);
        errors.push(`"${file.name}": file illeggibile, corrotto o protetto da password`);
      }
    }

    this.rebuildMergedCsv();
    this.isConvertingExcel = false;

    if (errors.length) {
      this.excelErrorMessage = errors.join(' · ');
    }

    if (!this.excelCsvOutput && this.excelFiles.length) {
      // Nessun file ha righe collo: senza CSV il resto della sezione non compare,
      // quindi il motivo va detto esplicitamente
      this.notificationService.error(
        'Nessuna riga con 5C o 5B nella seconda colonna: nessun dato da inviare');
    } else if (converted > 0) {
      this.notificationService.success(converted === 1
        ? 'File convertito in CSV'
        : `${converted} file convertiti e accorpati in un unico CSV`);
    } else if (errors.length) {
      this.notificationService.error('Nessun file convertito');
    }
  }

  /** Legge un file come workbook SheetJS */
  private readWorkbook(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result as ArrayBuffer);
          // cellDates: le date restituite come oggetti Date invece che come seriali Excel
          resolve(XLSX.read(data, { type: 'array', cellDates: true }));
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Converte in CSV un foglio del workbook prelevando le sole righe collo:
   * viene presa solo la riga che ha "5C" o "5B" nella seconda colonna, tutte
   * le altre (intestazioni, chiusure, righe senza marcatore) vengono scartate.
   * Di ogni riga presa si conserva il numero di riga nel foglio, così una
   * segnalazione resta rintracciabile nel file originale.
   */
  private sheetToParcelCsv(workbook: XLSX.WorkBook, sheetName: string): {
    csv: string;
    csvSourceRows: number[];
    skippedRows: number;
  } {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      return { csv: '', csvSourceRows: [], skippedRows: 0 };
    }

    // blankrows: true tiene l'indice di riga allineato a quello del foglio
    const rawCsv = XLSX.utils.sheet_to_csv(sheet, {
      FS: ';',        // separatore campo: il punto e virgola è lo standard Excel italiano
      blankrows: true
    });

    // sheet_to_csv parte dalla prima riga dell'intervallo, non per forza dalla 1
    const firstSheetRow = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0;

    const keptLines: string[] = [];
    const csvSourceRows: number[] = [];
    let skippedRows = 0;

    rawCsv.split(/\r?\n/).forEach((line, index) => {
      const cells = line.split(';').map(cell => cell.trim());

      // Le righe del tutto vuote non sono dati mancanti: non vanno conteggiate
      if (!cells.some(cell => cell.length > 0)) {
        return;
      }

      if (!this.isParcelRow(cells)) {
        skippedRows++;
        return;
      }

      keptLines.push(line.trim());
      csvSourceRows.push(firstSheetRow + index + 1);
    });

    return { csv: keptLines.join('\n'), csvSourceRows, skippedRows };
  }

  /**
   * Accorpa i CSV dei singoli file in una sola stringa, nell'ordine di inserimento.
   * I file contengono già le sole righe collo, quindi qui si aggiungono solo gli
   * eventuali marcatori e si tiene traccia della provenienza di ogni riga.
   * - excelInsertFileMarkers: antepone a ogni blocco una riga marcatore che segnala
   *   dove finisce un file e inizia il successivo
   */
  private rebuildMergedCsv(): void {
    const total = this.excelFiles.length;
    const lines: string[] = [];
    const sources: (CsvRowSource | null)[] = [];

    this.excelFiles.forEach((file, index) => {
      if (!file.csv) {
        return;
      }

      // Il marcatore ha senso solo quando i file sono più di uno
      if (this.excelInsertFileMarkers && total > 1) {
        lines.push(this.buildExcelFileMarker(file, index, total));
        sources.push(null);
      }

      file.csv.split('\n').forEach((line, rowIndex) => {
        lines.push(line);
        sources.push({
          fileId: file.id,
          fileName: file.name,
          sheetRow: file.csvSourceRows[rowIndex] ?? rowIndex + 1
        });
      });
    });

    this.excelCsvOutput = lines.join('\n');
    this.csvRowSources = sources;
    this.buildCsvCheck();
  }

  /**
   * Riga marcatore di inizio file. Comincia con '#', convenzione riconosciuta
   * come commento dalla maggior parte dei parser CSV; i ';' nel nome del file
   * vengono sostituiti per non generare colonne spurie.
   */
  private buildExcelFileMarker(file: ConvertedExcelFile, index: number, total: number): string {
    const safeName = file.name.replace(/;/g, ',');
    const sheet = file.sheetNames.length > 1 ? ` - foglio: ${file.selectedSheet.replace(/;/g, ',')}` : '';
    return `#### FILE ${index + 1}/${total} - ${safeName}${sheet} ####`;
  }

  /** Attiva/disattiva i marcatori di interruzione tra un file e l'altro */
  toggleExcelFileMarkers(): void {
    this.excelInsertFileMarkers = !this.excelInsertFileMarkers;
    this.rebuildMergedCsv();
  }

  /** Cambio foglio di un file: riconverte quel file e ricompone la stringa unica */
  onExcelSheetChange(file: ConvertedExcelFile, sheetName: string): void {
    file.selectedSheet = sheetName;
    const parsed = this.sheetToParcelCsv(file.workbook, sheetName);
    file.csv = parsed.csv;
    file.csvSourceRows = parsed.csvSourceRows;
    file.skippedRows = parsed.skippedRows;
    this.rebuildMergedCsv();

    if (!parsed.csv) {
      this.notificationService.error(
        `Il foglio "${sheetName}" non ha righe con 5C o 5B nella seconda colonna`);
    }
  }

  /** Rimuove un file dall'elenco e ricompone la stringa unica */
  removeExcelFile(id: number): void {
    this.excelFiles = this.excelFiles.filter(file => file.id !== id);
    this.rebuildMergedCsv();
  }

  /** Righe del singolo file (utile nell'elenco) */
  getExcelFileRowCount(file: ConvertedExcelFile): number {
    return file.csv ? file.csv.split('\n').filter(row => row.length > 0).length : 0;
  }

  /** Dimensione complessiva dei file caricati */
  get excelTotalSize(): number {
    return this.excelFiles.reduce((total, file) => total + file.size, 0);
  }

  /** Lunghezza in caratteri della stringa CSV accorpata */
  get excelCsvLength(): number {
    return this.excelCsvOutput.length;
  }

  /** Percentuale di riempimento rispetto al limite dell'endpoint */
  get excelCsvLengthPercent(): number {
    return Math.min(100, (this.excelCsvLength / this.excelCsvMaxLength) * 100);
  }

  /** True oltre il limite accettato da /insert-data-by-file: l'invio va bloccato */
  get isExcelCsvTooLong(): boolean {
    return this.excelCsvLength > this.excelCsvMaxLength;
  }

  /** True sopra il 90% del limite: avviso preventivo */
  get isExcelCsvNearLimit(): boolean {
    return !this.isExcelCsvTooLong && this.excelCsvLengthPercent >= 90;
  }

  /**
   * File caricati che non hanno prodotto nessuna riga collo: il foglio scelto non
   * ha "5C" o "5B" nella seconda colonna.
   */
  get excelFilesWithoutParcelRows(): ConvertedExcelFile[] {
    return this.excelFiles.filter(file => !file.csv);
  }

  /** True quando ci sono file caricati ma nessuno ha righe collo utilizzabili */
  get hasNoParcelRows(): boolean {
    return this.excelFiles.length > 0 && !this.excelCsvOutput;
  }

  /** Nomi dei file senza righe collo, pronti per la segnalazione */
  get excelFileNamesWithoutParcelRows(): string {
    return this.excelFilesWithoutParcelRows.map(file => file.name).join(', ');
  }

  /** Numero di righe collo con almeno un dato obbligatorio mancante o a zero */
  get csvIssuesCount(): number {
    return this.csvCheck?.issues.length ?? 0;
  }

  /** True se il CSV contiene righe con errori: l'invio va bloccato */
  get hasCsvIssues(): boolean {
    return this.csvIssuesCount > 0;
  }

  trackByExcelFileId(_index: number, file: ConvertedExcelFile): number {
    return file.id;
  }

  /** Numero di righe dati della stringa CSV accorpata (i marcatori non contano) */
  get excelCsvRowCount(): number {
    if (!this.excelCsvOutput) {
      return 0;
    }
    return this.excelCsvOutput
      .split('\n')
      .filter(row => row.length > 0 && !row.startsWith('####'))
      .length;
  }

  /** Dimensione file leggibile (B/KB/MB) */
  formatExcelFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Copia la stringa CSV negli appunti */
  copyCsvToClipboard(): void {
    if (!this.excelCsvOutput) {
      return;
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.excelCsvOutput)
        .then(() => this.notificationService.success('CSV copiato negli appunti'))
        .catch(err => {
          console.error('Failed to copy CSV: ', err);
          this.notificationService.error('Impossibile copiare il CSV');
        });
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = this.excelCsvOutput;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      this.notificationService.success('CSV copiato negli appunti');
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
      this.notificationService.error('Impossibile copiare il CSV');
    }
    document.body.removeChild(textArea);
  }

  /** Scarica la stringa CSV come file */
  downloadCsv(): void {
    if (!this.excelCsvOutput) {
      return;
    }

    // BOM UTF-8: senza, Excel non interpreta correttamente gli accenti
    const blob = new Blob(['﻿' + this.excelCsvOutput], { type: 'text/csv;charset=utf-8;' });

    // Con un solo file il nome è quello del file, con più file un nome unico
    const fileName = this.excelFiles.length === 1
      ? `${this.excelFiles[0].name.replace(/\.[^.]+$/, '')}.csv`
      : `export_unificato_${this.excelFiles.length}_file.csv`;

    saveAs(blob, fileName);
  }

  /**
   * Invia la stringa CSV accorpata a /insert-data-by-file come testo puro.
   * Il backend accetta al massimo excelCsvMaxLength caratteri e richiede
   * il permesso CREATE sulla funzione "shipment-request".
   */
  sendCsvToBackend(): void {
    if (!this.excelCsvOutput || this.isSendingCsv) {
      return;
    }

    if (this.isExcelCsvTooLong) {
      this.notificationService.warning(
        `La stringa supera il limite di ${this.excelCsvMaxLength.toLocaleString('it-IT')} caratteri`);
      return;
    }

    // Righe con dati obbligatori mancanti: l'invio resta bloccato finché non si
    // correggono i file caricati
    if (this.hasCsvIssues) {
      this.notificationService.warning(
        this.csvIssuesCount === 1
          ? 'C\'è 1 riga con dati mancanti: correggi il file prima di inviare'
          : `Ci sono ${this.csvIssuesCount} righe con dati mancanti: correggi il file prima di inviare`);
      return;
    }

    this.isSendingCsv = true;
    this.excelSendResult = null;

    this.shipmentRequestService.insertDataByFile(this.excelCsvOutput)
      .subscribe({
        next: (response) => {
          this.isSendingCsv = false;
          this.notificationService.success(
            `Dati inviati: ${(response?.receivedCharacters ?? 0).toLocaleString('it-IT')} caratteri accettati`);

          // Invio riuscito: la sezione si svuota e resta il solo avviso di esito
          this.excelFiles = [];
          this.excelCsvOutput = '';
          this.csvRowSources = [];
          this.csvCheck = null;
          this.excelErrorMessage = '';
          this.excelSendResult = response;

          // Aggiorna tabella borderò e tabella dati con quanto appena inserito
          this.refreshTables();
        },
        error: (error) => {
          console.error('Errore invio dati a /insert-data-by-file:', error);
          this.isSendingCsv = false;

          if (error?.status === 401 || error?.status === 403) {
            this.notificationService.error('Non hai i permessi per inserire dati da file');
          } else if (error?.status === 400) {
            this.notificationService.error('Contenuto rifiutato dal server: vuoto o troppo lungo');
          } else {
            this.notificationService.error('Errore nell\'invio dei dati');
          }
        }
      });
  }

  /** Svuota la sezione e riparte da zero */
  clearExcelConversion(): void {
    this.excelSendResult = null;
    this.isSendingCsv = false;
    this.excelFiles = [];
    this.excelCsvOutput = '';
    this.csvRowSources = [];
    this.csvCheck = null;
    this.csvIssuesByFile = {};
    this.excelErrorMessage = '';
    this.isExcelDragOver = false;
  }

  /**
   * Avvia la guida interattiva
   */
  startTour(): void {
    this.tourGuideService.startTour(SHIPMENT_REQUEST_TOUR_CONFIG);
  }

  getCurrentParcelsWeight(): number {
    return this.parcelsArray.controls.reduce((total, control) => {
      return total + (control.get('parcelWeight')?.value || 0);
    }, 0);
  }

  getCurrentParcelsVolume(): number {
    return this.parcelsArray.controls.reduce((total, control) => {
      return total + (control.get('parcelVolume')?.value || 0);
    }, 0);
  }

  getCurrentParcelsInsuredValue(): number {
    return this.parcelsArray.controls.reduce((total, control) => {
      return total + (control.get('parcelInsuredValue')?.value || 0);
    }, 0);
  }

  // Performance optimization methods
  private needsRecalculation(): boolean {
    const currentState = {
      searchTerm: this.searchTerm,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      waybillsLength: this.waybills.length,
      parcelsLength: this.parcels.length,
      dealersLength: this.dealers.length
    };

    return JSON.stringify(currentState) !== JSON.stringify(this._lastCalculationState);
  }

  private updateCalculationState(): void {
    this._lastCalculationState = {
      searchTerm: this.searchTerm,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      waybillsLength: this.waybills.length,
      parcelsLength: this.parcels.length,
      dealersLength: this.dealers.length
    };
  }

  private calculateFilteredWaybills(): Waybill[] {
    let filteredData = [...this.waybills];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filteredData = filteredData.filter(waybill => {
        const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);
        return waybill.waybillCode?.toLowerCase().includes(searchLower) ||
          waybill.waybillShipmentCode?.toLowerCase().includes(searchLower) ||
          dealer?.dealerName?.toLowerCase().includes(searchLower) ||
          dealer?.dealerCode?.toLowerCase().includes(searchLower);
      });
    }

    // Apply sorting
    return this.sortData([...filteredData], true);
  }

  private calculateFilteredParcels(): any[] {
    let allParcels = this.getAllParcelsWithWaybillInfo();

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      allParcels = allParcels.filter((parcel: any) =>
        parcel.parcelCode?.toLowerCase().includes(searchLower) ||
        parcel.parcelBarcode?.toLowerCase().includes(searchLower) ||
        parcel.waybillCode?.toLowerCase().includes(searchLower) ||
        parcel.dealerName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    return this.sortData([...allParcels], false);
  }

  private invalidateAllCaches(): void {
    this._filteredWaybills = [];
    this._filteredParcels = [];
    this._parcelCountMap.clear();
    this._volumeValidationCache.clear();
    // Forza il ricalcolo al prossimo accesso
    this._lastCalculationState = {
      searchTerm: '',
      sortColumn: '',
      sortDirection: '',
      waybillsLength: 0,
      parcelsLength: 0,
      dealersLength: 0
    };
  }

  private rebuildParcelCountCache(): void {
    this._parcelCountMap.clear();
    
    this.parcels.forEach(parcel => {
      if (parcel.parcelWaybillCode) {
        const currentCount = this._parcelCountMap.get(parcel.parcelWaybillCode) || 0;
        this._parcelCountMap.set(parcel.parcelWaybillCode, currentCount + 1);
      }
    });
  }

  // Volume validation methods
  calculateVolumeFromDimensions(length: number, width: number, height: number): number {
    // Convert from cm to m³: (cm³ / 1,000,000)
    // Round to 4 decimal places for volume precision
    const volume = (length * width * height) / 1000000;
    return Math.round(volume * 10000) / 10000;
  }

  getParcelVolumeValidation(parcelIndex: number): any {
    const parcel = this.parcelsArray.at(parcelIndex);
    if (!parcel) return null;

    // Crea una chiave cache basata sui valori correnti
    const length = parseFloat(parcel.get('parcelLength')?.value) || 0;
    const width = parseFloat(parcel.get('parcelWidth')?.value) || 0;
    const height = parseFloat(parcel.get('parcelHeight')?.value) || 0;
    const volume = parseFloat(parcel.get('parcelVolume')?.value) || 0;
    
    const cacheKey = `${parcelIndex}-${length}-${width}-${height}-${volume}`;
    
    // Controlla se il risultato è già in cache
    if (this._volumeValidationCache.has(cacheKey)) {
      return this._volumeValidationCache.get(cacheKey);
    }

    // Calcola validazione
    const result = this.calculateVolumeValidation(length, width, height, volume);
    
    // Salva in cache
    this._volumeValidationCache.set(cacheKey, result);
    
    // Limita dimensione cache (mantieni solo gli ultimi 100 calcoli)
    if (this._volumeValidationCache.size > 100) {
      const firstKey = this._volumeValidationCache.keys().next().value;
      if (firstKey !== undefined) {
        this._volumeValidationCache.delete(firstKey);
      }
    }
    
    return result;
  }

  // Estrae la logica di calcolo in un metodo separato
  private calculateVolumeValidation(length: number, width: number, height: number, enteredVolume: number): any {
    // Round dimensions to 1 decimal place
    const roundedLength = Math.round(length * 10) / 10;
    const roundedWidth = Math.round(width * 10) / 10;
    const roundedHeight = Math.round(height * 10) / 10;
    const roundedVolume = Math.round(enteredVolume * 10000) / 10000;

    // Check if we have all three dimensions
    const hasDimensions = roundedLength > 0 && roundedWidth > 0 && roundedHeight > 0;
    const hasVolume = roundedVolume > 0;

    if (!hasDimensions && !hasVolume) {
      return null; // No validation needed
    }

    if (!hasDimensions && hasVolume) {
      return null; // Volume entered but no dimensions - no validation
    }

    const calculatedVolume = this.calculateVolumeFromDimensions(roundedLength, roundedWidth, roundedHeight);

    if (!hasVolume) {
      return {
        type: 'missing',
        calculatedVolume: calculatedVolume,
        message: 'SHIPMENT_REQUEST.VOLUME_VALIDATION.VOLUME_MISSING'
      };
    }

    // Has both dimensions and volume - compare them
    const tolerance = 0.0001;
    const volumeDifference = Math.abs(calculatedVolume - roundedVolume);
    
    if (volumeDifference <= tolerance) {
      return {
        type: 'match',
        calculatedVolume: calculatedVolume,
        enteredVolume: roundedVolume,
        message: 'SHIPMENT_REQUEST.VOLUME_VALIDATION.VOLUME_MATCH'
      };
    } else {
      return {
        type: 'mismatch',
        calculatedVolume: calculatedVolume,
        enteredVolume: roundedVolume,
        message: 'SHIPMENT_REQUEST.VOLUME_VALIDATION.VOLUME_MISMATCH'
      };
    }
  }

  useCalculatedVolume(parcelIndex: number): void {
    const validation = this.getParcelVolumeValidation(parcelIndex);
    if (validation && validation.calculatedVolume) {
      const parcel = this.parcelsArray.at(parcelIndex);
      // Set volume with 4 decimal places
      parcel.get('parcelVolume')?.setValue(validation.calculatedVolume.toFixed(4));
      
      // Clear the cache for this parcel to trigger re-validation
      this.volumeValidationCache.delete(parcelIndex);
    }
  }

  onDimensionChange(parcelIndex: number): void {
    // Rimuovi tutte le entry cache per questo parcel quando le dimensioni cambiano
    const keysToDelete = Array.from(this._volumeValidationCache.keys())
      .filter(key => key.startsWith(`${parcelIndex}-`));
    
    keysToDelete.forEach(key => this._volumeValidationCache.delete(key));
    
    // Clear anche la cache vecchia per compatibilità
    this.volumeValidationCache.delete(parcelIndex);
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'waybill' ? 'parcel' : 'waybill';
    
    // Set appropriate default sorting for each view mode
    if (this.viewMode === 'waybill') {
      this.sortColumn = 'waybillCreatedAt';
      this.sortDirection = 'desc';
    } else {
      this.sortColumn = 'parcelCreatedAt';
      this.sortDirection = 'desc';
    }
    
    // Reset to first page when switching view mode
    this.currentPage = 1;
  }

  toggleDisplayMode(): void {
    this.displayMode = this.displayMode === 'table' ? 'cards' : 'table';
  }

  // TrackBy functions per le performance
  trackWaybillById(index: number, waybill: Waybill): any {
    return waybill.waybillId || waybill.waybillCode || index;
  }

  trackParcelById(index: number, parcel: any): any {
    return parcel.parcelId || parcel.parcelCode || index;
  }

  trackParcelControlById(index: number, control: any): any {
    return control;
  }

  getAllParcelsWithWaybillInfo() {
    const allParcels: any[] = [];
    this.waybills.forEach(waybill => {
      const waybillParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);
      const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);

      waybillParcels.forEach(parcel => {
        allParcels.push({
          ...parcel,
          waybillCode: waybill.waybillCode,
          waybillDate: waybill.waybillDate,
          dealerName: dealer?.dealerName,
          dealerCity: dealer?.dealerOperatingAddressCity,
          dealerProvince: dealer?.dealerOperatingAddressProvince,
          waybillId: waybill.waybillId
        });
      });
    });    return allParcels;
  }
  getFilteredWaybills() {
    let filteredData = this.waybills;

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filteredData = filteredData.filter(waybill => {
        const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);
        return waybill.waybillCode?.toLowerCase().includes(searchLower) ||
          waybill.waybillShipmentCode?.toLowerCase().includes(searchLower) ||
          dealer?.dealerName?.toLowerCase().includes(searchLower) ||
          dealer?.dealerCode?.toLowerCase().includes(searchLower);
      });
    }

    // Apply sorting
    return this.sortData([...filteredData], true);
  }

  getFilteredParcels() {
    let allParcels = this.getAllParcelsWithWaybillInfo();

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      allParcels = allParcels.filter((parcel: any) =>
        parcel.parcelCode?.toLowerCase().includes(searchLower) ||
        parcel.parcelBarcode?.toLowerCase().includes(searchLower) ||
        parcel.waybillCode?.toLowerCase().includes(searchLower) ||
        parcel.dealerName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    return this.sortData([...allParcels], false);
  }  getSummary() {
    // I dati provengono dall'endpoint /summary del backend (riepilogo giornaliero)
    return {
      totalShipments: this.parcelsSummary.distinctWaybills,
      totalParcels: this.parcelsSummary.totalParcels,
      totalWeight: this.parcelsSummary.totalWeight,
      totalVolume: this.parcelsSummary.totalVolume,
      totalInsuredValue: this.parcelsSummary.totalInsuredValue
    };
  }
  // Helper methods for template
  getDealerByCode(dealerCode: string | undefined): Dealer | undefined {
    if (!dealerCode) return undefined;
    return this.dealers.find(d => d.dealerCode === dealerCode);
  }

  getParcelCountForWaybill(waybillCode: string | undefined): number {
    if (!waybillCode) return 0;
    
    // Ricalcola cache solo se i parcels sono cambiati
    if (this._parcelCountMap.size === 0 || this._lastCalculationState.parcelsLength !== this.parcels.length) {
      this.rebuildParcelCountCache();
    }
    
    return this._parcelCountMap.get(waybillCode) || 0;
  }

  getClientsBrandsSelected() : ClientsBrands[] {
    let clientsBrandsSelected: ClientsBrands[];
    if(this.filteredClients.length == 1) {
      this.clients = [...this.filteredClients];
    }
    if (this.clients.length > 0) {
      clientsBrandsSelected = this.clients.map(sc => {
        // trovo il client originale da clientsBrands
        const originalClient = this.clientsBrands.find(cb => cb.clientCode === sc.clientCode);


        var filteredBrandsFromClient: Brand[] = [];
        if(this.brands.length > 0) {
          // brands effettivi = intersezione tra quelli del client e quelli selezionati
          filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
            this.brands.some(sb => sb.brandCode === b.brandCode)
          );
        }
        else {
          filteredBrandsFromClient = this.filteredBrands;
        }


        return {
          clientCode: sc.clientCode,
          clientName: sc.clientName,
          brands: filteredBrandsFromClient
        } as ClientsBrands;
      });
    } else {
      clientsBrandsSelected = this.clientsBrands;
    }

    return clientsBrandsSelected;
   }

  // Service integration methods
  isBorderoWarehouseAllowed(group: BorderoGroup): boolean {
    if (this.userWarehouseCodes.length === 0) return true;
    return !!group.warehouseFrom && this.userWarehouseCodes.includes(group.warehouseFrom);
  }

  isBorderoDateToday(group: BorderoGroup): boolean {
    if (!group.date) return false;
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const parsed = new Date(group.date);
    if (!isNaN(parsed.getTime())) {
      const parsedStr = `${parsed.getFullYear()}-${String(parsed.getMonth()+1).padStart(2,'0')}-${String(parsed.getDate()).padStart(2,'0')}`;
      return parsedStr === todayStr;
    }
    return group.date === this.formatDateForDisplay(now);
  }

  onStampaBordero(group: BorderoGroup): void {
    if (!group.borderoCodice) {
      this.notificationService.error('Codice borderò non disponibile');
      return;
    }
    // console.log('[Borderò] Stampa borderò con codice:', group.borderoCodice);
    this.stampingBordero.add(group.borderoCodice);
    const clientsBrandsSelected = this.getClientsBrandsSelected();
    this.shipmentRequestService.printLoadingList(group.borderoCodice, clientsBrandsSelected)
      .pipe(
        catchError(_error => {
          // console.error('Errore stampa borderò:', _error);
          this.notificationService.error('Errore durante la stampa del borderò');
          return of(null);
        }),
        finalize(() => { this.stampingBordero.delete(group.borderoCodice!); })
      )
      .subscribe(blob => {
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ts = new Date();
          const timestamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}_${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
          a.download = `bordero_${group.borderoCodice}_${timestamp}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      });
  }

  /**
   * Chiave stabile del gruppo, usata per tracciare la generazione in corso:
   * il borderoCodice non esiste ancora finche' il borderò non e' stato creato.
   */
  borderoGroupKey(group: BorderoGroup): string {
    return [group.warehouseFrom, group.warehouseTo, group.date, group.clientZone].join('|');
  }

  isGeneratingBordero(group: BorderoGroup): boolean {
    return this.generatingBordero.has(this.borderoGroupKey(group));
  }

  onGeneraBordero(group: BorderoGroup): void {
    const key = this.borderoGroupKey(group);
    // Evita doppie generazioni su click ripetuti finche' la chiamata e' in corso.
    if (this.generatingBordero.has(key)) {
      return;
    }
    const warehouseFrom = group.warehouseFrom;
    const warehouseTo = group.warehouseTo;
    if (!warehouseFrom || !warehouseTo) {
      this.notificationService.error('Magazzino non disponibile per questo borderò');
      return;
    }
    const parcelDate = group.date;
    if (!parcelDate) {
      this.notificationService.error('Data non disponibile per questo borderò');
      return;
    }
    this.generatingBordero.add(key);
    const clientsBrandsSelected = this.getClientsBrandsSelected();
    const clientCodes = clientsBrandsSelected.map(cb => cb.clientCode);
    const brandCodes = clientsBrandsSelected.flatMap(cb => cb.brands.map((b: any) => b.brandCode));
    // La divisione per zone e la zona del gruppo vanno passate come sono state
    // usate per caricare la lista: il backend rilegge i colli e senza queste
    // prenderebbe anche quelli delle altre zone.
    this.shipmentRequestService.createLoadingListFromComParcelIds(
      warehouseFrom, warehouseTo, parcelDate, clientCodes, brandCodes,
      this.effectiveSplitBorderoByClientZones, group.clientZone)
      .pipe(
        catchError(_error => {
          // console.error('Errore generazione borderò:', _error);
          this.notificationService.error('Errore durante la generazione del borderò');
          return of(null);
        }),
        finalize(() => { this.generatingBordero.delete(key); })
      )
      .subscribe(response => {
        if (response) {
          // console.log('[Borderò] Generato:', response);
          this.notificationService.success('Borderò generato con successo');
          this.refreshTables();
        }
      });
  }

  /**
   * Ricarica dal backend i dati mostrati nelle due tabelle della pagina:
   * quella del borderò e quella di visualizzazione dati (bolle/colli).
   * Da chiamare alla risposta di ogni operazione che modifica i dati lato server.
   */
  private refreshTables(): void {
    this.loadWaybills();
    this.loadParcels(); // ricarica anche il riepilogo giornaliero
    this.loadBordero();
  }

  private loadBordero(): void {
    this.isBorderoLoading = true;
    const clientsBrandsSelected = this.getClientsBrandsSelected();
    this.shipmentRequestService.getLoadingList(clientsBrandsSelected, this.effectiveSplitBorderoByClientZones)
      .pipe(
        catchError(_error => {
          // console.error('Error loading borderò:', _error);
          return of([]);
        }),
        finalize(() => { this.isBorderoLoading = false; })
      )
      .subscribe(groups => {
        // console.log('[Borderò] dati ricevuti:', groups);
        this.borderoGrouped = groups.map(g => ({
          ...g,
          warehouseFromName: g.warehouseFromName
            ?? this.warehouses.find(w => w.warehouseCode === g.warehouseFrom)?.warehouseName
            ?? this.warehouses.find(w => w.warehouseCode === g.warehouseFrom)?.warehouseShortName
        }));
      });
  }

  private loadWaybills(): void {
    this.isLoading = true;    var clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();

    this.shipmentRequestService.getWaybills(clientsBrandsSelected)
      .pipe(catchError(_error => {
          // console.error('Error loading waybills:', _error);
          this.notificationService.error('Errore nel caricamento delle bolle');
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        }))
      .subscribe(waybills => {
        this.waybills = waybills;
        this.invalidateAllCaches(); // Invalida cache quando i dati cambiano
        this.clampCurrentPage();
      });
  }

  private loadParcels(): void {
    var clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();
    this.shipmentRequestService.getParcels(clientsBrandsSelected)
      .pipe(
        catchError(_error => {
          // console.error('Error loading parcels:', _error);
          return of([]);
        })
      )
      .subscribe(parcels => {
        this.parcels = parcels;
        this.invalidateAllCaches(); // Invalida cache quando i dati cambiano
        this.clampCurrentPage();
      });
    // Aggiorna il riepilogo giornaliero (card di Riepilogo) dal backend
    this.loadSummary(clientsBrandsSelected);
  }

  private loadSummary(clientsBrandsSelected?: ClientsBrands[]): void {
    const clients = clientsBrandsSelected ?? this.getClientsBrandsSelected();
    this.shipmentRequestService.getParcelsSummary(clients)
      .pipe(
        catchError(_error => {
          // In caso di errore azzera il riepilogo per evitare dati incoerenti
          return of<ParcelsSummary>({
            distinctWaybills: 0,
            totalParcels: 0,
            totalWeight: 0,
            totalVolume: 0,
            totalInsuredValue: 0
          });
        })
      )
      .subscribe(summary => {
        this.parcelsSummary = summary;
      });
  }

  // private loadBrands(): void {
  //   this.shipmentRequestService.getBrands()
  //     .pipe(
  //       catchError(error => {
  //         console.error('Error loading brands:', error);
  //         return of([]);
  //       })
  //     )
  //     .subscribe(brands => {
  //       this.brands = brands;
  //     });
  // }

  private loadDealers(): void {
    var clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();
    this.shipmentRequestService.getDealers(clientsBrandsSelected)
      .pipe(
        catchError(_error => {
          // console.error('Error loading dealers:', _error);
          return of([]);
        })
      )
      .subscribe(dealers => {
        this.dealers = dealers;
        this.invalidateAllCaches(); // Invalida cache quando i dati cambiano
        //console.log("Dealers ", dealers);
        // Update dealer count after loading dealers
        this.updateClientBrandSelectionState();
      });
  }

  private loadWarehouses(): void {
    var clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();
    console.log("[ShipmentRequest] Requesting warehouses for clients/brands:", clientsBrandsSelected);
    this.shipmentRequestService.getWarehouses(clientsBrandsSelected)
      .pipe(
        catchError(_error => {
          console.error("[ShipmentRequest] Error loading warehouses:", _error);
          return of([]);
        })
      )
      .subscribe(warehouses => {
        this.warehouses = warehouses;
        console.log("[ShipmentRequest] Warehouses loaded from backend:", warehouses);
        console.table((warehouses || []).map(w => ({
          code: w.warehouseCode,
          name: w.warehouseName,
          affiliateCode: w.warehouseAffiliateCode,
          affiliateType: w.warehouseAffiliateType,
          status: w.warehouseStatus
        })));

        // Arricchisce i gruppi borderò già caricati con il nome del magazzino origine
        // (gestisce la race condition in cui il borderò arriva prima dei magazzini)
        if (this.borderoGrouped.length > 0) {
          this.borderoGrouped = this.borderoGrouped.map(g => ({
            ...g,
            warehouseFromName: g.warehouseFromName
              ?? warehouses.find(w => w.warehouseCode === g.warehouseFrom)?.warehouseName
              ?? warehouses.find(w => w.warehouseCode === g.warehouseFrom)?.warehouseShortName
          }));
        }

        // Se c'è un solo cliente già selezionato, filtra e gestisci i magazzini automaticamente
        const selectedClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
        if (selectedClientCode) {
          this.filteredWarehouses = this.filterWarehousesByUserConfig(
            this.getWarehousesForClient(selectedClientCode)
          );

          const warehouseControl = this.shipmentForm.get('waybill.waybillOriginLocation');
          warehouseControl?.enable();

          // Preselezione magazzino tramite logica condivisa (singolo disponibile
          // o primo dei magazzini configurati nel profilo utente).
          warehouseControl?.setValue(this.pickDefaultWarehouseCode(this.filteredWarehouses));
        }
      });
  }

  // Autocomplete methods
  onDealerCodeInput(event: any): void {
    const value = event.target.value;

    // Se c'è un dealer selezionato e il valore è diverso, annulla la selezione
    if (this.isDealerSelected && this.selectedDealer && value !== this.selectedDealer.dealerCode) {
      this.clearDealerSelection();
      this.clearDealerFields();
    }

    // Ripulisci gli altri campi principali quando modifichi il codice dealer
    if (value && value.length >= 1) {
      this.clearOtherPrimaryFields('dealerCode');
      this.filterDealersByCode(value);
      this.showDealerCodeSuggestions = true;
    } else {
      this.showDealerCodeSuggestions = false;
      this.dealerCodeSuggestions = [];
    }
  }

  onDealerNameInput(event: any): void {
    const value = event.target.value;

    // Se c'è un dealer selezionato e il valore è diverso, annulla la selezione
    if (this.isDealerSelected && this.selectedDealer && value !== this.selectedDealer.dealerName) {
      this.clearDealerSelection();
      this.clearDealerFields();
    }

    // Ripulisci gli altri campi principali quando modifichi il nome dealer
    if (value && value.length >= 1) {
      this.clearOtherPrimaryFields('dealerName');
      this.filterDealersByName(value);
      this.showDealerNameSuggestions = true;
    } else {
      this.showDealerNameSuggestions = false;
      this.dealerNameSuggestions = [];
    }
  }

  onDealerVatInput(event: any): void {
    const value = event.target.value;

    // Se c'è un dealer selezionato e il valore è diverso, annulla la selezione
    if (this.isDealerSelected && this.selectedDealer && value !== this.selectedDealer.dealerVatNumber) {
      this.clearDealerSelection();
      this.clearDealerFields();
    }

    // Ripulisci gli altri campi principali quando modifichi la partita IVA
    if (value && value.length >= 1) {
      this.clearOtherPrimaryFields('dealerVatNumber');
      this.filterDealersByVat(value);
      this.showDealerVatSuggestions = true;
    } else {
      this.showDealerVatSuggestions = false;
      this.dealerVatSuggestions = [];
    }
  }
  private filterDealersByCode(query: string): void {
    // Check if client and brand are selected
    const clientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
    const brandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
    
    if (!clientCode || !brandCode) {
      this.dealerCodeSuggestions = [];
      return;
    }
    
    // Filter dealers based on selected client and brand
    const filteredDealers = this.getFilteredDealersByClientBrand(clientCode, brandCode);
    
    const lowerQuery = query.toLowerCase();
    this.dealerCodeSuggestions = filteredDealers.filter(dealer =>
      dealer.dealerCode?.toLowerCase().includes(lowerQuery)
    ); // No limit on suggestions
  }

  private filterDealersByName(query: string): void {
    // Check if client and brand are selected
    const clientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
    const brandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
    
    if (!clientCode || !brandCode) {
      this.dealerNameSuggestions = [];
      return;
    }
    
    // Filter dealers based on selected client and brand
    const filteredDealers = this.getFilteredDealersByClientBrand(clientCode, brandCode);
    
    const lowerQuery = query.toLowerCase();
    this.dealerNameSuggestions = filteredDealers.filter(dealer =>
      dealer.dealerName?.toLowerCase().includes(lowerQuery)
    ); // No limit on suggestions
  }

  private filterDealersByVat(query: string): void {
    // Check if client and brand are selected
    const clientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
    const brandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
    
    if (!clientCode || !brandCode) {
      this.dealerVatSuggestions = [];
      return;
    }
    
    // Filter dealers based on selected client and brand
    const filteredDealers = this.getFilteredDealersByClientBrand(clientCode, brandCode);
    
    const lowerQuery = query.toLowerCase();
    this.dealerVatSuggestions = filteredDealers.filter(dealer =>
      dealer.dealerVatNumber?.toLowerCase().includes(lowerQuery)
    ); // No limit on suggestions
  }

  /**
   * Filters dealers based on selected client and brand
   * This method ensures dealers are only shown if they are associated with the selected client and brand
   */
  private getFilteredDealersByClientBrand(clientCode: string, brandCode: string): Dealer[] {
    // Get the selected client's brands
    const selectedClientBrands = this.clientsBrands.find(cb => cb.clientCode === clientCode);
    
    if (!selectedClientBrands) {
      return [];
    }
    
    // Check if the selected brand exists for this client
    const brandExists = selectedClientBrands.brands.some(b => b.brandCode === brandCode);
    
    if (!brandExists) {
      return [];
    }
    
    // Filter dealers that match the client and brand combination
    return this.dealers.filter(dealer => {
      // Check if dealer is associated with the selected client and brand
      return dealer.dealerClientCode === clientCode && dealer.dealerBrandCode === brandCode;
    });
  }
  
  selectDealerFromCode(dealer: Dealer): void {
    this.populateDealerData(dealer);
    this.showDealerCodeSuggestions = false;
    this.setDealerAsSelected(dealer);
  }

  selectDealerFromName(dealer: Dealer): void {
    this.populateDealerData(dealer);
    this.showDealerNameSuggestions = false;
    this.setDealerAsSelected(dealer);
  }

  selectDealerFromVat(dealer: Dealer): void {
    this.populateDealerData(dealer);
    this.showDealerVatSuggestions = false;
    this.setDealerAsSelected(dealer);
  } private populateDealerData(dealer: Dealer): void {
    // Helper function to return value or "-" if empty/null/undefined
    const getValueOrDash = (value: any): string => {
      return (value && value.toString().trim() !== '') ? value : '-';
    };

    this.shipmentForm.patchValue({
      destination: {
        dealerCode: getValueOrDash(dealer.dealerCode),
        dealerName: getValueOrDash(dealer.dealerName), // Ragione Sociale
        dealerVatNumber: getValueOrDash(dealer.dealerVatNumber),
        dealerInternalBillingId: getValueOrDash(dealer.dealerInternalBillingId),
        dealerInternalId: getValueOrDash(dealer.dealerInternalId),
        dealerDeliveryZoneCode: getValueOrDash(dealer.dealerDeliveryZoneCode),
        dealerBillingZoneCode: getValueOrDash(dealer.dealerBillingZoneCode),
        dealerIndustry: getValueOrDash(dealer.dealerIndustry),
        dealerStatus: dealer.dealerStatus || DealerStatus.ACTIVE,

        // Contact information (these fields exist in form but might not have data from dealer)
        dealerOperatingPhone: '-',
        dealerOperatingEmail: '-',

        // Legal address
        dealerLegalAddressStreet: getValueOrDash(dealer.dealerLegalAddressStreet),
        dealerLegalAddressNumber: getValueOrDash(dealer.dealerLegalAddressNumber),
        dealerLegalAddressCity: getValueOrDash(dealer.dealerLegalAddressCity),
        dealerLegalAddressZip: getValueOrDash(dealer.dealerLegalAddressZip),
        dealerLegalAddressProvince: getValueOrDash(dealer.dealerLegalAddressProvince),
        dealerLegalAddressCountry: getValueOrDash(dealer.dealerLegalAddressCountry),

        // Operating address
        dealerOperatingAddressStreet: getValueOrDash(dealer.dealerOperatingAddressStreet),
        dealerOperatingAddressNumber: getValueOrDash(dealer.dealerOperatingAddressNumber),
        dealerOperatingAddressCity: getValueOrDash(dealer.dealerOperatingAddressCity),
        dealerOperatingAddressZip: getValueOrDash(dealer.dealerOperatingAddressZip),
        dealerOperatingAddressProvince: getValueOrDash(dealer.dealerOperatingAddressProvince),
        dealerOperatingAddressCountry: getValueOrDash(dealer.dealerOperatingAddressCountry)
      }
    });

    this.showSuccessMessage('Dati dealer caricati automaticamente');
    this.setDealerAsSelected(dealer);
  }

  hideSuggestions(field: string): void {
    // Use setTimeout to allow click events on suggestions to fire first
    setTimeout(() => {
      switch (field) {
        case 'code':
          this.showDealerCodeSuggestions = false;
          break;
        case 'name':
          this.showDealerNameSuggestions = false;
          break;
        case 'vat':
          this.showDealerVatSuggestions = false;
          break;
      }
    }, 200);
  }
  public showSuccessMessage(message: string): void {
    this.notificationService.success(message);
  }

  public showErrorMessage(message: string): void {
    this.notificationService.error(message);
  }

  /**
   * Show a warning message using NotificationService
   */
  public showWarningMessage(message: string): void {
    this.notificationService.warning(message);
  }
  
  private setDealerAsSelected(dealer: Dealer): void {
    this.isDealerSelected = true;
    this.selectedDealer = dealer;
    this.disableSecondaryDealerFields();
    //this.showSuccessMessage('Dealer selezionato. I campi sono ora protetti dalle modifiche.');
  } clearDealerSelection(): void {
    this.isDealerSelected = false;
    this.selectedDealer = null;
    this.enableAllDealerFields();
    this.showWarningMessage('Selezione dealer annullata. Puoi ora modificare i campi liberamente.');
  }

  private disableSecondaryDealerFields(): void {
    const destination = this.shipmentForm.get('destination');
    if (destination) {
      // Lista dei campi secondari da disabilitare
      const secondaryFields = [
        'dealerOperatingPhone',
        'dealerOperatingEmail',
        'dealerOperatingAddressStreet',
        'dealerOperatingAddressNumber',
        'dealerOperatingAddressZip',
        'dealerOperatingAddressCity',
        'dealerOperatingAddressProvince',
        'dealerOperatingAddressCountry'
      ];

      secondaryFields.forEach(fieldName => {
        const control = destination.get(fieldName);
        if (control) {
          control.disable();
        }
      });
    }
  }

  private enableAllDealerFields(): void {
    const destination = this.shipmentForm.get('destination');
    if (destination) {
      // Lista di tutti i campi del dealer
      const allFields = [
        'dealerCode',
        'dealerName',
        'dealerVatNumber',
        'dealerOperatingPhone',
        'dealerOperatingEmail',
        'dealerOperatingAddressStreet',
        'dealerOperatingAddressNumber',
        'dealerOperatingAddressZip',
        'dealerOperatingAddressCity',
        'dealerOperatingAddressProvince',
        'dealerOperatingAddressCountry'
      ];

      allFields.forEach(fieldName => {
        const control = destination.get(fieldName);
        if (control) {
          control.enable();
        }
      });
    }
  }
  private clearDealerFields(): void {
    // Pulisce tutti i campi del dealer tranne i tre principali che l'utente sta modificando
    const currentValues = this.shipmentForm.get('destination')?.value;

    this.shipmentForm.patchValue({
      destination: {
        // Manteniamo i valori attuali per i campi principali
        dealerCode: currentValues?.dealerCode || '',
        dealerName: currentValues?.dealerName || '',
        dealerVatNumber: currentValues?.dealerVatNumber || '',

        // Puliamo tutti gli altri campi
        dealerInternalBillingId: '',
        dealerInternalId: '',
        dealerDeliveryZoneCode: '',
        dealerBillingZoneCode: '',
        dealerIndustry: '',
        dealerStatus: DealerStatus.ACTIVE,
        dealerOperatingPhone: '',
        dealerOperatingEmail: '',

        // Legal address
        dealerLegalAddressStreet: '',
        dealerLegalAddressNumber: '',
        dealerLegalAddressCity: '',
        dealerLegalAddressZip: '',
        dealerLegalAddressProvince: '',
        dealerLegalAddressCountry: '',

        // Operating address
        dealerOperatingAddressStreet: '',
        dealerOperatingAddressNumber: '',
        dealerOperatingAddressCity: '',
        dealerOperatingAddressZip: '',
        dealerOperatingAddressProvince: '',
        dealerOperatingAddressCountry: ''
      }
    });
  }

  private clearOtherPrimaryFields(keepField: string): void {
    // Pulisce gli altri due campi principali mantenendo solo quello specificato
    const currentValues = this.shipmentForm.get('destination')?.value;

    const updateData: any = {};

    // Mantiene solo il campo specificato, pulisce gli altri due
    switch (keepField) {
      case 'dealerCode':
        updateData.dealerName = '';
        updateData.dealerVatNumber = '';
        break;
      case 'dealerName':
        updateData.dealerCode = '';
        updateData.dealerVatNumber = '';
        break;
      case 'dealerVatNumber':
        updateData.dealerCode = '';
        updateData.dealerName = '';
        break;
    }

    this.shipmentForm.patchValue({
      destination: updateData
    });
  }  // Metodo per verificare se un campo è disabilitato (per le classi CSS e le icone)
  isDealerFieldDisabled(fieldName: string): boolean {
    const control = this.shipmentForm.get(`destination.${fieldName}`);
    return control ? control.disabled : false;
  }

  // Dropdown management methods for action buttons
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Se il clic non è su un dropdown o sui suoi elementi, chiudi tutti i dropdown
    if (!target.closest('.dropdown')) {
      this.closeAllDropdowns();
    }

    // Chiudi il popup informativo PENDING se il clic è fuori da esso
    if (!target.closest('.pending-info-wrapper')) {
      this.closePendingInfo();
    }
  }

  toggleDropdown(itemId: string, event: Event): void {
    event.stopPropagation();
    
    // Chiudi tutti gli altri dropdown
    this.closeAllDropdowns();
    
    // Toggle del dropdown corrente
    if (this.openDropdowns.has(itemId)) {
      this.openDropdowns.delete(itemId);    } else {
      this.openDropdowns.add(itemId);
    }
  }
  // Download methods for documents
  downloadLabels(waybillId: number): void {
    
    // Find the waybill
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
    if (!waybill) {
      this.showErrorMessage('Bolla non trovata');
      return;
    }

    // Get parcels for this waybill
    const waybillParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);
    
    if (waybillParcels.length === 0) {
      this.showErrorMessage('Nessun collo trovato per questa bolla');
      return;
    }

    // Get dealer information for the label
    const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);
    
    // Generate labels using the label generation service
    this.labelGenerationService.generateWaybillLabels(waybillParcels, waybill, dealer)
      .subscribe({        next: (success) => {
          if (success) {
            this.notificationService.success(`Etichette generate per bolla ${waybill.waybillCode} (${waybillParcels.length} colli)`);
          } else {
            this.showErrorMessage('Errore nella generazione delle etichette');
          }
        },
        error: (_error) => {
          // console.error('Error generating labels:', _error);
          this.showErrorMessage('Errore nella generazione delle etichette');
        }      });
  }
    downloadWaybillDocument(waybillId: number): void {
    // console.log('Downloading waybill document for waybill:', waybillId);
    
    // Find the waybill
    const waybill = this.waybills.find(w => w.waybillId === waybillId);
    if (!waybill) {
      this.showErrorMessage('Bolla non trovata');
      return;
    }

    // Use the WaybillService to download PDF from backend
    this.waybillService.downloadPdf(waybill.waybillCode, waybill.waybillShipmentCode);
      // Show success message
    this.notificationService.success(`Download PDF avviato per bolla ${waybill.waybillCode}`);
  }

  downloadParcelLabels(parcelId: number): void {
    // console.log('Downloading labels for parcel:', parcelId);
    // TODO: Implementare il download delle etichette del collo
    // Questo metodo verrà implementato quando avremo il servizio per generare i documenti
    this.notificationService.success('Download etichette avviato per collo ID: ' + parcelId);
  }

  downloadParcelDocument(parcelId: number): void {
    // console.log('Downloading document for parcel:', parcelId);
    // TODO: Implementare il download del documento del collo
    // Questo metodo verrà implementato quando avremo il servizio per generare i documenti
    this.notificationService.success('Download documento avviato per collo ID: ' + parcelId);
  }

  downloadBorderoForWaybill(waybill: Waybill): void {
    this.printBorderoByCode(waybill.waybillLoadingList);
  }

  downloadBorderoForParcel(parcel: Parcel): void {
    this.printBorderoByCode(parcel.parcelLoadingList);
  }

  /**
   * Stampa il borderò dato il `loadingListCode`. Riproduce il comportamento
   * di `onStampaBordero` (sezione Borderò) per le righe delle tabelle
   * "Bolle Inserite" e "Colli".
   */
  private printBorderoByCode(code: string | null | undefined): void {
    if (!code) {
      this.notificationService.error('Codice borderò non disponibile');
      return;
    }
    this.stampingBordero.add(code);
    const clientsBrandsSelected = this.getClientsBrandsSelected();
    this.shipmentRequestService.printLoadingList(code, clientsBrandsSelected)
      .pipe(
        catchError(_error => {
          this.notificationService.error('Errore durante la stampa del borderò');
          return of(null);
        }),
        finalize(() => { this.stampingBordero.delete(code); })
      )
      .subscribe(blob => {
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ts = new Date();
          const timestamp = `${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, '0')}${String(ts.getDate()).padStart(2, '0')}_${String(ts.getHours()).padStart(2, '0')}${String(ts.getMinutes()).padStart(2, '0')}${String(ts.getSeconds()).padStart(2, '0')}`;
          a.download = `bordero_${code}_${timestamp}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      });
  }

  closeAllDropdowns(): void {
    this.openDropdowns.clear();
  }

  scrollToTopSmooth(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  isDropdownOpen(itemId: string): boolean {
    return this.openDropdowns.has(itemId);
  }

  /**
   * Forza il valore del campo Codice Bolla in maiuscolo mentre l'utente digita,
   * preservando la posizione del cursore.
   */
  onWaybillCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upper = input.value.toUpperCase();

    const control = this.shipmentForm.get('waybill.waybillCode');
    if (control && control.value !== upper) {
      control.setValue(upper);
    }
    // Ripristina la posizione del cursore dopo l'aggiornamento del valore
    input.value = upper;
    input.setSelectionRange(start, end);
  }

  /**
   * Apre/chiude il popup informativo per una bolla in stato PENDING
   */
  togglePendingInfo(waybillId: number, event: Event): void {
    event.stopPropagation();
    this.pendingInfoWaybillId = this.pendingInfoWaybillId === waybillId ? null : waybillId;
  }

  /**
   * Chiude il popup informativo PENDING
   */
  closePendingInfo(): void {
    this.pendingInfoWaybillId = null;
  }

  // Sorting functionality
  sortBy(column: string): void {
    if (this.sortColumn === column) {
      // Same column clicked - cycle through sort states
      if (this.sortDirection === '') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortDirection = 'asc';
      } else {
        this.sortDirection = '';
        this.sortColumn = '';
      }
    } else {
      // Different column clicked - start with descending
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'fas fa-sort text-muted';
    }
    
    if (this.sortDirection === 'desc') {
      return 'fas fa-sort-down text-primary';
    } else if (this.sortDirection === 'asc') {
      return 'fas fa-sort-up text-primary';
    } else {
      return 'fas fa-sort text-muted';
    }
  }

  private sortData(data: any[], isWaybillData: boolean = true): any[] {
    if (!this.sortColumn || !this.sortDirection) {
      return data;
    }

    return data.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      // Get values based on column and data type
      if (isWaybillData) {
        valueA = this.getWaybillSortValue(a, this.sortColumn);
        valueB = this.getWaybillSortValue(b, this.sortColumn);
      } else {
        valueA = this.getParcelSortValue(a, this.sortColumn);
        valueB = this.getParcelSortValue(b, this.sortColumn);
      }

      // Handle null/undefined values
      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return this.sortDirection === 'asc' ? -1 : 1;
      if (valueB == null) return this.sortDirection === 'asc' ? 1 : -1;

      // Compare values
      let comparison = 0;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
      } else {
        comparison = String(valueA).localeCompare(String(valueB));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  private getWaybillSortValue(waybill: any, column: string): any {
    const dealer = this.dealers.find(d => d.dealerCode === waybill.waybillDealerCode);
    
    switch (column) {
      case 'index':
        return this.waybills.indexOf(waybill) + 1;
      case 'waybillCode':
        return waybill.waybillCode;
      case 'waybillDate':
        // Convert date string to Date object for proper sorting
        return waybill.waybillDate ? new Date(waybill.waybillDate).getTime() : 0;
      case 'waybillCreatedAt':
        // Convert createdAt timestamp to Date object for proper sorting
        return waybill.waybillCreatedAt ? new Date(waybill.waybillCreatedAt).getTime() : 0;
      case 'companyName':
        return dealer?.dealerName || '';
      case 'address':
        return dealer ? `${dealer.dealerOperatingAddressStreet} ${dealer.dealerOperatingAddressNumber}` : '';
      case 'city':
        return dealer?.dealerOperatingAddressCity || '';
      case 'province':
        return dealer?.dealerOperatingAddressProvince || '';
      case 'parcelsCount':
        return this.getParcelCountForWaybill(waybill.waybillCode);
      case 'status':
        return waybill.waybillStatus;
      case 'bordero':
        return waybill.waybillLoadingList || '';
      default:
        return '';
    }
  }

  private getParcelSortValue(parcel: any, column: string): any {
    const dealer = this.dealers.find(d => d.dealerCode === parcel.parcelDealerCode);
    
    switch (column) {
      case 'index':
        return this.getAllParcelsWithWaybillInfo().indexOf(parcel) + 1;
      case 'parcelCode':
        return parcel.parcelCode || '';
      case 'parcelDate':
        // Convert date string to Date object for proper sorting
        return parcel.parcelDate ? new Date(parcel.parcelDate).getTime() : 0;
      case 'parcelCreatedAt':
        // Convert createdAt timestamp to Date object for proper sorting
        return parcel.parcelCreatedAt ? new Date(parcel.parcelCreatedAt).getTime() : 0;
      case 'waybillCode':
        return parcel.waybillCode;
      case 'companyName':
        return dealer?.dealerName || parcel.dealerName || '';
      case 'address':
        return dealer ? `${dealer.dealerOperatingAddressStreet} ${dealer.dealerOperatingAddressNumber}` : '';
      case 'city':
        return dealer?.dealerOperatingAddressCity || '';
      case 'province':
        return dealer?.dealerOperatingAddressProvince || '';
      case 'weight':
        return parseFloat(parcel.parcelWeight) || 0;
      case 'volume':
        return parseFloat(parcel.parcelVolume) || 0;      case 'insuredValue':
        return parseFloat(parcel.parcelInsuredValue) || 0;
      case 'bordero':
        return parcel.parcelLoadingList || '';
      default:
        return '';
    }
  }  // Pagination methods
  setItemsPerPage(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1; // Reset alla prima pagina quando cambi il numero di elementi
    localStorage.setItem('shipmentRequestItemsPerPage', this.itemsPerPage.toString());
    localStorage.setItem('shipmentRequestCurrentPage', '1');
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    localStorage.setItem('shipmentRequestCurrentPage', page.toString());
  }

  /**
   * Riporta la pagina corrente dentro l'intervallo valido dopo un ricaricamento
   * dati: senza questo, se i nuovi dati sono meno di prima, la pagina memorizzata
   * resterebbe fuori range e la tabella risulterebbe vuota.
   */
  private clampCurrentPage(): void {
    const lastPage = this.getTotalPages();
    if (this.currentPage > lastPage) {
      this.onPageChange(lastPage);
    }
  }
  getTotalPages(): number {
    // Almeno 1: la barra di paginazione è sempre visibile, anche a lista vuota,
    // quindi serve una pagina "1" da mostrare e su cui disabilitare le frecce.
    return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const pages: number[] = [];
    const maxPages = 5; // Maximum number of page numbers to show
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // TrackBy functions for better performance
  trackByPageNumber(index: number, page: number): number {
    return page;
  }
  // Excel Export Methods
  exportToExcel(): void {
    // Disable the button immediately
    this.isExportingExcel = true;
    
    if (this.viewMode === 'waybill') {
      this.exportWaybillsToExcel();
    } else {
      this.exportParcelsToExcel();
    }
    
    // Re-enable the button after 5 seconds
    setTimeout(() => {
      this.isExportingExcel = false;
    }, 5000);
  }
  private exportWaybillsToExcel(): void {
    const filteredWaybills = this.filteredWaybills;
    
    // Prepare data for Excel
    const excelData = filteredWaybills.map((waybill, index) => {
      const dealer = this.getDealerByCode(waybill.waybillDealerCode);
      // Convert date to Europe/Rome timezone (UTC+1/UTC+2 with DST)
      // JS Date is always local, so we need to format as Europe/Rome
      // For Excel, best is to use toLocaleString with 'it-IT' and 'Europe/Rome' timeZone
      return {
        '#': index + 1,
        'Codice Bolla': waybill.waybillCode || '-',
        'Data/Ora': waybill.waybillDate
          ? new Date(waybill.waybillDate).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
          : '-',
        'Ragione Sociale': dealer?.dealerName || '-',
        'Indirizzo': dealer ? `${dealer.dealerOperatingAddressStreet} ${dealer.dealerOperatingAddressNumber}` : '-',
        'Città': dealer?.dealerOperatingAddressCity || '-',
        'Provincia': dealer?.dealerOperatingAddressProvince || '-',
        'CAP': dealer?.dealerOperatingAddressZip || '-',
        'Paese': dealer?.dealerOperatingAddressCountry || '-',
        'Numero Colli': this.getParcelCountForWaybill(waybill.waybillCode),
        'Stato': waybill.waybillStatus || '-',
        'Brand': waybill.waybillBrandCode || '-',
        'Note': waybill.waybillNote || '-',
        'Peso Totale (kg)': waybill.waybillTotalWeight || 0,
        'Volume Totale (m³)': waybill.waybillTotalVolume || 0,
        'Valore Assicurato (€)': this.getTotalInsuredValueForWaybill(waybill.waybillCode),
        'Codice Dealer': waybill.waybillDealerCode || '-',
        'P.IVA': dealer?.dealerVatNumber || '-'
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bolle');

    // Auto-size columns
    const colWidths = Object.keys(excelData[0] || {}).map(key => {
      const maxLength = Math.max(
        key.length,
        ...excelData.map(row => String(row[key as keyof typeof row]).length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `Bolle_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
    
    // Show success message
    this.notificationService.success(`File Excel esportato: ${filename}`);
  }

  private exportParcelsToExcel(): void {
    const filteredParcels = this.filteredParcels;
    
    // Prepare data for Excel
    const excelData = filteredParcels.map((parcel, index) => {
      const dealer = this.dealers.find(d => d.dealerCode === parcel.parcelDealerCode);
      const waybill = this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode);
      
      return {
        '#': index + 1,
        'Codice Collo': parcel.parcelCode || '-',
        'Data/Ora': parcel.parcelDate
          ? new Date(parcel.parcelDate).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
          : '-',
        'Codice Bolla': parcel.parcelWaybillCode || '-',
        'Ragione Sociale': dealer?.dealerName || '-',
        'Indirizzo': dealer ? `${dealer.dealerOperatingAddressStreet} ${dealer.dealerOperatingAddressNumber}` : '-',
        'Città': dealer?.dealerOperatingAddressCity || '-',
        'Provincia': dealer?.dealerOperatingAddressProvince || '-',
        'CAP': dealer?.dealerOperatingAddressZip || '-',
        'Paese': dealer?.dealerOperatingAddressCountry || '-',
        'Peso (kg)': parcel.parcelWeight || 0,
        'Volume (m³)': parcel.parcelVolume || 0,
        'Lunghezza (cm)': parcel.parcelLength || 0,
        'Larghezza (cm)': parcel.parcelWidth || 0,
        'Altezza (cm)': parcel.parcelHeight || 0,
        'Valore Assicurato (€)': parcel.parcelInsuredValue || 0,
        'Descrizione': parcel.parcelDescription || '-',
        'Stato': parcel.parcelStatus || '-',
        'Codice Dealer': parcel.parcelDealerCode || '-',
        'P.IVA': dealer?.dealerVatNumber || '-',
        'Fragile': parcel.parcelFragile === 'Y' ? 'Sì' : 'No',
        'Materiale Pericoloso': parcel.parcelHazardousMaterial === 'Y' ? 'Sì' : 'No',
        'Sensibile Temperatura': parcel.parcelTemperatureSensitive === 'Y' ? 'Sì' : 'No',
        'Alto Valore': parcel.parcelHighValueItem === 'Y' ? 'Sì' : 'No'
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Colli');

    // Auto-size columns
    const colWidths = Object.keys(excelData[0] || {}).map(key => {
      const maxLength = Math.max(
        key.length,
        ...excelData.map(row => String(row[key as keyof typeof row]).length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `Colli_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
    
    // Show success message
    this.notificationService.success(`File Excel esportato: ${filename}`);
  }

  private getTotalInsuredValueForWaybill(waybillCode: string | undefined): number {
    if (!waybillCode) return 0;
    return this.parcels
      .filter(p => p.parcelWaybillCode === waybillCode)
      .reduce((total, p) => total + (p.parcelInsuredValue || 0), 0);
  }  // Proprietà per distinguere tra modalità inserimento e modifica
  isEditingDealer: boolean = false;

  // Metodo per modificare il dealer selezionato
  modifyDealer(): void {
    if (this.isDealerSelected && this.selectedDealer) {
      // Imposta la modalità modifica
      this.isEditingDealer = true;
      
      // Inizializza il form del dealer con i dati esistenti
      this.dealerForm = this.createDealerForm();        // Popola il form con i dati del dealer selezionato
      this.dealerForm.patchValue({
        dealerCode: this.selectedDealer.dealerCode || '',
        dealerClientCode: this.selectedDealer.dealerClientCode || '',
        dealerBrandCode: this.selectedDealer.dealerBrandCode || '',
        code: this.selectedDealer.dealerCode || '',
        name: this.selectedDealer.dealerName || '',
        vatNumber: this.selectedDealer.dealerVatNumber || '',
        internalBillingId: this.selectedDealer.dealerInternalBillingId || '',
        dealerInternalId: this.selectedDealer.dealerInternalId || '',
        sdiCode: this.selectedDealer.dealerSdiCode || '',
        industry: this.selectedDealer.dealerIndustry || '',
        status: this.selectedDealer.dealerStatus || DealerStatus.ACTIVE,
        notes: this.selectedDealer.dealerNotes || '',
        
        // Legal address
        legalAddress: {
          street: this.selectedDealer.dealerLegalAddressStreet || '',
          number: this.selectedDealer.dealerLegalAddressNumber || '',
          zip: this.selectedDealer.dealerLegalAddressZip || '',
          city: this.selectedDealer.dealerLegalAddressCity || '',
          province: this.selectedDealer.dealerLegalAddressProvince || '',
          country: this.selectedDealer.dealerLegalAddressCountry || 'IT'
        },
        
        // Operating address
        operatingAddress: {
          street: this.selectedDealer.dealerOperatingAddressStreet || '',
          number: this.selectedDealer.dealerOperatingAddressNumber || '',
          zip: this.selectedDealer.dealerOperatingAddressZip || '',
          city: this.selectedDealer.dealerOperatingAddressCity || '',
          province: this.selectedDealer.dealerOperatingAddressProvince || '',
          country: this.selectedDealer.dealerOperatingAddressCountry || 'IT'
        },
        
        // Coordinates (se disponibili nel dealer)
        latitude: null,
        longitude: null
      });
      
      // Apri il popup in modalità modifica
      this.showDealerInsertPopup = true;
      
      this.showSuccessMessage('Modalità modifica dealer attivata. Modifica i dati e salva per aggiornare.');
    }
  }// Metodo per inserire un nuovo dealer
  insertDealer(): void {
    // Inizializza il form del dealer
    this.dealerForm = this.createDealerForm();
    
    // Imposta la modalità inserimento
    this.isEditingDealer = false;
    
    // Apri il popup
    this.showDealerInsertPopup = true;
  }

  // ===== Dealer Management Popup =====
  openDealerCreatePopup(): void {
    this.dealerManagementMode = 'create';
    this.dealerManagementInitial = null;
    this.showDealerManagementPopup = true;
  }

  openDealerEditPopup(): void {
    if (!this.isDealerSelected || !this.selectedDealer) {
      this.showErrorMessage('Seleziona un dealer da modificare');
      return;
    }
    this.dealerManagementMode = 'edit';
    // Adatta il Dealer del service all'interfaccia anagraphic richiesta dal popup
    this.dealerManagementInitial = {
      ...this.selectedDealer,
      dealerStatus: this.selectedDealer.dealerStatus ?? DealerStatus.ACTIVE,
      dealerVersion: this.selectedDealer.dealerVersion ?? 0,
      dealerCreatedAt: this.selectedDealer.dealerCreatedAt ?? new Date().toISOString()
    };
    this.showDealerManagementPopup = true;
  }

  closeDealerManagementPopup(): void {
    this.showDealerManagementPopup = false;
    this.dealerManagementInitial = null;
  }

  onDealerManagementSaved(dealer: Dealer): void {
    // Patch del form di spedizione con i dati del dealer salvato
    this.shipmentForm.patchValue({
      destination: {
        dealerCode: dealer.dealerCode,
        dealerName: dealer.dealerName,
        dealerVatNumber: dealer.dealerVatNumber,
        dealerInternalBillingId: dealer.dealerInternalBillingId,
        dealerInternalId: dealer.dealerInternalId,
        dealerOperatingPhone: (dealer as any).dealerOperatingPhone || '',
        dealerOperatingEmail: (dealer as any).dealerOperatingEmail || '',
        dealerOperatingAddressStreet: dealer.dealerOperatingAddressStreet || '',
        dealerOperatingAddressNumber: dealer.dealerOperatingAddressNumber || '',
        dealerOperatingAddressCity: dealer.dealerOperatingAddressCity || '',
        dealerOperatingAddressZip: dealer.dealerOperatingAddressZip || '',
        dealerOperatingAddressProvince: dealer.dealerOperatingAddressProvince || '',
        dealerOperatingAddressCountry: dealer.dealerOperatingAddressCountry || ''
      }
    });

    // Aggiorna selezione corrente e ricarica anagrafica dealer
    this.selectedDealer = dealer as unknown as Dealer;
    this.isDealerSelected = true;
    this.loadDealers();

    this.closeDealerManagementPopup();
  }

  // Metodi per gestire il popup del dealer
  closeDealerInsertPopup(): void {
    this.showDealerInsertPopup = false;
    this.dealerForm = null;
    this.isEditingDealer = false;
  }

  onDealerFormSubmit(): void {
    if (!this.dealerForm || this.dealerForm.invalid) {
      this.markFormGroupTouched(this.dealerForm || this.fb.group({}));
      this.showErrorMessage('Compila tutti i campi obbligatori del dealer');
      return;
    }

    const dealerData = this.dealerForm.getRawValue();

    const dealer = {
      dealerCode: dealerData.code,
      dealerName: dealerData.name,
      dealerVatNumber: dealerData.vatNumber,
      dealerInternalBillingId: dealerData.internalBillingId,
      dealerInternalId: dealerData.dealerInternalId,
      dealerSdiCode: dealerData.sdiCode,
      dealerIndustry: dealerData.industry,
      dealerStatus: dealerData.status,
      dealerClientCode: dealerData.dealerClientCode,
      dealerBrandCode: dealerData.dealerBrandCode,
      dealerLegalAddressStreet: dealerData.legalAddress?.street || '',
      dealerLegalAddressNumber: dealerData.legalAddress?.number || '',
      dealerLegalAddressCity: dealerData.legalAddress?.city || '',
      dealerLegalAddressZip: dealerData.legalAddress?.zip || '',
      dealerLegalAddressProvince: dealerData.legalAddress?.province || '',
      dealerLegalAddressCountry: dealerData.legalAddress?.country || '',
      dealerOperatingAddressStreet: dealerData.operatingAddress?.street || '',
      dealerOperatingAddressNumber: dealerData.operatingAddress?.number || '',
      dealerOperatingAddressCity: dealerData.operatingAddress?.city || '',
      dealerOperatingAddressZip: dealerData.operatingAddress?.zip || '',
      dealerOperatingAddressProvince: dealerData.operatingAddress?.province || '',
      dealerOperatingAddressCountry: dealerData.operatingAddress?.country || ''
    };

    const request$ = this.isEditingDealer
      ? this.shipmentRequestService.updateDealer(dealer)
      : this.shipmentRequestService.createDealer(dealer);

    request$.subscribe({
      next: (savedDealer) => {
        this.shipmentForm.patchValue({
          destination: {
            dealerCode: savedDealer.dealerCode,
            dealerName: savedDealer.dealerName || '',
            dealerVatNumber: savedDealer.dealerVatNumber || '',
            dealerInternalBillingId: savedDealer.dealerInternalBillingId || '',
            dealerInternalId: savedDealer.dealerInternalId || '',
            dealerIndustry: savedDealer.dealerIndustry || '',
            dealerStatus: savedDealer.dealerStatus || '',
            dealerOperatingPhone: '',
            dealerOperatingEmail: '',
            dealerLegalAddressStreet: savedDealer.dealerLegalAddressStreet || '',
            dealerLegalAddressNumber: savedDealer.dealerLegalAddressNumber || '',
            dealerLegalAddressCity: savedDealer.dealerLegalAddressCity || '',
            dealerLegalAddressZip: savedDealer.dealerLegalAddressZip || '',
            dealerLegalAddressProvince: savedDealer.dealerLegalAddressProvince || '',
            dealerLegalAddressCountry: savedDealer.dealerLegalAddressCountry || '',
            dealerOperatingAddressStreet: savedDealer.dealerOperatingAddressStreet || '',
            dealerOperatingAddressNumber: savedDealer.dealerOperatingAddressNumber || '',
            dealerOperatingAddressCity: savedDealer.dealerOperatingAddressCity || '',
            dealerOperatingAddressZip: savedDealer.dealerOperatingAddressZip || '',
            dealerOperatingAddressProvince: savedDealer.dealerOperatingAddressProvince || '',
            dealerOperatingAddressCountry: savedDealer.dealerOperatingAddressCountry || ''
          }
        });

        this.closeDealerInsertPopup();
        this.enableAllDealerFields();
        this.showSuccessMessage(this.isEditingDealer ? 'Dealer aggiornato con successo.' : 'Dealer inserito con successo.');
      },
      error: (err) => {
        this.showErrorMessage(err?.message || 'Errore durante il salvataggio del dealer.');
      }
    });
  }  /**
   * Copy legal address to operating address in dealer form
   * Only copies fields that have actual data (not empty or null)
   */
  copyLegalToOperating(): void {
    if (!this.dealerForm) return;
    
    const legalAddress = this.dealerForm.get('legalAddress')?.value;
    if (!legalAddress) return;
    
    // Create an object with only non-empty fields
    const fieldsWithData: any = {};
    let hasDataToCopy = false;
    
    Object.keys(legalAddress).forEach(key => {
      const value = legalAddress[key];
      if (value && value.toString().trim() !== '') {
        fieldsWithData[key] = value;
        hasDataToCopy = true;
      }
    });
    
    // Only patch if there's actual data to copy
    if (hasDataToCopy) {
      this.dealerForm.get('operatingAddress')?.patchValue(fieldsWithData);
      this.showSuccessMessage('Indirizzo legale copiato in indirizzo operativo');
    } else {
      this.showErrorMessage('Nessun dato da copiare dall\'indirizzo legale');
    }
  }

  /**
   * Copy operating address to legal address in dealer form
   * Only copies fields that have actual data (not empty or null)
   */
  copyOperatingToLegal(): void {
    if (!this.dealerForm) return;
    
    const operatingAddress = this.dealerForm.get('operatingAddress')?.value;
    if (!operatingAddress) return;
    
    // Create an object with only non-empty fields
    const fieldsWithData: any = {};
    let hasDataToCopy = false;
    
    Object.keys(operatingAddress).forEach(key => {
      const value = operatingAddress[key];
      if (value && value.toString().trim() !== '') {
        fieldsWithData[key] = value;
        hasDataToCopy = true;
      }
    });
    
    // Only patch if there's actual data to copy
    if (hasDataToCopy) {
      this.dealerForm.get('legalAddress')?.patchValue(fieldsWithData);
      this.showSuccessMessage('Indirizzo operativo copiato in indirizzo legale');
    } else {
      this.showErrorMessage('Nessun dato da copiare dall\'indirizzo operativo');
    }
  }

  /**
   * Get dealer popup title based on editing mode
   */
  getDealerPopupTitle(): string {
    return this.isEditingDealer ? 'Modifica Dealer' : 'Inserisci Nuovo Dealer';
  }
  /**
   * Gestisce il cambio di client e filtra i brand associati
   */
  onClientChange(event: any): void {
    const clientCode = event?.target?.value || '';
    this.onClientCodeChange(clientCode);
  }

  /**
   * Gestisce la selezione cliente (da UI o programmatica) e aggiorna:
   * - settaggio campi
   * - brand disponibili
   * - magazzini disponibili
   */
  private onClientCodeChange(clientCode: string): void {
    const savedDate = this.shipmentForm.get('waybill.waybillDate')?.value;

    // Reset dealer fields when client changes
    this.resetDealerFields();
    this.isDealerSelected = false;

    // Carica il settaggio dei campi (clientFieldSettings) dal client selezionato.
    // Se il client non ne ha, tutti i campi rimangono abilitati di default.
    this.loadFieldSettingsFromClient(clientCode);
    
    if (clientCode) {
      // Trova i brand associati al client selezionato
      const selectedClientBrands = this.clientsBrands.find(cb => cb.clientCode === clientCode);
      
      if (selectedClientBrands) {
        this.filteredBrands = selectedClientBrands.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }));
        
        // Imposta automaticamente il primo brand correlato
        if (this.filteredBrands.length == 1) {
          this.shipmentForm.get('waybill.waybillBrandCode')?.setValue(this.filteredBrands[0].brandCode);
        } else {
          this.shipmentForm.get('waybill.waybillBrandCode')?.setValue('');
        }
      } else {
        this.filteredBrands = [];
        this.shipmentForm.get('waybill.waybillBrandCode')?.setValue('');
      }

      // Filtra i magazzini in base al client selezionato
      this.filteredWarehouses = this.filterWarehousesByUserConfig(
        this.getWarehousesForClient(clientCode)
      );

      // Abilita il campo warehouse
      const warehouseControl = this.shipmentForm.get('waybill.waybillOriginLocation');
      warehouseControl?.enable();

      // Preselezione magazzino:
      // 1. Se c'è un solo magazzino disponibile per il cliente, lo seleziono.
      // 2. Altrimenti, se l'utente ha dei magazzini configurati nel profilo,
      //    cerco il primo che corrisponde a uno di quelli filtrati.
      // 3. Altrimenti lascio vuoto per forzare la selezione manuale.
      const preselected = this.pickDefaultWarehouseCode(this.filteredWarehouses);
      warehouseControl?.setValue(preselected);
    } else {
      // Se nessun client è selezionato, mostra tutti i brand
      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      );
      
      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );
      
      // Reset del brand selezionato quando nessun client è selezionato
      this.shipmentForm.get('waybill.waybillBrandCode')?.setValue('');

      // Disabilita il campo warehouse e resetta i magazzini filtrati
      this.filteredWarehouses = [];
      const warehouseControl = this.shipmentForm.get('waybill.waybillOriginLocation');
      warehouseControl?.setValue('');
      warehouseControl?.disable();
    }
    
    // Update client and brand selection state
    this.updateClientBrandSelectionState();

    // Ripristina la data selezionata dall'utente: le operazioni sopra
    // (applyFieldSettings, checkTimeRestrictions) possono resettarla.
    if (savedDate) {
      this.shipmentForm.get('waybill.waybillDate')?.setValue(savedDate, { emitEvent: false });
      if (this.flatpickrInstance) {
        this.flatpickrInstance.setDate(savedDate, false);
        if (this.requestedDateInput?.nativeElement) {
          this.requestedDateInput.nativeElement.value = savedDate;
        }
      }
    }
  }

  /**
   * Restituisce i magazzini associati al client indicato.
   * `warehouseAffiliateCode` può contenere più codici cliente separati da
   * `;` (es. "CLT00002;CLT00003;CLT00007"): il magazzino è compatibile se
   * uno qualsiasi dei codici coincide con il client selezionato.
   */
  private getWarehousesForClient(clientCode: string | null | undefined): Warehouse[] {
    const code = (clientCode || '').trim();
    if (!code) return [];
    return this.warehouses.filter(w => this.warehouseBelongsToClient(w, code));
  }

  /** True se `warehouseAffiliateCode` (singolo o multi-codice `;`) contiene il client. */
  private warehouseBelongsToClient(warehouse: Warehouse, clientCode: string): boolean {
    const affiliateCodes = (warehouse.warehouseAffiliateCode || '')
      .split(';')
      .map(c => c.trim())
      .filter(c => c.length > 0);
    return affiliateCodes.includes(clientCode);
  }

  private filterWarehousesByUserConfig(warehouses: Warehouse[]): Warehouse[] {
    if (this.userWarehouseCodes.length === 0) return warehouses;
    return warehouses.filter(w => this.userWarehouseCodes.includes(w.warehouseCode));
  }

  /**
   * Sceglie il codice magazzino da preselezionare:
   * 1. Se c'è un solo magazzino disponibile, restituisce quello.
   * 2. Altrimenti, se l'utente ha magazzini configurati nel profilo
   *    (`userFunctions.warehouses`) e ne combacia ESATTAMENTE UNO con
   *    quelli disponibili per il client, restituisce quello.
   * 3. In tutti gli altri casi (più match o nessun match) restituisce
   *    stringa vuota per forzare la selezione manuale.
   */
  private pickDefaultWarehouseCode(available: Warehouse[]): string {
    // console.log('[ShipmentRequest] pickDefaultWarehouseCode', {
    //   availableCount: available?.length || 0,
    //   availableCodes: (available || []).map(w => w.warehouseCode),
    //   userWarehouseCodes: this.userWarehouseCodes
    // });
    if (!available || available.length === 0) return '';
    if (available.length === 1) return available[0].warehouseCode;
    if (this.userWarehouseCodes.length > 0) {
      const matches = available.filter(w => this.userWarehouseCodes.includes(w.warehouseCode));
      if (matches.length === 1) return matches[0].warehouseCode;
    }
    return '';
  }

  /**
   * Gestisce il cambio di brand e aggiorna il client correlato se necessario
   */
  onBrandChange(event: any): void {
    const brandCode = event.target.value;
    
    if (brandCode) {
      // Trova il client che contiene questo brand
      const clientWithBrand = this.clientsBrands.find(cb => 
        cb.brands.some(b => b.brandCode === brandCode)
      );
      
      if (clientWithBrand) {
        const currentClientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
        
        // Se il client corrente non contiene questo brand, cambia client
        if (currentClientCode !== clientWithBrand.clientCode) {
          this.shipmentForm.get('waybill.waybillClientCode')?.setValue(clientWithBrand.clientCode);
          
          // Aggiorna i brand filtrati per il nuovo client
          this.filteredBrands = clientWithBrand.brands.map(b => ({
            brandCode: b.brandCode,
            brandName: b.brandName,
          }));

          // Filtra i magazzini in base al nuovo client selezionato
          this.filteredWarehouses = this.filterWarehousesByUserConfig(
            this.getWarehousesForClient(clientWithBrand.clientCode)
          );

          // Abilita il campo warehouse
          const warehouseControl = this.shipmentForm.get('waybill.waybillOriginLocation');
          warehouseControl?.enable();

          // Se c'è un solo magazzino, selezionalo automaticamente
          if (this.filteredWarehouses.length === 1) {
            warehouseControl?.setValue(this.filteredWarehouses[0].warehouseCode);
          } else {
            // Se ci sono più magazzini o nessuno, resetta la selezione
            warehouseControl?.setValue('');
          }
        }
      }
    }
    
    // Update client and brand selection state
    this.updateClientBrandSelectionState();
  }

  /**
   * Updates the client and brand selection state
   */
  private updateClientBrandSelectionState(): void {
    const clientCode = this.shipmentForm.get('waybill.waybillClientCode')?.value;
    const brandCode = this.shipmentForm.get('waybill.waybillBrandCode')?.value;
    
    this.isClientBrandSelected = !!(clientCode && brandCode);
    
    // Calculate available dealers count if both client and brand are selected
    if (this.isClientBrandSelected) {
      const filteredDealers = this.getFilteredDealersByClientBrand(clientCode, brandCode);
      this.availableDealersCount = filteredDealers.length;
    } else {
      this.availableDealersCount = 0;
    }
  }

  /**
   * Reset all dealer fields in the destination section
   */
  private resetDealerFields(): void {
    const destinationGroup = this.shipmentForm.get('destination') as FormGroup;
    if (destinationGroup) {
      destinationGroup.patchValue({
        dealerCode: '',
        dealerName: '',
        dealerVatNumber: '',
        dealerOperatingPhone: '',
        dealerOperatingEmail: '',
        dealerOperatingAddressStreet: '',
        dealerOperatingAddressNumber: '',
        dealerOperatingAddressZip: '',
        dealerOperatingAddressCity: '',
        dealerOperatingAddressProvince: '',
        dealerOperatingAddressCountry: 'IT'
      });
      
      // Mark fields as untouched to remove validation errors
      Object.keys(destinationGroup.controls).forEach(key => {
        destinationGroup.get(key)?.markAsUntouched();
        destinationGroup.get(key)?.markAsPristine();
      });
    }
  }

  /**
   * Generate daily summary PDF - Borderò giornaliero
   */
  generateDailySummaryPDF(): void {
    // Create PDF in landscape mode
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const today = new Date();
    const todayString = today.toLocaleDateString('it-IT');
    const summary = this.getSummary();
    
    // Get today's waybills (filter by today's date)
    const todayWaybills = this.waybills.filter(w => {
      if (!w.waybillDate) return false;
      const waybillDate = new Date(w.waybillDate);
      return waybillDate.toDateString() === today.toDateString();
    });

    // Portal colors (matching the application theme)
    const primaryRed = [237, 29, 38]; // #ed1d26 - Main portal color
    const darkGray = [52, 58, 64]; // #343a40 - Bootstrap dark
    const lightGray = [248, 249, 250]; // #f8f9fa - Bootstrap light
    const mediumGray = [108, 117, 125]; // #6c757d - Bootstrap secondary
    const white = [255, 255, 255];
    const black = [0, 0, 0];

    // Page margins
    const margin = 15;
    const pageWidth = 297;
    const contentWidth = pageWidth - (margin * 2);

    // Header section
    pdf.setFillColor(primaryRed[0], primaryRed[1], primaryRed[2]);
    pdf.rect(0, 0, pageWidth, 25, 'F');
    
    // Company info
    pdf.setTextColor(white[0], white[1], white[2]);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BERTANI LOGISTICS', margin, 12);
    
    // Document title
    pdf.setFontSize(16);
    pdf.text('BORDERÒ GIORNALIERO', pageWidth - margin, 12, { align: 'right' });
    
    // Date and info bar
    pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
    pdf.rect(0, 25, pageWidth, 15, 'F');
    
    pdf.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Data: ${todayString}`, margin, 35);
    pdf.text(`Generato: ${new Date().toLocaleString('it-IT')}`, pageWidth - margin, 35, { align: 'right' });

    // Summary section
    let yPos = 55;
    pdf.setTextColor(black[0], black[1], black[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RIEPILOGO SPEDIZIONI', margin, yPos);
    
    yPos += 15;
    
    // Summary grid (2x3 layout)
    const boxWidth = (contentWidth - 20) / 3;
    const boxHeight = 25;
    
    const summaryData = [
      { label: 'Totale Bolle', value: summary.totalShipments.toString() },
      { label: 'Totale Colli', value: summary.totalParcels.toString() },
      { label: 'Peso Totale', value: `${summary.totalWeight.toFixed(1)} kg` },
      { label: 'Volume Totale', value: `${summary.totalVolume.toFixed(2)} m³` },
      { label: 'Valore Assicurato', value: `€ ${summary.totalInsuredValue.toFixed(0)}` },
      { label: 'Status Report', value: 'COMPLETO' }
    ];
    
    summaryData.forEach((item, index) => {
      const row = Math.floor(index / 3);
      const col = index % 3;
      const x = margin + (col * (boxWidth + 10));
      const y = yPos + (row * (boxHeight + 5));
      
      // Box border
      pdf.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      pdf.setLineWidth(0.5);
      pdf.rect(x, y, boxWidth, boxHeight);
      
      // Value (large)
      pdf.setTextColor(primaryRed[0], primaryRed[1], primaryRed[2]);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(item.value, x + boxWidth/2, y + 12, { align: 'center' });
      
      // Label (small)
      pdf.setTextColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(item.label, x + boxWidth/2, y + 20, { align: 'center' });
    });
    
    yPos += 65;
    
    // Detailed table section
    pdf.setTextColor(black[0], black[1], black[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DETTAGLIO BOLLE INSERITE', margin, yPos);
    
    yPos += 10;
    
    // Table header
    pdf.setFillColor(darkGray[0], darkGray[1], darkGray[2]);
    pdf.rect(margin, yPos, contentWidth, 10, 'F');
    
    pdf.setTextColor(white[0], white[1], white[2]);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    
    // Column positions (optimized for landscape)
    const cols = [
      { label: 'Bolla', x: margin + 5, width: 35 },
      { label: 'Data', x: margin + 45, width: 25 },
      { label: 'Cliente', x: margin + 75, width: 50 },
      { label: 'Indirizzo', x: margin + 130, width: 45 },
      { label: 'Città', x: margin + 180, width: 30 },
      { label: 'Prov.', x: margin + 215, width: 15 },
      { label: 'Client', x: margin + 235, width: 20 },
      { label: 'Brand', x: margin + 260, width: 20 }
    ];
    
    cols.forEach(col => {
      pdf.text(col.label, col.x, yPos + 7);
    });
    
    yPos += 12;
    pdf.setTextColor(black[0], black[1], black[2]);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    
    // Table rows
    if (todayWaybills.length === 0) {
      pdf.setTextColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Nessuna bolla inserita nella giornata corrente', margin, yPos + 10);
    } else {
      todayWaybills.forEach((waybill, index) => {
        // Check for new page
        if (yPos > 180) {
          pdf.addPage('landscape');
          yPos = 30;
          
          // Repeat table header
          pdf.setFillColor(darkGray[0], darkGray[1], darkGray[2]);
          pdf.rect(margin, yPos, contentWidth, 10, 'F');
          pdf.setTextColor(white[0], white[1], white[2]);
          pdf.setFont('helvetica', 'bold');
          cols.forEach(col => {
            pdf.text(col.label, col.x, yPos + 7);
          });
          yPos += 12;
          pdf.setTextColor(black[0], black[1], black[2]);
          pdf.setFont('helvetica', 'normal');
        }
        
        // Alternate row background
        if (index % 2 === 0) {
          pdf.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
          pdf.rect(margin, yPos - 2, contentWidth, 8, 'F');
        }
        
        const dealer = this.getDealerByCode(waybill.waybillDealerCode);
        const waybillDateStr = waybill.waybillDate ? 
          new Date(waybill.waybillDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : '-';
        
        // Truncate long text to fit columns
        const truncate = (text: string, maxLength: number) => 
          text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
        
        const rowData = [
          { text: waybill.waybillCode || '-', x: cols[0].x },
          { text: waybillDateStr, x: cols[1].x },
          { text: truncate(dealer?.dealerName || '-', 20), x: cols[2].x },
          { text: truncate(`${dealer?.dealerOperatingAddressStreet || ''} ${dealer?.dealerOperatingAddressNumber || ''}`.trim() || '-', 18), x: cols[3].x },
          { text: truncate(dealer?.dealerOperatingAddressCity || '-', 12), x: cols[4].x },
          { text: dealer?.dealerOperatingAddressProvince || '-', x: cols[5].x },
          { text: waybill.waybillClientCode || '-', x: cols[6].x },
          { text: waybill.waybillBrandCode || '-', x: cols[7].x }
        ];
        
        rowData.forEach(data => {
          pdf.text(data.text, data.x, yPos + 3);
        });
        
        yPos += 8;
      });
    }
    
    // Footer
    const pageCount = (pdf as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      
      // Footer line
      pdf.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 195, pageWidth - margin, 195);
      
      // Footer text
      pdf.setTextColor(mediumGray[0], mediumGray[1], mediumGray[2]);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Pagina ${i} di ${pageCount}`, pageWidth - margin, 202, { align: 'right' });
      pdf.text('Sistema Gestione Spedizioni - Bertani Logistics', margin, 202);
    }
    
    // Save PDF
    const filename = `bordero-${todayString.replace(/\//g, '-')}.pdf`;
    pdf.save(filename);
  }
}
